import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { getTrackingData, OnTripAmbulance, OnDutyStaff, getBookings, getBooking, Booking, Ambulance, getAmbulanceLiveLocation, getAmbulanceLiveLocationByDeviceId, getTraccarLiveLocation, getTraccarDayRoute, getTenant, getAmbulancesWithBookingStatus, getAmbulanceTypes, AmbulanceType, getAreas, Area, getTrackingSummary, updateAmbulance, downloadBookingDetailsPDF, downloadBookingDetailsExcel, revertTripToPending } from '../../../../services/api';
import ConfirmModal from '../../../../components/ConfirmModal';
import { formatDateTimeDDMMYYYY, formatDateDDMMYYYY } from '../../../../utils/dateUtils';
import { cumulativeDistancesKm } from '../../../../utils/distance';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  BOOKING_STATUS,
  BOOKING_STATUS_LABELS,
  BOOKING_STATUS_COLORS,
  BOOKING_STATUS_DISPLAY,
  ACTIVE_BOOKING_STATUSES,
  BOOKING_TYPE,
  type BookingType,
  ROUTE_VIEW_MODE,
  COLORS,
  type BookingStatus,
  type RouteViewMode,
} from '../../../../constants/booking';
import { RouteViewTabs } from '../RouteViewTabs';
import { getBookingId, queryActiveBookingFromAPI } from '../../utils/bookingHelpers';
import VehicleSummaryCards from './VehicleSummaryCards';
import BookingSummaryCards from './BookingSummaryCards';
import { RouteStop, formatAmbulanceStatus, formatAmbulanceType } from './liveTrackingUtils';
import { useDayRoutePlayback } from './useDayRoutePlayback';
import { formatSpeedDisplay } from '../../../../utils/speedUtils';

// Distance helpers imported from ../../../../utils/distance

// Custom overlay for day-route playback info window (positioned above marker, with speech-bubble tail)
// Uses google.maps.OverlayView at runtime; typed as any to avoid missing @types
type PlaybackInfoOverlayInstance = {
  setPosition(latlng: google.maps.LatLng): void;
  setContent(html: string): void;
  setMap(map: google.maps.Map | null): void;
};

function createDayRoutePlaybackInfoOverlay(position: google.maps.LatLng, content: string): PlaybackInfoOverlayInstance {
  const OverlayView = (window as any).google?.maps?.OverlayView;
  if (!OverlayView) return { setPosition: () => { }, setContent: () => { }, setMap: () => { } };

  function Overlay(this: any, pos: google.maps.LatLng, c: string) {
    OverlayView.call(this);
    this.position = pos;
    this.content = c;
    this.div = null;
  }

  Overlay.prototype = Object.create(OverlayView.prototype);
  Overlay.prototype.onAdd = function (this: any) {
    this.div = document.createElement('div');
    this.div.style.cssText = 'position:absolute;pointer-events:auto;z-index:100;display:flex;flex-direction:column;align-items:center;';
    this.div.innerHTML = this.content;
    const panes = this.getPanes();
    if (panes?.floatPane) panes.floatPane.appendChild(this.div);
  };
  Overlay.prototype.draw = function (this: any) {
    if (!this.div || !this.position) return;
    const projection = this.getProjection();
    if (!projection) return;
    const point = projection.fromLatLngToDivPixel(this.position);
    if (point) {
      const divW = this.div.offsetWidth || 0;
      this.div.style.left = `${point.x - divW / 2}px`;
      this.div.style.top = `${point.y - 130}px`;
    }
  };
  Overlay.prototype.onRemove = function (this: any) {
    if (this.div?.parentNode) this.div.parentNode.removeChild(this.div);
    this.div = null;
  };
  Overlay.prototype.setPosition = function (this: any, latlng: google.maps.LatLng) {
    this.position = latlng;
    this.draw();
  };
  Overlay.prototype.setContent = function (this: any, html: string) {
    this.content = html;
    if (this.div) this.div.innerHTML = html;
  };
  return new (Overlay as any)(position, content);
}

/** Resolve icon URL to absolute so relative paths work when deployed (e.g. under base path or different origin). */
function resolveIconUrl(url: string): string {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base =
    typeof window !== 'undefined' ? window.location.origin + (process.env.PUBLIC_URL || '') : '';
  return base + (url.startsWith('/') ? url : '/' + url);
}

function formatLocalDateYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatOverlayDateTime(d: Date): string {
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

/**
 * Timestamp for live-location bubble: prefer last fix `time` from lastLocationsObject (matches device point),
 * then envelope lastUpdate. (lastUpdate can lag behind lastLocationsObject.time on the same API payload.)
 */
function preferredLiveFixTimestampMs(lastLoc: { time?: unknown } | null | undefined, ambulance: { lastUpdate?: unknown } | null | undefined): number | null {
  const tFix = lastLoc?.time != null && String(lastLoc.time).trim() !== '' ? new Date(lastLoc.time as string | number).getTime() : NaN;
  if (!isNaN(tFix)) return tFix;
  const tEnv =
    ambulance?.lastUpdate != null && String(ambulance.lastUpdate).trim() !== ''
      ? new Date(ambulance.lastUpdate as string | number).getTime()
      : NaN;
  if (!isNaN(tEnv)) return tEnv;
  return null;
}

function pickLiveSpeedForDisplay(lastLoc: any, ambulance: any, summary: any): number | string | null {
  const raw = lastLoc?.speed ?? ambulance?.speed ?? summary?.speed ?? summary?.averageSpeed;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (t === '' || t === '-' || t === '—') return null;
  }
  return raw;
}

/** When day-route playback hides the selected vehicle from the map list, tooltips still need the live API row. */
function mergeAmbulanceWithLatestLiveApiRow(sel: Ambulance, allAmbulances: Ambulance[], liveLocations: any[]): any {
  const fullAmb = allAmbulances.find((a) => a._id === sel._id || a.metadata?.name === sel.metadata?.name);
  const gpsDeviceId = fullAmb?.spec?.gpsDevice?.deviceId ?? fullAmb?.spec?.gpsDevice?.gpsDeviceId;
  const base = {
    id: fullAmb?.metadata?.name ?? sel.metadata?.name,
    ambulanceId: sel._id,
    type: fullAmb?.spec?.type ?? 'Ambulance',
    status: fullAmb?.spec?.status ?? 'available',
  };
  if (!gpsDeviceId) {
    return { ...sel, ...base };
  }
  const raw = liveLocations.find(
    (loc: any) => loc.deviceId === gpsDeviceId || loc.deviceId === fullAmb?.spec?.gpsDevice?.gpsDeviceId
  );
  if (!raw) {
    return { ...sel, ...base };
  }
  const liveLoc = raw.liveLocation;
  const longitude = Array.isArray(liveLoc) && liveLoc.length === 2 ? liveLoc[0] : null;
  const latitude = Array.isArray(liveLoc) && liveLoc.length === 2 ? liveLoc[1] : null;
  if (latitude == null || longitude == null || isNaN(latitude) || isNaN(longitude)) {
    return {
      ...base,
      ...sel,
      lastUpdate: raw.lastUpdate,
      lastLocationsObject: raw.lastLocationsObject,
      summary: raw.summary,
      speed: raw.lastLocationsObject?.speed ?? raw.speed ?? null,
    };
  }
  return {
    ...base,
    currentLocation: { latitude, longitude, address: undefined },
    liveLocation: true,
    isOffline: !!(raw.isOffline && fullAmb?.spec?.status !== 'on_duty'),
    lastUpdate: raw.lastUpdate,
    lastLocationsObject: raw.lastLocationsObject,
    summary: raw.summary,
    speed: raw.lastLocationsObject?.speed ?? raw.speed ?? null,
  };
}

/** Merge on-map snapshot with raw live-location row so lastLocationsObject / speed stay in sync when the list row omits fields. */
function pickAmbulanceForLiveInfoOverlay(
  selected: Ambulance | null | undefined,
  fromMap: any,
  allAmbulances: Ambulance[],
  liveLocations: any[]
): any {
  if (!selected) return fromMap ?? {};
  const merged = mergeAmbulanceWithLatestLiveApiRow(selected, allAmbulances, liveLocations);
  if (!fromMap) return merged;
  return {
    ...merged,
    ...fromMap,
    lastLocationsObject: fromMap.lastLocationsObject ?? merged.lastLocationsObject,
    lastUpdate: fromMap.lastUpdate ?? merged.lastUpdate,
    summary: fromMap.summary ?? merged.summary,
    speed: fromMap.speed ?? merged.speed,
  };
}

/** Current Route in-trip: Traccar live position poll interval */
const CURRENT_ROUTE_TRACCAR_POLL_MS = 30_000;

const STARTED_TRIP_STATUSES: BookingStatus[] = [
  BOOKING_STATUS.IN_TRANSIT,
  BOOKING_STATUS.PATIENT_PICKED,
  BOOKING_STATUS.PATIENT_DROPPED,
];

function isInTripOnCurrentRoute(
  ambulanceId: string | undefined,
  viewMode: RouteViewMode,
  bookings: Booking[]
): boolean {
  if (!ambulanceId || viewMode !== ROUTE_VIEW_MODE.CURRENT) return false;
  return bookings.some(
    (b) => b.spec.ambulanceId === ambulanceId && STARTED_TRIP_STATUSES.includes(b.spec.status as BookingStatus)
  );
}

function resolveDashboardRouteDataRefreshMs(rawValue: unknown): number {
  const DEFAULT_MS = 15_000;
  const MIN_MS = 10_000;
  if (rawValue == null) return DEFAULT_MS;
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_MS;

  // If config value looks like seconds, convert to ms.
  const normalizedMs = parsed >= 1000 ? parsed : parsed * 1000;
  return Math.max(MIN_MS, Math.round(normalizedMs));
}

interface LiveTrackingTabProps {
  refreshTrigger?: number;
  onTrackingDataRefreshed?: () => void;
  onBookingFilterChange?: (statusFilter: string | undefined, cardId: string | null) => void;
  selectedBookingCard?: string | null;
}

const LiveTrackingTab: React.FC<LiveTrackingTabProps> = ({ refreshTrigger, onTrackingDataRefreshed, onBookingFilterChange, selectedBookingCard }) => {
  const { user, isTenantAdmin, isSystemAdmin } = useAuth();
  const [onTripAmbulances, setOnTripAmbulances] = useState<OnTripAmbulance[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [onDutyStaff, setOnDutyStaff] = useState<OnDutyStaff[]>([]);
  const [allAmbulances, setAllAmbulances] = useState<Ambulance[]>([]);
  const [liveLocations, setLiveLocations] = useState<any[]>([]);
  const [initialLoading, setInitialLoading] = useState(true); // Only true on first load
  const [loading, setLoading] = useState(false); // For background updates, doesn't hide UI
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState('');
  const [groupId, setGroupId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedAmbulance, setSelectedAmbulance] = useState<Ambulance | null>(null);
  const [routeBookings, setRouteBookings] = useState<Booking[]>([]);
  const [locationHistoryRoute, setLocationHistoryRoute] = useState<any[]>([]);
  const [routeLoading, setRouteLoading] = useState(false);
  const [ambulanceTypes, setAmbulanceTypes] = useState<AmbulanceType[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [hospitalLocation, setHospitalLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [hospitalDefaultLocation, setHospitalDefaultLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [convertKnotsToKmph, setConvertKnotsToKmph] = useState(false);
  const [dashboardRouteDataRefreshMs, setDashboardRouteDataRefreshMs] = useState(15_000);
  const [liveLocationsApiFinished, setLiveLocationsApiFinished] = useState(false);
  const [routeViewMode, setRouteViewMode] = useState<RouteViewMode>(ROUTE_VIEW_MODE.CURRENT);
  /** Calendar date for Day Route location history API (`date=YYYY-MM-DD`); defaults to today */
  const [dayRouteHistoryDate, setDayRouteHistoryDate] = useState(() => formatLocalDateYMD(new Date()));
  const dayRouteHistoryDateRef = React.useRef(dayRouteHistoryDate);
  dayRouteHistoryDateRef.current = dayRouteHistoryDate;
  const [currentRoute, setCurrentRoute] = useState<{
    origin: { lat: number; lng: number };
    destination: { lat: number; lng: number };
    destinationType: 'pickup' | 'dropoff';
    stops?: RouteStop[];
    bookingType?: BookingType;
    bookingStatus?: string;
  } | null>(null);
  const [routeEta, setRouteEta] = useState<{ pickupEtaMinutes?: number; dropoffEtaMinutes?: number } | null>(null);
  const [showEtaTooltip, setShowEtaTooltip] = useState(false);
  const {
    index: dayRoutePlaybackIndex,
    progress: dayRoutePlaybackProgress,
    isPlaying: dayRouteIsPlaying,
    speed: dayRoutePlaybackSpeed,
    setSpeed: setDayRoutePlaybackSpeed,
    indexRef: dayRoutePlaybackIndexRef,
    progressRef: dayRoutePlaybackProgressRef,
    isPlayingRef: dayRouteIsPlayingRef,
    play: dayRoutePlaybackPlay,
    pause: dayRoutePlaybackPause,
    stepBack: dayRoutePlaybackStepBack,
    stepForward: dayRoutePlaybackStepForward,
    reset: resetDayRoutePlayback,
  } = useDayRoutePlayback({
    routeViewMode,
    locationHistoryLength: locationHistoryRoute.length,
  });
  const [isSpeedCompact, setIsSpeedCompact] = useState(false);
  const [isSpeedMenuOpen, setIsSpeedMenuOpen] = useState(false);
  const speedControlsRef = React.useRef<HTMLDivElement | null>(null);
  const mapSectionRef = React.useRef<HTMLDivElement | null>(null);
  // Filter categories: total, standby, on_trip, inactive
  // Total is mutually exclusive with others. Default to 'total' on load.
  const [statusFilters, setStatusFilters] = useState<string[]>(['total']); // Array of selected filter categories

  // Compact speed controls based on map card width (tracking-map-section)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ResizeObserverImpl: typeof ResizeObserver | undefined = (window as any).ResizeObserver;
    const el = mapSectionRef.current;
    if (!ResizeObserverImpl || !el) return;

    const observer = new ResizeObserverImpl((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const width = entry.contentRect.width;
      // If map card is narrow, use burger; if wide, show all speed buttons inline
      setIsSpeedCompact(width < 400);
    });

    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);
  const shouldFitBoundsRef = React.useRef<boolean>(true);
  const prevSelectedAmbulanceIdRef = React.useRef<string | null>(null);
  const prevRouteViewModeRef = React.useRef<RouteViewMode>(routeViewMode);
  const currentRouteTabHasFittedRef = React.useRef<boolean>(false);
  // Once the user zooms or pans on Current Route, never programmatically fit again until they leave the tab.
  const userHasPannedOrZoomedRef = React.useRef<boolean>(false);

  // Helper function to map filter categories to actual status values
  const getStatusesFromFilter = (filterCategory: string): string[] => {
    switch (filterCategory) {
      case 'total':
        return ['available', 'on_duty', 'off_duty', 'maintenance']; // All statuses
      case 'standby':
        return ['available'];
      case 'on_trip':
        return ['on_duty'];
      case 'inactive':
        return ['off_duty', 'maintenance'];
      default:
        return [];
    }
  };

  // Helper function to check if an ambulance status matches any of the selected filter categories
  const matchesStatusFilter = (ambulanceStatus: string): boolean => {
    // At least one filter should always be selected, but as a safety check:
    if (statusFilters.length === 0) {
      return false;
    }

    // Check if the ambulance status matches any of the selected filter categories
    return statusFilters.some((filterCategory) => {
      const statuses = getStatusesFromFilter(filterCategory);
      return statuses.includes(ambulanceStatus);
    });
  };
  const isInfoWindowOpenRef = React.useRef<boolean>(false); // Track if an info window is currently open
  const traccarFetchInFlightRef = React.useRef<Map<string, Promise<any[] | undefined>>>(new Map());
  const [runningStatusFilters, setRunningStatusFilters] = useState<string[]>([
    BOOKING_STATUS.ASSIGNED,
    BOOKING_STATUS.IN_TRANSIT,
    BOOKING_STATUS.PATIENT_PICKED,
    BOOKING_STATUS.PATIENT_DROPPED,
    BOOKING_STATUS.PENDING,
  ]); // Array of selected running statuses
  const [showFilters, setShowFilters] = useState<boolean>(false); // Toggle to show/hide filters
  const [showSearch, setShowSearch] = useState<boolean>(false); // Toggle to show/hide search
  const [activeBookings, setActiveBookings] = useState<Booking[]>([]); // Active bookings for status display
  const [allBookings, setAllBookings] = useState<Booking[]>([]); // All bookings for summary
  const fetchingBookingsRef = React.useRef<Set<string>>(new Set()); // Track bookings currently being fetched to prevent duplicate calls
  const [ambulanceFuelData, setAmbulanceFuelData] = useState<any>(null);
  const [ambulanceFuelLoading, setAmbulanceFuelLoading] = useState(false);
  const [fuelGraphHoveredPoint, setFuelGraphHoveredPoint] = useState<{ reading: any; x: number; y: number } | null>(null);
  const [fuelGraphHoveredDistancePoint, setFuelGraphHoveredDistancePoint] = useState<{ x: number; y: number; distanceKm: number; date: string } | null>(null);
  const [fuelGraphTooltipPosition, setFuelGraphTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [isFuelGraphFullscreen, setIsFuelGraphFullscreen] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Ensure keyframes for the circular fuel loader exist
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const id = 'fuel-spinner-keyframes';
    if (document.getElementById(id)) return;

    const styleEl = document.createElement('style');
    styleEl.id = id;
    styleEl.innerHTML = `
      @keyframes fuel-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleEl);
  }, []);

  // Normalise location history into the same structure used by the distance chart
  // and compute cumulative GPS-based distance (Haversine) from it.
  // Sort by date so label and chart always use the same order (cumulative distance depends on order).
  const getDistanceFromLocationHistory = React.useCallback((rawLocationHistory: any[] | undefined | null) => {
    const locationHistory = (rawLocationHistory || [])
      .slice()
      .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const locCoords = locationHistory
      .filter((e: any) => {
        if (e.location && Array.isArray(e.location) && e.location.length >= 2) return true;
        if (e.vehicleInfo?.location && Array.isArray(e.vehicleInfo.location) && e.vehicleInfo.location.length >= 2) return true;
        return e.latitude != null && e.longitude != null;
      })
      .map((e: any) => {
        const loc = e.location ?? e.vehicleInfo?.location;
        if (loc && Array.isArray(loc) && loc.length >= 2) {
          return { lat: loc[1], lng: loc[0], date: e.date };
        }
        return { lat: e.latitude, lng: e.longitude, date: e.date };
      });

    if (locCoords.length < 2) {
      return { locCoords, cumulativeKm: [] as number[], totalKm: 0 };
    }

    const cumulativeKm = cumulativeDistancesKm(locCoords);
    const totalKm = cumulativeKm[cumulativeKm.length - 1] ?? 0;
    return { locCoords, cumulativeKm, totalKm };
  }, []);

  // --- Ambulance last 24hr trips state ---
  const [ambulanceTrips, setAmbulanceTrips] = useState<Booking[]>([]);
  const [ambulanceTripsLoading, setAmbulanceTripsLoading] = useState(false);
  const [tripsRefreshTrigger, setTripsRefreshTrigger] = useState(0);
  const [revertTripModalBooking, setRevertTripModalBooking] = useState<Booking | null>(null);
  const [revertTripSubmitting, setRevertTripSubmitting] = useState(false);
  const [tripDetailBooking, setTripDetailBooking] = useState<Booking | null>(null);
  const [tripDetailsDownloading, setTripDetailsDownloading] = useState(false);
  const [tripDetailsDownloadError, setTripDetailsDownloadError] = useState('');
  const [showTripDetailsDownloadMenu, setShowTripDetailsDownloadMenu] = useState(false);
  const tripDetailsDownloadMenuRef = React.useRef<HTMLDivElement>(null);

  // Close fuel graph fullscreen on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFuelGraphFullscreen) {
        setIsFuelGraphFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFuelGraphFullscreen]);

  // Close trip details download menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showTripDetailsDownloadMenu && tripDetailsDownloadMenuRef.current && !tripDetailsDownloadMenuRef.current.contains(event.target as Node)) {
        setShowTripDetailsDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTripDetailsDownloadMenu]);

  // Download booking details (PDF/Excel) for trip drawer
  const handleDownloadTripDetails = async (format: 'pdf' | 'excel') => {
    if (!tripDetailBooking) return;
    try {
      setTripDetailsDownloading(true);
      setTripDetailsDownloadError('');
      setShowTripDetailsDownloadMenu(false);

      const bookingId = (tripDetailBooking as any)._id || (tripDetailBooking as any).id;
      if (!bookingId) {
        setTripDetailsDownloadError('Booking ID is missing for download');
        return;
      }

      let response;
      if (format === 'pdf') {
        response = await downloadBookingDetailsPDF(bookingId);
        const { url } = response.data.data;
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      } else {
        response = await downloadBookingDetailsExcel(bookingId);
      }

      const { url, fileName } = response.data.data;
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'booking-details.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error(`Error downloading booking details ${format.toUpperCase()}:`, err);
      setTripDetailsDownloadError(
        err?.response?.data?.message || `Failed to download booking details ${format.toUpperCase()}`
      );
    } finally {
      setTripDetailsDownloading(false);
    }
  };

  useEffect(() => {
    fetchTenantGroupId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.tenantId]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const fetchTenantGroupId = async () => {
    if (!user?.tenantId) {
      console.warn('No tenantId found in user data');
      return;
    }

    try {
      const response = await getTenant(user.tenantId);
      if (response.data.success && response.data.data?.spec?.groupId) {
        setGroupId(response.data.data.spec.groupId);
      } else {
        console.warn('No groupId found in tenant data');
      }

      // Fetch hospital location from tenant
      if (response.data.success && response.data.data?.spec?.hospital) {
        const hospital = response.data.data.spec.hospital;
        const hospitalLat = hospital.hospitalLocation?.latitude ?? hospital.latitude;
        const hospitalLng = hospital.hospitalLocation?.longitude ?? hospital.longitude;

        // Validate that coordinates are valid numbers
        if (
          hospitalLat !== undefined &&
          hospitalLng !== undefined &&
          typeof hospitalLat === 'number' &&
          typeof hospitalLng === 'number' &&
          !isNaN(hospitalLat) &&
          !isNaN(hospitalLng) &&
          hospitalLat >= -90 && hospitalLat <= 90 &&
          hospitalLng >= -180 && hospitalLng <= 180
        ) {
          setHospitalLocation({ latitude: hospitalLat, longitude: hospitalLng });
        } else {
          console.warn('Invalid hospital location coordinates, defaulting to Delhi, India');
          setHospitalLocation(null);
        }
      } else {
        // No hospital data available, default to Delhi
        console.warn('No hospital location found in tenant data, defaulting to Delhi, India');
        setHospitalLocation(null);
      }

      // Also load ambulance types for display names
      try {
        const typesRes = await getAmbulanceTypes(1, 100);
        setAmbulanceTypes(typesRes.data.data || []);
      } catch (err) {
        console.error('Failed to fetch ambulance types for LiveTracking:', err);
      }
    } catch (err: any) {
      console.error('Failed to fetch tenant groupId:', err);
      // On error, ensure hospital location is null so it defaults to Delhi
      setHospitalLocation(null);
    }
  };

  // Fetch hospital default location from tenant settings (similar to AmbulanceManagement.tsx)
  useEffect(() => {
    const fetchHospitalDefaultLocation = async () => {
      if (!user?.tenantId || user?.isSystem) {
        setHospitalDefaultLocation(null);
        return;
      }

      try {
        const response = await getTenant(user.tenantId);
        if (response.data.success && response.data.data) {
          const tenant = response.data.data;
          setConvertKnotsToKmph(tenant.spec?.configSettings?.convertKnotsToKmph ?? false);
          const configSettings = (tenant.spec?.configSettings || {}) as Record<string, unknown>;
          setDashboardRouteDataRefreshMs(
            resolveDashboardRouteDataRefreshMs(
              configSettings.dashboardRouteDataRefreshMs ?? configSettings.currentRouteDataRefreshMs
            )
          );
          const hospital = tenant.spec?.hospital;

          if (hospital) {
            // Check hospitalLocation first (preferred), then fallback to direct latitude/longitude
            const hospitalLat = hospital.hospitalLocation?.latitude ?? hospital.latitude;
            const hospitalLng = hospital.hospitalLocation?.longitude ?? hospital.longitude;

            if (hospitalLat !== undefined && hospitalLng !== undefined &&
              !isNaN(hospitalLat) && !isNaN(hospitalLng) &&
              hospitalLat >= -90 && hospitalLat <= 90 &&
              hospitalLng >= -180 && hospitalLng <= 180) {
              setHospitalDefaultLocation({ latitude: hospitalLat, longitude: hospitalLng });
              return;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching hospital default location:', error);
        setDashboardRouteDataRefreshMs(15_000);
      }

      setHospitalDefaultLocation(null);
    };

    fetchHospitalDefaultLocation();
  }, [user?.tenantId, user?.isSystem]);

  const fetchTrackingData = async () => {
    try {
      // Don't set loading to true on refreshes - only update data silently
      setError('');
      const response = await getTrackingData();
      setOnTripAmbulances(response.data.data.onTripAmbulances || []);
      setOnDutyStaff(response.data.data.onDutyStaff || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load tracking data');
      console.error('Failed to load tracking data:', err);
    } finally {
      // Only set initialLoading to false on first load
      if (initialLoading) {
        setInitialLoading(false);
      }
    }
  };

  // Note: Active bookings are now fetched as part of fetchAllAmbulances via the optimized endpoint
  // This eliminates the need for a separate API call and reduces client-side processing

  // Create a Map for O(1) lookup - now using activeBooking from ambulance spec
  const bookingMap = React.useMemo(() => {
    const map = new Map<string, { status: string }>();
    allAmbulances.forEach((amb: any) => {
      if (amb.spec?.activeBooking) {
        map.set(amb._id, amb.spec.activeBooking);
      }
    });
    return map;
  }, [allAmbulances]);

  // Get running status for an ambulance based on its active booking
  const getAmbulanceRunningStatus = React.useCallback((ambulanceId: string): { status: string; color: string } | null => {
    const booking = bookingMap.get(ambulanceId);
    if (!booking) return null;

    const statusMap: Record<string, { status: string; color: string }> = {
      [BOOKING_STATUS.PENDING]: { status: BOOKING_STATUS_LABELS[BOOKING_STATUS.PENDING], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.PENDING] },
      [BOOKING_STATUS.ASSIGNED]: { status: BOOKING_STATUS_LABELS[BOOKING_STATUS.ASSIGNED], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.ASSIGNED] },
      [BOOKING_STATUS.IN_TRANSIT]: { status: BOOKING_STATUS_LABELS[BOOKING_STATUS.IN_TRANSIT], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.IN_TRANSIT] },
      [BOOKING_STATUS.PATIENT_PICKED]: { status: BOOKING_STATUS_LABELS[BOOKING_STATUS.PATIENT_PICKED], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.PATIENT_PICKED] },
      [BOOKING_STATUS.PATIENT_DROPPED]: { status: BOOKING_STATUS_LABELS[BOOKING_STATUS.PATIENT_DROPPED], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.PATIENT_DROPPED] },
    };

    return statusMap[booking.status] || null;
  }, [bookingMap]);

  // Get progress states for an ambulance
  const getAmbulanceProgressStates = React.useCallback((ambulanceId: string): { currentState: string; states: Array<{ label: string; status: string; color: string; active: boolean }> } | null => {
    const booking = bookingMap.get(ambulanceId);
    if (!booking) return null;

    const states = [
      { label: BOOKING_STATUS_LABELS[BOOKING_STATUS.ASSIGNED], status: BOOKING_STATUS.ASSIGNED, color: BOOKING_STATUS_COLORS[BOOKING_STATUS.ASSIGNED] },
      { label: BOOKING_STATUS_LABELS[BOOKING_STATUS.IN_TRANSIT], status: BOOKING_STATUS.IN_TRANSIT, color: BOOKING_STATUS_COLORS[BOOKING_STATUS.IN_TRANSIT] },
      { label: BOOKING_STATUS_LABELS[BOOKING_STATUS.PATIENT_PICKED], status: BOOKING_STATUS.PATIENT_PICKED, color: BOOKING_STATUS_COLORS[BOOKING_STATUS.PATIENT_PICKED] },
      { label: BOOKING_STATUS_LABELS[BOOKING_STATUS.PATIENT_DROPPED], status: BOOKING_STATUS.PATIENT_DROPPED, color: BOOKING_STATUS_COLORS[BOOKING_STATUS.PATIENT_DROPPED] },
    ];

    const currentStatus = booking.status;
    const currentIndex = states.findIndex((s) => s.status === currentStatus);

    const progressStates = states.map((state, index) => ({
      ...state,
      active: index <= currentIndex,
    }));

    return {
      currentState: currentStatus,
      states: progressStates,
    };
  }, [bookingMap]);

  const fetchAllBookings = async () => {
    try {
      // Calculate date range for last 24 hours
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

      // Format dates as YYYY-MM-DD
      const formatDateLocal = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const startDate = formatDateLocal(twentyFourHoursAgo);
      const endDate = formatDateLocal(now);

      // Fetch bookings from last 24 hours for summary
      const response = await getBookings(
        1,
        1000, // Fetch up to 1000 bookings for summary
        undefined, // No status filter - get all statuses
        undefined, // No patientId filter
        undefined, // No ambulanceId filter
        undefined, // No priority filter
        undefined, // No search filter
        startDate, // Start date (24 hours ago)
        endDate    // End date (today)
      );
      setAllBookings(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to fetch all bookings for summary:', err);
      setAllBookings([]);
    }
  };

  const fetchAllAmbulances = async (): Promise<Ambulance[]> => {
    try {
      // ALWAYS fetch ALL ambulances without any filters
      // Filters will be applied in the frontend for display purposes only
      // This ensures allAmbulances always contains the complete, unfiltered dataset
      const response = await getAmbulancesWithBookingStatus(
        1,
        1000, // Fetch up to 1000 for now (can be paginated later)
        undefined, // No search term - fetch all
        undefined, // No status filter - fetch all statuses
        undefined  // No running status filter - fetch all running statuses
      );

      const ambulances = response.data.data || [];
      // allAmbulances should ALWAYS contain the complete unfiltered dataset
      setAllAmbulances(ambulances);

      // Extract active bookings from the response (ambulances now have activeBooking field)
      const bookings: Booking[] = [];
      ambulances.forEach((amb: any) => {
        if (amb.spec?.activeBooking) {
          // Get the actual booking ID from activeBooking - backend now includes _id
          const bookingId = amb.spec.activeBooking._id || amb.spec.activeBooking.bookingId || amb.spec.activeBooking.id;

          // Only add booking if we have a valid booking ID (not ambulance ID)
          if (bookingId && bookingId !== amb._id) {
            bookings.push({
              _id: bookingId,
              metadata: amb.metadata,
              spec: {
                ...amb.spec,
                ambulanceId: amb._id,
                status: amb.spec.activeBooking.status,
                patientId: amb.spec.activeBooking.patientId || '',
                bookedBy: amb.spec.activeBooking.bookedBy || '',
                bookedByDisplay: amb.spec.activeBooking.bookedByDisplay,
                timings: amb.spec.activeBooking.timings || {
                  bookingTime: amb.spec.activeBooking.bookingTime || amb.spec.activeBooking.timings?.bookingTime || 0,
                },
                pickupLocation: amb.spec.activeBooking.pickupLocation || { latitude: 0, longitude: 0 },
                dropoffLocation: amb.spec.activeBooking.dropoffLocation || { latitude: 0, longitude: 0 },
                priority: amb.spec.activeBooking.priority || 'medium',
              },
            } as Booking);
          } else {
            console.warn('[Tracking] Skipping booking extraction - no valid booking ID', {
              ambulanceId: amb._id,
              activeBooking: amb.spec.activeBooking,
              extractedBookingId: bookingId,
            });
          }
        }
      });
      setActiveBookings(bookings);

      // Only set initialLoading to false on first load
      if (initialLoading) {
        setInitialLoading(false);
      }
      return ambulances;
    } catch (err: any) {
      console.error('Failed to load ambulances:', err);
      setError(err.response?.data?.message || 'Failed to load ambulances');
      // Only set initialLoading to false on first load
      if (initialLoading) {
        setInitialLoading(false);
      }
      return [];
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await getAreas(1, 1000); // Fetch all areas
      setAreas(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load areas:', err);
    }
  };

  /** Returns the location payload(s) just written to state (for immediate use without waiting on React state). */
  const fetchLiveLocations = React.useCallback(async (
    selectedAmbulanceData?: Ambulance | null,
    options?: { useTraccar?: boolean }
  ): Promise<any[] | undefined> => {
    // If ambulance is selected, fetch only that ambulance's live location by IMEI
    if (selectedAmbulanceData) {
      const gpsDeviceId = selectedAmbulanceData.spec?.gpsDevice?.deviceId;
      if (!gpsDeviceId) {
        setLiveLocationsApiFinished(true);
        return undefined;
      }

      const fetchOne = async (): Promise<any[] | undefined> => {
        try {
          const response = options?.useTraccar
            ? await getTraccarLiveLocation(gpsDeviceId)
            : await getAmbulanceLiveLocationByDeviceId(gpsDeviceId);
          const liveLocationData = options?.useTraccar
            ? response.data?.data
            : response.data || response;

          if (liveLocationData?.liveLocation && Array.isArray(liveLocationData.liveLocation) && liveLocationData.liveLocation.length === 2) {
            const [lng, lat] = liveLocationData.liveLocation;
            if (lng !== 0 && lat !== 0 && !isNaN(lng) && !isNaN(lat)) {
              setLiveLocations([liveLocationData]);
              setLiveLocationsApiFinished(true);
              return [liveLocationData];
            }
          }
          setLiveLocationsApiFinished(true);
          return undefined;
        } catch (err: any) {
          console.error('Failed to fetch selected ambulance live location:', err);
          setLiveLocationsApiFinished(true);
          return undefined;
        }
      };

      if (options?.useTraccar) {
        const inFlight = traccarFetchInFlightRef.current.get(gpsDeviceId);
        if (inFlight) return inFlight;
        const promise = fetchOne().finally(() => {
          traccarFetchInFlightRef.current.delete(gpsDeviceId);
        });
        traccarFetchInFlightRef.current.set(gpsDeviceId, promise);
        return promise;
      }

      return fetchOne();
    }

    // If no ambulance selected, fetch all ambulances by groupId
    if (!groupId) {
      setLiveLocationsApiFinished(true);
      return undefined;
    }

    try {
      const response = await getAmbulanceLiveLocation(groupId);
      // API response is an array of objects with structure:
      // { id, deviceId, isOffline, lastUpdate, liveLocation: [longitude, latitude] }
      const data = response.data;
      let locations: any[] = [];

      if (Array.isArray(data)) {
        locations = data;
      } else if (data.data && Array.isArray(data.data)) {
        locations = data.data;
      } else if (data.vehicles && Array.isArray(data.vehicles)) {
        locations = data.vehicles;
      } else if (data.ambulances && Array.isArray(data.ambulances)) {
        locations = data.ambulances;
      }

      // Filter out invalid locations (empty arrays or [0,0])
      const validLocations = locations.filter((loc: any) => {
        const liveLoc = loc.liveLocation;
        if (!Array.isArray(liveLoc) || liveLoc.length !== 2) return false;
        const [lng, lat] = liveLoc;
        // Filter out [0,0] and empty/invalid coordinates
        return lng !== 0 && lat !== 0 && !isNaN(lng) && !isNaN(lat);
      });

      setLiveLocations(validLocations);
      setLiveLocationsApiFinished(true);

      // Sync ambulance status from device online/offline (handled by separate 5-minute interval)
      return validLocations;
    } catch (err: any) {
      console.error('Failed to fetch live locations:', err);
      // Don't set error state - live locations are optional
      setLiveLocationsApiFinished(true);
      return undefined;
    }
  }, [groupId]);

  // Helper function to fetch all tracking data (eliminates duplication)
  // Using useRef to store the latest functions to avoid stale closures
  const fetchAllTrackingDataRef = React.useRef<() => void>();

  fetchAllTrackingDataRef.current = () => {
    // Don't refresh if an info window is currently open (prevents disruption when user is viewing details)
    if (isInfoWindowOpenRef.current) {
      return;
    }
    fetchTrackingData();
    fetchAllAmbulances(); // This now includes active bookings from optimized endpoint
    fetchAllBookings(); // Fetch all bookings for summary
    // Dashboard route polling (tenant setting): Current Route in-trip + Day Route idle — skip duplicate live fetch here
    const hasStartedTripOnCurrentRoute =
      routeViewMode === ROUTE_VIEW_MODE.CURRENT &&
      selectedAmbulance &&
      activeBookings.some(
        (b) =>
          b.spec.ambulanceId === selectedAmbulance._id &&
          (b.spec.status === BOOKING_STATUS.IN_TRANSIT ||
            b.spec.status === BOOKING_STATUS.PATIENT_PICKED ||
            b.spec.status === BOOKING_STATUS.PATIENT_DROPPED)
      );
    const dayRouteHistoryIsToday =
      String(dayRouteHistoryDateRef.current || '').trim() === formatLocalDateYMD(new Date());
    const dayRouteIdleDedicatedPoll =
      routeViewMode === ROUTE_VIEW_MODE.DAY &&
      selectedAmbulance &&
      !dayRouteIsPlayingRef.current &&
      dayRouteHistoryIsToday;
    const skipLiveForDedicatedDashboardRoutePoll = hasStartedTripOnCurrentRoute || dayRouteIdleDedicatedPoll;
    if (!skipLiveForDedicatedDashboardRoutePoll) {
      void fetchLiveLocations(selectedAmbulance);
    }
    // Trigger refresh of bookings when tracking data is refreshed
    if (onTrackingDataRefreshed) {
      onTrackingDataRefreshed();
    }
  };

  useEffect(() => {
    // Reset API finished state when groupId changes
    setLiveLocationsApiFinished(false);

    if (groupId) {
      // Call the ref function to avoid dependency issues
      fetchAllTrackingDataRef.current?.();
      // Refresh data every 5 seconds
      const interval = setInterval(() => {
        fetchAllTrackingDataRef.current?.();
      }, 5000);
      return () => clearInterval(interval);
    } else {
      // If no groupId, still try to load ambulances and bookings
      fetchAllAmbulances();
      fetchAllBookings();
      fetchAreas();
      setLiveLocationsApiFinished(true);
    }
    // Only fetch when groupId changes - filters are applied in frontend, not in API call
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // Sync ambulance status to "On Trip" (on_duty) when associated booking is In Transit, Patient Pickup, or Patient Drop
  const syncAmbulanceStatusToOnTripFromBookings = async () => {
    try {
      const response = await getAmbulancesWithBookingStatus(
        1,
        1000,
        undefined,
        undefined,
        undefined
      );
      const ambulances = response.data.data || [];
      const tripBookingStatuses = [
        BOOKING_STATUS.IN_TRANSIT,
        BOOKING_STATUS.PATIENT_PICKED,
        BOOKING_STATUS.PATIENT_DROPPED,
      ];

      for (const amb of ambulances) {
        const activeBooking = (amb as any).spec?.activeBooking;
        const bookingStatus = activeBooking?.status;
        const currentStatus = amb.spec?.status;

        const hasTripBooking =
          bookingStatus && tripBookingStatuses.includes(bookingStatus);
        const notOnTrip = currentStatus !== 'on_duty';

        if (hasTripBooking && notOnTrip) {
          void updateAmbulance(amb._id, {
            spec: {
              status: 'on_duty',
            },
          })
            .then(() => {
              void fetchAllAmbulances();
            })
            .catch((err: any) => {
              console.error(
                `[Tracking] Failed to set ambulance ${amb._id} to on_duty:`,
                err
              );
            });
        }
      }
    } catch (err: any) {
      console.error(
        '[Tracking] Failed to sync ambulance status to on_trip from bookings:',
        err
      );
    }
  };

  // Update live locations when selection changes (non in-trip Current Route uses binimise API; in-trip uses 30s Traccar poll only)
  useEffect(() => {
    if (!groupId) return;
    if (isInTripOnCurrentRoute(selectedAmbulance?._id, routeViewMode, activeBookings)) {
      return;
    }
    void fetchLiveLocations(selectedAmbulance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAmbulance?._id, routeViewMode, activeBookings]);

  // Sync ambulance status to "On Trip" when booking is In Transit / Patient Pickup / Patient Drop — every 3 minutes
  useEffect(() => {
    const THREE_MINUTES_MS = 3 * 60 * 1000;

    const initialTimeout = setTimeout(() => {
      syncAmbulanceStatusToOnTripFromBookings();
    }, 2000);

    const interval = setInterval(() => {
      syncAmbulanceStatusToOnTripFromBookings();
    }, THREE_MINUTES_MS);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh when refreshTrigger changes (triggered by actions in AllBookings)
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0 && groupId) {
      fetchAllTrackingDataRef.current?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Day route trail via backend → Traccar reports/route
  const fetchDayRouteHistory = React.useCallback(async (ambulance: Ambulance, dateStr: string) => {
    if (!ambulance || !ambulance._id) {
      return;
    }

    try {
      setRouteLoading(true);
      setLocationHistoryRoute([]);
      setRouteBookings([]);

      // Get IMEI from ambulance GPS device
      const imei = ambulance.spec?.gpsDevice?.deviceId;
      if (!imei) {
        console.warn('No IMEI found for ambulance:', ambulance._id);
        setRouteLoading(false);
        return;
      }

      const response = await getTraccarDayRoute(imei, dateStr);
      const responseData = response.data?.data ?? response.data ?? response;
      const history = responseData.history || [];

      if (Array.isArray(history) && history.length > 0) {
        const processedRoute = history
          .filter((item: any) => {
            const lat = item.lat ?? item.latitude;
            const lng = item.lng ?? item.longitude;
            return typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng);
          })
          .map((item: any) => ({
            lat: item.lat ?? item.latitude,
            lng: item.lng ?? item.longitude,
            date: item.date,
            speed: item.speed ?? null,
            odometer: item.odometer ?? null,
            fuel: item.fuel ?? null,
          }));

        if (processedRoute.length > 0) {
          setLocationHistoryRoute(processedRoute);
        } else {
          console.warn('[Tracking] fetchDayRouteHistory: No valid location data in history', {
            historyLength: history.length,
          });
          setLocationHistoryRoute([]);
        }
      } else {
        console.warn('[Tracking] fetchDayRouteHistory: No history data found in response', {
          responseData,
        });
        setLocationHistoryRoute([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch location history:', err);
      setLocationHistoryRoute([]);
    } finally {
      setRouteLoading(false);
    }
  }, []);

  // Load day route when ambulance, Day tab, or selected date changes
  useEffect(() => {
    if (!selectedAmbulance || routeViewMode !== ROUTE_VIEW_MODE.DAY) return;
    fetchDayRouteHistory(selectedAmbulance, dayRouteHistoryDate);
  }, [selectedAmbulance, routeViewMode, dayRouteHistoryDate, fetchDayRouteHistory]);

  // Fetch booking details with pickup/drop locations
  const fetchBookingDetails = React.useCallback(async (bookingId: string) => {
    if (!bookingId) {
      console.warn('[Tracking] fetchBookingDetails: No booking ID provided');
      return null;
    }

    // Check if booking is already being fetched to prevent duplicate calls
    if (fetchingBookingsRef.current.has(bookingId)) {
      // Wait a bit and check if booking is now in activeBookings
      await new Promise(resolve => setTimeout(resolve, 200));
      const existingBooking = activeBookings.find(b => b._id === bookingId);
      if (existingBooking) {
        return existingBooking;
      }
      // If still not found, return null (the other fetch should complete soon)
      return null;
    }

    // Check if booking is already in activeBookings and has valid locations
    const existingBooking = activeBookings.find(b => b._id === bookingId);
    if (existingBooking) {
      const hasValidPickup = existingBooking.spec.pickupLocation &&
        existingBooking.spec.pickupLocation.latitude !== 0 &&
        existingBooking.spec.pickupLocation.longitude !== 0;
      const hasValidDropoff = existingBooking.spec.dropoffLocation &&
        existingBooking.spec.dropoffLocation.latitude !== 0 &&
        existingBooking.spec.dropoffLocation.longitude !== 0;

      if (hasValidPickup && hasValidDropoff) {
        return existingBooking;
      }
    }

    // Mark as being fetched
    fetchingBookingsRef.current.add(bookingId);

    try {
      const response = await getBooking(bookingId);

      if (response.data.success && response.data.data) {
        const booking = response.data.data;

        // Update activeBookings with the fetched booking details
        setActiveBookings((prev) => {
          const updated = [...prev];
          const index = updated.findIndex((b) => b._id === bookingId);
          if (index !== -1) {
            updated[index] = booking;
          } else {
            // If not found, add it
            updated.push(booking);
          }
          return updated;
        });

        // Remove from fetching set
        fetchingBookingsRef.current.delete(bookingId);
        return booking;
      }

      // Remove from fetching set if no data
      fetchingBookingsRef.current.delete(bookingId);
    } catch (err: any) {
      console.error('[Tracking] Failed to fetch booking details:', err);
      // Remove from fetching set on error
      fetchingBookingsRef.current.delete(bookingId);
    }
    return null;
  }, [activeBookings]);

  const refetchAmbulanceFuelSummary = React.useCallback(
    async (amb: Ambulance | null, options?: { withLoading?: boolean }) => {
      if (!amb?.spec?.gpsDevice?.deviceId) {
        setAmbulanceFuelData(null);
        return;
      }
      const withLoading = options?.withLoading !== false;
      if (withLoading) setAmbulanceFuelLoading(true);
      try {
        const imei = amb.spec.gpsDevice.deviceId;
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        const response = await getTrackingSummary(imei, undefined, dateStr, dateStr);
        const summaryData = response.data.data;
        if (summaryData) {
          setAmbulanceFuelData(summaryData);
        } else {
          setAmbulanceFuelData(null);
        }
      } catch (error: any) {
        console.error('Failed to fetch fuel data:', error);
        setAmbulanceFuelData(null);
      } finally {
        if (withLoading) setAmbulanceFuelLoading(false);
      }
    },
    []
  );

  // Calculate current route based on ambulance status and booking
  // options.silent: when true (e.g. 5-sec ETA refresh), don't set routeLoading to avoid button/ETA flicker
  const calculateCurrentRoute = React.useCallback(async (
    ambulance: Ambulance,
    liveLocationsData?: any[],
    options?: { silent?: boolean }
  ) => {
    const showLoading = !options?.silent;

    // Set loading state only when not silent (avoids flicker on 5-sec refresh)
    if (showLoading) setRouteLoading(true);

    // Find active booking for this ambulance
    let activeBooking = activeBookings.find((b) => b.spec.ambulanceId === ambulance._id);

    // Always fetch fresh booking details to ensure we have the latest pickup/dropoff locations
    // This ensures we're using the exact locations from the API, not stale cached data
    let bookingId = activeBooking?._id || (ambulance.spec as any)?.activeBooking?._id || (ambulance.spec as any)?.activeBooking?.bookingId;

    if (!bookingId && !activeBooking) {
      // Try to find booking by querying the API
      try {
        const bookingsResponse = await getBookings(
          1,
          100,
          undefined,
          undefined,
          ambulance._id,
          undefined,
          undefined
        );

        const bookings = bookingsResponse.data.data || [];
        const foundBooking = bookings.find((b: Booking) =>
          b.spec.ambulanceId === ambulance._id &&
          ACTIVE_BOOKING_STATUSES.includes(b.spec.status as BookingStatus)
        );

        if (foundBooking) {
          bookingId = foundBooking._id;
        }
      } catch (err) {
        console.error('[Tracking] calculateCurrentRoute: Error querying bookings API', err);
      }
    }

    // Always fetch fresh booking details to get the latest locations from API
    if (bookingId) {
      const fetchedBooking = await fetchBookingDetails(bookingId);
      if (fetchedBooking) {
        activeBooking = fetchedBooking;
      } else {
        console.warn('[Tracking] calculateCurrentRoute: Failed to fetch booking details', { bookingId });
      }
    }

    if (!activeBooking) {
      console.warn('[Tracking] calculateCurrentRoute: No active booking found');
      setCurrentRoute(null);
      if (showLoading) setRouteLoading(false);
      return;
    }

    try {
      // Get current location of ambulance from live location API
      let currentLocation = ambulance.spec.currentLocation;
      let currentLocationSource = 'Stored location (ambulance.spec.currentLocation)';

      // Get GPS device ID (IMEI) from ambulance
      const gpsDeviceId = ambulance.spec?.gpsDevice?.deviceId;

      if (gpsDeviceId) {
        // First, try to use already-fetched liveLocations data to avoid unnecessary API calls
        const locationsToSearch = liveLocationsData || liveLocations;
        const liveLocationFromCache = locationsToSearch.find(
          (loc: any) => loc.deviceId?.toLowerCase() === gpsDeviceId.toLowerCase() ||
            loc.id?.toLowerCase() === gpsDeviceId.toLowerCase()
        );

        if (liveLocationFromCache?.liveLocation && Array.isArray(liveLocationFromCache.liveLocation) && liveLocationFromCache.liveLocation.length === 2) {
          const [lng, lat] = liveLocationFromCache.liveLocation;
          currentLocation = { latitude: lat, longitude: lng };
          currentLocationSource = `Live location (cached, deviceId: ${gpsDeviceId}, isOffline: ${liveLocationFromCache.isOffline}, lastUpdate: ${liveLocationFromCache.lastUpdate})`;
        } else {
          currentLocationSource = 'Stored location (fallback: no cached live location; wait for Traccar poll)';
        }
      } else {
        // Use stored location if no device ID
        currentLocationSource = 'Stored location (no GPS device ID)';
      }

      // Get pickup and dropoff locations directly from booking response
      // These come from GET /api/bookings/:id API response
      const pickupLocation = activeBooking.spec.pickupLocation;
      const dropoffLocation = activeBooking.spec.dropoffLocation;
      const bookingStatus = activeBooking.spec.status;
      const bookingType = (activeBooking.spec as any)?.bookingType || BOOKING_TYPE.TO_HOSPITAL;

      // Validate coordinates exist and are valid (exclude 0,0 - often used as placeholder / Gulf of Guinea)
      const isValidCoord = (lat: number, lng: number) =>
        typeof lat === 'number' && typeof lng === 'number' &&
        !isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0);

      const hasValidCurrentLocation = currentLocation &&
        isValidCoord(currentLocation.latitude, currentLocation.longitude);

      const hasValidPickupLocation = pickupLocation &&
        isValidCoord(pickupLocation.latitude, pickupLocation.longitude);

      const hasValidDropoffLocation = dropoffLocation &&
        isValidCoord(dropoffLocation.latitude, dropoffLocation.longitude);


      // Route calculation: Show only the current leg based on status and booking type.
      // to_hospital: in_transit → ambulance→pickup; patient_picked → ambulance→dropoff; then complete.
      // from_hospital: in_transit → ambulance→pickup; patient_picked → ambulance→dropoff; patient_dropped → ambulance→hospital (pickup); then complete.
      const onWayToPick = bookingStatus === BOOKING_STATUS.ASSIGNED ||
        bookingStatus === BOOKING_STATUS.PENDING ||
        bookingStatus === BOOKING_STATUS.IN_TRANSIT;

      // Assigned / pending / in_transit: show path from ambulance to pickup only
      if (onWayToPick && hasValidCurrentLocation && hasValidPickupLocation && currentLocation && pickupLocation) {
        const isToHospital = bookingType === BOOKING_TYPE.TO_HOSPITAL;
        const pickupStop: RouteStop = isToHospital
          ? {
            lat: pickupLocation.latitude,
            lng: pickupLocation.longitude,
            type: 'patient',
            label: 'Patient Pickup',
          }
          : {
            lat: pickupLocation.latitude,
            lng: pickupLocation.longitude,
            type: 'hospital',
            label: 'Hospital Pickup',
          };

        setCurrentRoute({
          origin: {
            lat: currentLocation.latitude,
            lng: currentLocation.longitude,
          },
          destination: {
            lat: pickupStop.lat,
            lng: pickupStop.lng,
          },
          destinationType: 'pickup' as const,
          stops: [pickupStop],
          bookingType: bookingType as BookingType,
          bookingStatus: bookingStatus,
        });
        return;
      }

      // Patient picked: show path from ambulance to dropoff (both to_hospital and from_hospital)
      const isPickupDone = bookingStatus === BOOKING_STATUS.PATIENT_PICKED || bookingStatus === BOOKING_STATUS.PATIENT_DROPPED;
      const isFromHospital = bookingType === BOOKING_TYPE.FROM_HOSPITAL;
      const showReturnToHospital = isPickupDone && bookingStatus === BOOKING_STATUS.PATIENT_DROPPED && isFromHospital;

      if (showReturnToHospital && hasValidCurrentLocation && hasValidPickupLocation && currentLocation && pickupLocation) {
        // from_hospital + patient_dropped: show path from ambulance to hospital (pickup location)
        const pickupStop: RouteStop = {
          lat: pickupLocation.latitude,
          lng: pickupLocation.longitude,
          type: 'hospital',
          label: 'Hospital',
        };
        const dropoffStop: RouteStop = hasValidDropoffLocation && dropoffLocation
          ? { lat: dropoffLocation.latitude, lng: dropoffLocation.longitude, type: 'patient', label: 'Drop Location' }
          : pickupStop;
        setCurrentRoute({
          origin: { lat: currentLocation.latitude, lng: currentLocation.longitude },
          destination: { lat: pickupStop.lat, lng: pickupStop.lng },
          destinationType: 'pickup' as const,
          stops: [dropoffStop, pickupStop],
          bookingType: bookingType as BookingType,
          bookingStatus: bookingStatus,
        });
        return;
      }

      // Patient picked (or patient_dropped for to_hospital): route to dropoff; include both stops for markers
      if (isPickupDone && hasValidCurrentLocation && hasValidDropoffLocation && currentLocation && dropoffLocation) {
        const isToHospital = bookingType === BOOKING_TYPE.TO_HOSPITAL;
        const pickupStop: RouteStop | null = hasValidPickupLocation && pickupLocation
          ? (isToHospital
            ? { lat: pickupLocation.latitude, lng: pickupLocation.longitude, type: 'patient', label: 'Patient Pickup' }
            : { lat: pickupLocation.latitude, lng: pickupLocation.longitude, type: 'hospital', label: 'Hospital Pickup' })
          : null;
        const dropoffStop: RouteStop = isToHospital
          ? {
            lat: dropoffLocation.latitude,
            lng: dropoffLocation.longitude,
            type: 'hospital',
            label: 'Hospital Dropoff',
          }
          : {
            lat: dropoffLocation.latitude,
            lng: dropoffLocation.longitude,
            type: 'patient',
            label: 'Drop Location',
          };

        const stopsForMarkers = pickupStop ? [pickupStop, dropoffStop] : [dropoffStop];

        setCurrentRoute({
          origin: {
            lat: currentLocation.latitude,
            lng: currentLocation.longitude,
          },
          destination: {
            lat: dropoffStop.lat,
            lng: dropoffStop.lng,
          },
          destinationType: 'dropoff' as const,
          stops: stopsForMarkers,
          bookingType: bookingType as BookingType,
          bookingStatus: bookingStatus,
        });
        return;
      }

      if (bookingStatus === BOOKING_STATUS.PATIENT_PICKED || bookingStatus === BOOKING_STATUS.PATIENT_DROPPED) {
        if (hasValidCurrentLocation && hasValidDropoffLocation && currentLocation && dropoffLocation) {
          const isToHospital = bookingType === BOOKING_TYPE.TO_HOSPITAL;
          const pickupStop: RouteStop | null = hasValidPickupLocation && pickupLocation
            ? (isToHospital
              ? { lat: pickupLocation.latitude, lng: pickupLocation.longitude, type: 'patient', label: 'Patient Pickup' }
              : { lat: pickupLocation.latitude, lng: pickupLocation.longitude, type: 'hospital', label: 'Hospital Pickup' })
            : null;
          const dropoffStop: RouteStop = isToHospital
            ? { lat: dropoffLocation.latitude, lng: dropoffLocation.longitude, type: 'hospital', label: 'Hospital Dropoff' }
            : { lat: dropoffLocation.latitude, lng: dropoffLocation.longitude, type: 'patient', label: 'Drop Location' };
          const stopsForMarkers = pickupStop ? [pickupStop, dropoffStop] : [dropoffStop];

          const route = {
            origin: {
              lat: currentLocation.latitude,
              lng: currentLocation.longitude,
            },
            destination: {
              lat: dropoffLocation.latitude,
              lng: dropoffLocation.longitude,
            },
            destinationType: 'dropoff' as const,
            stops: stopsForMarkers,
            bookingType: bookingType as BookingType,
            bookingStatus: bookingStatus,
          };
          setCurrentRoute(route);
        } else {
          console.warn('[Tracking] Invalid coordinates for current route (ambulance to dropoff):', {
            origin: {
              currentLocation,
              hasValidCurrentLocation,
              source: currentLocationSource,
            },
            destination: {
              dropoffLocation,
              hasValidDropoffLocation,
              source: 'Booking API',
            },
          });
          setCurrentRoute(null);
        }
      } else {
        console.warn('[Tracking] Unknown booking status, cannot calculate route', { bookingStatus });
        setCurrentRoute(null);
      }
    } catch (err) {
      console.error('Failed to calculate current route:', err);
      setCurrentRoute(null);
    } finally {
      if (showLoading) setRouteLoading(false);
    }
  }, [activeBookings, fetchBookingDetails, liveLocations]);

  const calculateCurrentRouteRef = React.useRef(calculateCurrentRoute);
  calculateCurrentRouteRef.current = calculateCurrentRoute;

  // Handle ambulance click - toggle selection
  const handleAmbulanceClick = async (ambulance: Ambulance) => {
    // If clicking the same ambulance, deselect it
    if (selectedAmbulance?._id === ambulance._id) {
      setSelectedAmbulance(null);
      setLocationHistoryRoute([]);
      setRouteBookings([]);
      setCurrentRoute(null);
      setRouteViewMode(ROUTE_VIEW_MODE.DAY);
      setAmbulanceFuelData(null);
      setAmbulanceTrips([]);
      setTripDetailBooking(null);
      resetDayRoutePlayback();
    } else {
      setSelectedAmbulance(ambulance);
      // Set initial route view mode
      // Only show current route if trip has started (in_transit or arrived), not when just assigned
      const hasStartedTrip = activeBookings.some((b) =>
        b.spec.ambulanceId === ambulance._id &&
        (b.spec.status === BOOKING_STATUS.IN_TRANSIT || b.spec.status === BOOKING_STATUS.PATIENT_PICKED || b.spec.status === BOOKING_STATUS.PATIENT_DROPPED)
      );
      setRouteViewMode(hasStartedTrip ? ROUTE_VIEW_MODE.CURRENT : ROUTE_VIEW_MODE.DAY);
      // Day route history is loaded by useEffect (dayRouteHistoryDate + routeViewMode)

      // Always fetch booking details to get pickup/drop locations when ambulance is clicked
      // Try multiple ways to get booking ID - prioritize activeBooking._id from backend
      let bookingId = (ambulance.spec as any)?.activeBooking?._id;

      // If not found, try other possible locations
      if (!bookingId) {
        bookingId = (ambulance.spec as any)?.activeBooking?.bookingId ||
          (ambulance.spec as any)?.activeBooking?.id;
      }

      // If still not found, try to find it from activeBookings array
      if (!bookingId) {
        const foundBooking = activeBookings.find((b) => b.spec.ambulanceId === ambulance._id);
        if (foundBooking && foundBooking._id) {
          bookingId = foundBooking._id;
        }
      }

      // If still not found, query bookings API directly to find active booking for this ambulance
      if (!bookingId) {
        try {
          const bookingsResponse = await getBookings(
            1,
            100,
            undefined, // status - get all statuses to find any booking
            undefined, // patientId
            ambulance._id, // ambulanceId - filter by this ambulance
            undefined, // priority
            undefined // search
          );

          const bookings = bookingsResponse.data.data || [];
          // Find most recent active booking (assigned, in_transit, or arrived)
          // If no active booking, find the most recent booking for this ambulance
          const activeBooking = bookings.find((b: Booking) =>
            b.spec.ambulanceId === ambulance._id &&
            ACTIVE_BOOKING_STATUSES.includes(b.spec.status as BookingStatus)
          ) || bookings.find((b: Booking) => b.spec.ambulanceId === ambulance._id);

          if (activeBooking) {
            bookingId = activeBooking._id;
            // Add to activeBookings state
            setActiveBookings((prev) => {
              if (!prev.find(b => b._id === bookingId)) {
                return [...prev, activeBooking];
              }
              return prev;
            });
          }
        } catch (err) {
          console.error('[Tracking] Error querying bookings API:', err);
        }
      }

      // Always fetch booking details if booking ID is found, regardless of ambulance status
      if (bookingId && bookingId !== ambulance._id) {
        try {
          await fetchBookingDetails(bookingId);
        } catch (err) {
          console.error('[Tracking] Error in fetchBookingDetails:', err);
        }
      } else {
        console.warn('[Tracking] No valid booking ID found for ambulance:', {
          ambulanceId: ambulance._id,
          extractedBookingId: bookingId,
          activeBooking: (ambulance.spec as any)?.activeBooking,
          activeBookingsCount: activeBookings.length,
        });
      }

      // In-trip Current Route: Traccar poll effect fetches GPS + recalculates route (no duplicate calls here)
    }
  };

  // Enable fitBounds only when ambulance selection *identity* changes (not on every object reference change from API refresh)
  useEffect(() => {
    const id = selectedAmbulance?._id ?? (selectedAmbulance as any)?.metadata?.name ?? null;
    if (id !== prevSelectedAmbulanceIdRef.current) {
      prevSelectedAmbulanceIdRef.current = id;
      shouldFitBoundsRef.current = true;
      currentRouteTabHasFittedRef.current = false; // allow one fit for new selection
    }
  }, [selectedAmbulance]);

  // When entering Current Route tab: allow one fitBounds. When leaving: reset so next time we enter we fit again.
  useEffect(() => {
    const prev = prevRouteViewModeRef.current;
    prevRouteViewModeRef.current = routeViewMode;
    if (prev !== ROUTE_VIEW_MODE.CURRENT && routeViewMode === ROUTE_VIEW_MODE.CURRENT) {
      shouldFitBoundsRef.current = true;
      currentRouteTabHasFittedRef.current = false;
      userHasPannedOrZoomedRef.current = false;
    }
    if (prev === ROUTE_VIEW_MODE.CURRENT && routeViewMode !== ROUTE_VIEW_MODE.CURRENT) {
      currentRouteTabHasFittedRef.current = false;
      userHasPannedOrZoomedRef.current = false;
    }
  }, [routeViewMode]);

  // Enable fitBounds when filters change
  useEffect(() => {
    shouldFitBoundsRef.current = true;
  }, [statusFilters]);

  // Recalculate current route when selection/tab/booking changes (assigned only — in-trip uses Traccar 30s poll)
  useEffect(() => {
    if (!selectedAmbulance || routeViewMode !== ROUTE_VIEW_MODE.CURRENT) return;
    if (isInTripOnCurrentRoute(selectedAmbulance._id, routeViewMode, activeBookings)) {
      return;
    }

    const hasActiveBooking = activeBookings.some((b) =>
      b.spec.ambulanceId === selectedAmbulance._id &&
      ['assigned', 'in_transit', 'patient_picked', 'patient_dropped'].includes(b.spec.status)
    ) || (selectedAmbulance.spec as any)?.activeBooking?._id;

    if (hasActiveBooking || selectedAmbulance.spec?.status === 'on_duty') {
      calculateCurrentRouteRef.current(selectedAmbulance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAmbulance?._id, activeBookings, routeViewMode]);

  // Assigned / early Current Route: global poll still updates liveLocations every 5s — keep route in sync (in-trip + Day Route idle use tenant dashboard route refresh instead)
  useEffect(() => {
    if (!selectedAmbulance || routeViewMode !== ROUTE_VIEW_MODE.CURRENT) return;

    const hasStartedTrip = activeBookings.some(
      (b) =>
        b.spec.ambulanceId === selectedAmbulance._id &&
        (b.spec.status === BOOKING_STATUS.IN_TRANSIT ||
          b.spec.status === BOOKING_STATUS.PATIENT_PICKED ||
          b.spec.status === BOOKING_STATUS.PATIENT_DROPPED)
    );
    if (hasStartedTrip) return;

    const hasActiveBooking = activeBookings.some((b) =>
      b.spec.ambulanceId === selectedAmbulance._id &&
      ['assigned', 'in_transit', 'patient_picked', 'patient_dropped'].includes(b.spec.status)
    ) || (selectedAmbulance.spec as any)?.activeBooking?._id;

    if (hasActiveBooking || selectedAmbulance.spec?.status === 'on_duty') {
      calculateCurrentRouteRef.current(selectedAmbulance);
    }
  }, [liveLocations, selectedAmbulance, activeBookings, routeViewMode]);

  // Current Route (in-trip): single Traccar poll every 30s + recalculate blue line (only source of Traccar calls)
  const inTripOnCurrentRoute = React.useMemo(
    () => isInTripOnCurrentRoute(selectedAmbulance?._id, routeViewMode, activeBookings),
    [selectedAmbulance?._id, routeViewMode, activeBookings]
  );

  useEffect(() => {
    if (!groupId || !selectedAmbulance || !inTripOnCurrentRoute) {
      return undefined;
    }

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      const freshLocs = await fetchLiveLocations(selectedAmbulance, { useTraccar: true });
      if (cancelled) return;
      await calculateCurrentRouteRef.current(selectedAmbulance, freshLocs, { silent: true });
    };

    void tick();
    const intervalId = window.setInterval(() => void tick(), CURRENT_ROUTE_TRACCAR_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [groupId, selectedAmbulance?._id, inTripOnCurrentRoute, fetchLiveLocations]);

  // Day Route (idle): refresh live location on tenant "Dashboard Route … Refresh" interval
  useEffect(() => {
    if (!groupId || !selectedAmbulance) {
      return undefined;
    }

    const dayRouteIdle =
      routeViewMode === ROUTE_VIEW_MODE.DAY &&
      !dayRouteIsPlaying &&
      dayRouteHistoryDate.trim() === formatLocalDateYMD(new Date());

    if (!dayRouteIdle) {
      return undefined;
    }

    const tick = () => {
      void fetchLiveLocations(selectedAmbulance, { useTraccar: true });
    };

    void tick();
    const intervalId = window.setInterval(tick, CURRENT_ROUTE_TRACCAR_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [
    groupId,
    selectedAmbulance,
    routeViewMode,
    dayRouteIsPlaying,
    dayRouteHistoryDate,
    fetchLiveLocations,
  ]);

  // Clear ETA only when leaving Current Route tab or deselecting ambulance (not when currentRoute is null during refresh – avoids flicker)
  useEffect(() => {
    if (routeViewMode !== ROUTE_VIEW_MODE.CURRENT || !selectedAmbulance) {
      setRouteEta(null);
    }
  }, [routeViewMode, selectedAmbulance]);

  // Fetch fuel / tracking summary when ambulance is selected (initial load with loading indicator)
  useEffect(() => {
    if (!selectedAmbulance?.spec?.gpsDevice?.deviceId) {
      setAmbulanceFuelData(null);
      return;
    }
    void refetchAmbulanceFuelSummary(selectedAmbulance, { withLoading: true });
  }, [selectedAmbulance, refetchAmbulanceFuelSummary]);

  // Fetch last 24hr bookings/trips when ambulance is selected
  useEffect(() => {
    const fetchAmbulanceTrips = async () => {
      if (!selectedAmbulance?._id) {
        setAmbulanceTrips([]);
        return;
      }
      try {
        setAmbulanceTripsLoading(true);
        const now = new Date();
        const past24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const startDate = past24h.toISOString().split('T')[0]; // YYYY-MM-DD
        const endDate = now.toISOString().split('T')[0];
        const response = await getBookings(1, 100, undefined, undefined, selectedAmbulance._id, undefined, undefined, startDate, endDate);
        if (response.data?.data) {
          // Sort by most recent first (createdAt descending)
          const sorted = [...response.data.data].sort((a, b) => (b.metadata.createdAt || 0) - (a.metadata.createdAt || 0));
          setAmbulanceTrips(sorted);
        } else {
          setAmbulanceTrips([]);
        }
      } catch (error) {
        console.error('Failed to fetch ambulance trips:', error);
        setAmbulanceTrips([]);
      } finally {
        setAmbulanceTripsLoading(false);
      }
    };
    fetchAmbulanceTrips();
  }, [selectedAmbulance, tripsRefreshTrigger]);

  const revertTripSubmittingRef = React.useRef(false);
  const handleRevertTripConfirm = React.useCallback(async () => {
    const b = revertTripModalBooking;
    if (!b || !selectedAmbulance?._id || revertTripSubmittingRef.current) return;
    revertTripSubmittingRef.current = true;
    setRevertTripSubmitting(true);
    try {
      await revertTripToPending(b._id, selectedAmbulance._id);
      setRevertTripModalBooking(null);
      setTripsRefreshTrigger((n) => n + 1);
      setTripDetailBooking((cur) => (cur?._id === b._id ? null : cur));
      // Refresh lists even if map info window is open (fetchAllTrackingDataRef skips when it is)
      const ambs = await fetchAllAmbulances();
      await fetchTrackingData();
      void fetchAllBookings();
      fetchLiveLocations(selectedAmbulance);
      setSelectedAmbulance((prev) => {
        if (!prev?._id) return prev;
        const next = ambs.find((a) => a._id === prev._id);
        return next ?? prev;
      });
      if (onTrackingDataRefreshed) {
        onTrackingDataRefreshed();
      }
      window.dispatchEvent(new Event('notificationRead'));
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Failed to reset trip';
      window.alert(typeof msg === 'string' ? msg : 'Failed to reset trip');
    } finally {
      revertTripSubmittingRef.current = false;
      setRevertTripSubmitting(false);
    }
  }, [revertTripModalBooking, selectedAmbulance, onTrackingDataRefreshed]);

  // Filter ambulances based on search term
  // Filters by fields that are visible in the UI: ID, Name, License Plate, Type, Status, and GPS Device ID/IMEI
  const filteredAmbulances = React.useMemo(() => {
    let filtered = onTripAmbulances;

    // Filter by status
    // If no filters selected, show nothing (handled by matchesStatusFilter returning false)
    if (statusFilters.length > 0) {
      filtered = filtered.filter((ambulance) => {
        // Find full ambulance data to get status
        const fullAmb = allAmbulances.find((a) => a._id === ambulance.ambulanceId || a.metadata.name === ambulance.id);
        const status = fullAmb?.spec?.status || ambulance.status || '';
        return matchesStatusFilter(status);
      });
    } else {
      // No filters selected, show nothing
      filtered = [];
    }

    // Filter by search term
    if (!debouncedSearchTerm.trim()) {
      return filtered;
    }

    const searchLower = debouncedSearchTerm.toLowerCase().trim();
    return filtered.filter((ambulance) => {
      // Primary identifiers (shown in sidebar table)
      const id = ambulance.id?.toLowerCase() || '';
      const type = ambulance.type?.toLowerCase() || '';

      // Status (shown in map info window)
      const status = ambulance.status?.toLowerCase() || '';

      // License plate (common vehicle identifier)
      const licensePlate = ambulance.licensePlate?.toLowerCase() || '';

      // Find full ambulance data for additional fields
      const fullAmb = allAmbulances.find((a) => a._id === ambulance.ambulanceId || a.metadata.name === ambulance.id);
      const name = fullAmb?.metadata?.name?.toLowerCase() || '';

      // GPS Device ID/IMEI (deviceId) - important for tracking
      const gpsDeviceId = fullAmb?.spec?.gpsDevice?.deviceId?.toLowerCase() || '';

      // Search across all visible/commonly used fields
      return (
        id.includes(searchLower) ||
        name.includes(searchLower) ||
        licensePlate.includes(searchLower) ||
        type.includes(searchLower) ||
        status.includes(searchLower) ||
        gpsDeviceId.includes(searchLower)
      );
    });
  }, [onTripAmbulances, allAmbulances, debouncedSearchTerm, statusFilters]);

  // Filter live locations based on search term and status filters
  const filteredLiveLocations = React.useMemo(() => {
    let filtered = liveLocations;

    // Filter by status (multiple selections)
    // If all 4 statuses are selected or none are selected, show all
    if (statusFilters.length > 0) {
      filtered = filtered.filter((loc: any) => {
        // Find the ambulance for this location
        const fullAmb = allAmbulances.find((a) =>
          a.spec?.gpsDevice?.deviceId === loc.deviceId ||
          a.spec?.gpsDevice?.gpsDeviceId === loc.deviceId
        );
        const status = fullAmb?.spec?.status || '';
        return matchesStatusFilter(status);
      });
    } else {
      // No filters selected, show nothing
      filtered = [];
    }

    // Filter by search term
    if (!debouncedSearchTerm.trim()) {
      return filtered;
    }

    const searchLower = debouncedSearchTerm.toLowerCase().trim();
    return filtered.filter((loc: any) => {
      const deviceId = loc.deviceId?.toLowerCase() || '';

      // Check if this deviceId matches the search term directly
      if (deviceId.includes(searchLower)) {
        return true;
      }

      // Check if any ambulance with this GPS device matches the search
      const matchingAmb = allAmbulances.find((a) => {
        const ambDeviceId = a.spec?.gpsDevice?.deviceId?.toLowerCase() || '';
        if (ambDeviceId === deviceId) {
          const id = a.metadata?.name?.toLowerCase() || '';
          const name = a.metadata?.name?.toLowerCase() || '';
          const licensePlate = a.spec?.licensePlate?.toLowerCase() || '';
          const type = a.spec?.type?.toLowerCase() || '';
          const status = a.spec?.status?.toLowerCase() || '';

          return (
            id.includes(searchLower) ||
            name.includes(searchLower) ||
            licensePlate.includes(searchLower) ||
            type.includes(searchLower) ||
            status.includes(searchLower) ||
            ambDeviceId.includes(searchLower)
          );
        }
        return false;
      });

      return !!matchingAmb;
    });
  }, [liveLocations, allAmbulances, debouncedSearchTerm, statusFilters]);

  // Calculate total vehicles that will be displayed on the map
  // This includes both filteredAmbulances and vehicles from filteredLiveLocations that aren't in filteredAmbulances
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const totalDisplayedVehicles = React.useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return filteredAmbulances.length;
    }

    // Count vehicles from filteredAmbulances
    let count = filteredAmbulances.length;

    // Count vehicles from filteredLiveLocations that aren't already in filteredAmbulances
    const filteredAmbulanceIds = new Set(
      filteredAmbulances.map(amb => {
        const fullAmb = allAmbulances.find((a) => a._id === amb.ambulanceId || a.metadata.name === amb.id);
        return fullAmb?.spec?.gpsDevice?.deviceId?.toLowerCase() || amb.ambulanceId?.toLowerCase() || amb.id?.toLowerCase();
      })
    );

    const additionalFromLiveLocations = filteredLiveLocations.filter((loc: any) => {
      const deviceId = loc.deviceId?.toLowerCase() || '';
      return !filteredAmbulanceIds.has(deviceId);
    }).length;

    return count + additionalFromLiveLocations;
  }, [filteredAmbulances, filteredLiveLocations, allAmbulances, debouncedSearchTerm]);

  // Filter all ambulances for sidebar list
  const filteredAmbulancesForList = React.useMemo(() => {
    let filtered = allAmbulances;

    // Filter by status
    // If no filters selected, show nothing (handled by matchesStatusFilter returning false)
    if (statusFilters.length > 0) {
      filtered = filtered.filter((ambulance) => matchesStatusFilter(ambulance.spec?.status || ''));
    } else {
      // No filters selected, show nothing
      filtered = [];
    }

    // Filter by running status (booking status)
    if (runningStatusFilters.length > 0 && runningStatusFilters.length < 5) {
      filtered = filtered.filter((ambulance) => {
        const booking = activeBookings.find((b) => b.spec.ambulanceId === ambulance._id);
        if (booking) {
          return runningStatusFilters.includes(booking.spec.status);
        }
        // If no active booking, don't show in running status filter
        return false;
      });
    }

    // Filter by search term
    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase().trim();
      filtered = filtered.filter((ambulance) => {
        const id = ambulance.metadata?.name?.toLowerCase() || '';
        const licensePlate = ambulance.spec?.licensePlate?.toLowerCase() || '';
        const type = ambulance.spec?.type?.toLowerCase() || '';
        const status = ambulance.spec?.status?.toLowerCase() || '';
        const gpsDeviceId = ambulance.spec?.gpsDevice?.deviceId?.toLowerCase() || '';

        return (
          id.includes(searchLower) ||
          licensePlate.includes(searchLower) ||
          type.includes(searchLower) ||
          status.includes(searchLower) ||
          gpsDeviceId.includes(searchLower)
        );
      });
    }

    // Sort ambulances
    // 1. First by status: on_duty, available, off_duty, maintenance
    // 2. Within on_duty: sort by last update time (newest first)
    // 3. Within available: assigned ambulances first (by assigned time), then normal available
    const statusOrder: Record<string, number> = {
      on_duty: 1,
      available: 2,
      off_duty: 3,
      maintenance: 4,
    };

    return filtered.sort((a, b) => {
      const statusA = a.spec?.status || '';
      const statusB = b.spec?.status || '';

      // First, sort by status
      const statusDiff = (statusOrder[statusA] || 999) - (statusOrder[statusB] || 999);
      if (statusDiff !== 0) {
        return statusDiff;
      }

      // If same status, apply status-specific sorting
      if (statusA === 'on_duty') {
        // Sort by last update time (newest first)
        const updatedAtA = a.metadata?.updatedAt || 0;
        const updatedAtB = b.metadata?.updatedAt || 0;
        return updatedAtB - updatedAtA; // Descending (newest first)
      }

      if (statusA === 'available') {
        // Check if ambulance has an active booking (assigned)
        const bookingA = activeBookings.find(b => b.spec.ambulanceId === a._id);
        const bookingB = activeBookings.find(b => b.spec.ambulanceId === b._id);
        const hasActiveBookingA = !!bookingA;
        const hasActiveBookingB = !!bookingB;

        // Assigned ambulances first
        if (hasActiveBookingA && !hasActiveBookingB) {
          return -1;
        }
        if (!hasActiveBookingA && hasActiveBookingB) {
          return 1;
        }

        // If both assigned, sort by assigned time (newest first)
        if (hasActiveBookingA && hasActiveBookingB) {
          const assignedTimeA = bookingA?.spec.timings?.assignedTime || bookingA?.metadata?.updatedAt || 0;
          const assignedTimeB = bookingB?.spec.timings?.assignedTime || bookingB?.metadata?.updatedAt || 0;
          return assignedTimeB - assignedTimeA; // Descending (newest first)
        }
      }

      // For other statuses, maintain original order
      return 0;
    });
  }, [allAmbulances, debouncedSearchTerm, statusFilters, runningStatusFilters, activeBookings]);

  // Calculate vehicle statistics for summary
  // Calculate zone allocation: areas with at least one available ambulance
  const zoneAllocation = React.useMemo(() => {
    if (areas.length === 0) {
      return { areasWithAvailable: 0, totalAreas: 0 };
    }

    // Create a map of ambulance IDs to their status for quick lookup
    const ambulanceStatusMap = new Map<string, string>();
    allAmbulances.forEach((amb) => {
      ambulanceStatusMap.set(amb._id, amb.spec?.status || '');
    });

    // Count areas that have at least one available ambulance
    let areasWithAvailable = 0;
    areas.forEach((area) => {
      if (area.spec?.ambulances && area.spec.ambulances.length > 0) {
        // Check if any ambulance in this area is available
        const hasAvailable = area.spec.ambulances.some((areaAmb) => {
          const ambStatus = ambulanceStatusMap.get(areaAmb.ambulanceId);
          return ambStatus === 'available';
        });
        if (hasAvailable) {
          areasWithAvailable++;
        }
      }
    });

    return {
      areasWithAvailable,
      totalAreas: areas.length,
    };
  }, [areas, allAmbulances]);

  const vehicleStats = React.useMemo(() => {
    const total = allAmbulances.length;
    const available = allAmbulances.filter((a) => a.spec?.status === 'available').length;
    const onDuty = allAmbulances.filter((a) => a.spec?.status === 'on_duty').length;
    const offDuty = allAmbulances.filter((a) => a.spec?.status === 'off_duty').length;
    const maintenance = allAmbulances.filter((a) => a.spec?.status === 'maintenance').length;

    // Vehicles in trip (have active bookings)
    const inTrip = allAmbulances.filter((a) => {
      const booking = activeBookings.find((b) => b.spec.ambulanceId === a._id);
      return booking && ['assigned', 'in_transit', 'patient_picked', 'patient_dropped'].includes(booking.spec.status);
    }).length;

    return {
      total,
      available,
      onDuty,
      offDuty,
      maintenance,
      inTrip,
    };
  }, [allAmbulances, activeBookings]);

  return (
    <div
      className="live-tracking-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        height: 'calc(100vh - 90px)',
        minHeight: 0,
      }}
    >
      {/* Vehicle Summary Cards */}
      <VehicleSummaryCards
        vehicleStats={vehicleStats}
        zoneAllocation={zoneAllocation}
        onFilterChange={(filterCategory) => {
          setStatusFilters([filterCategory]);
        }}
        selectedFilter={statusFilters.length > 0 ? statusFilters[0] : 'total'}
      />

      {/* Booking Summary Cards */}
      <BookingSummaryCards bookings={allBookings} onBookingFilterChange={onBookingFilterChange} selectedBookingCard={selectedBookingCard} />

      {/* Main Content Area */}
      <div
        className="tracking-content"
        style={{ display: 'flex', gap: '8px', height: '100%', minHeight: 0, flex: 1, paddingTop: '8px' }}
      >
        {/* Left Sidebar - Ambulance List */}
        <div
          className="tracking-ambulance-sidebar"
          style={{
            flexShrink: 0,
            flexBasis: '340px',
            width: 'min(360px, 32%)',
            minWidth: '280px',
            maxWidth: '360px',
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            borderRadius: '8px',
            padding: '12px',
            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {/* Header Row: Title, Search Icon, Filter Icon */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '8px' }}>
            <h3
              style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: isDark ? '#f1f5f9' : '#0f172a',
                flex: 1,
              }}
            >
              Ambulances
            </h3>
            {/* Search Icon Button */}
            <button
              onClick={() => setShowSearch(!showSearch)}
              style={{
                padding: '6px',
                border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                borderRadius: '4px',
                backgroundColor: isDark ? '#0f172a' : '#ffffff',
                color: isDark ? '#f1f5f9' : '#0f172a',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                width: '32px',
                height: '32px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDark ? '#1e293b' : '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isDark ? '#0f172a' : '#ffffff';
              }}
              title={showSearch ? 'Hide search' : 'Show search'}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </button>
            {/* Filter Icon Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                padding: '6px',
                border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                borderRadius: '4px',
                backgroundColor: isDark ? '#0f172a' : '#ffffff',
                color: isDark ? '#f1f5f9' : '#0f172a',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                width: '32px',
                height: '32px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDark ? '#1e293b' : '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isDark ? '#0f172a' : '#ffffff';
              }}
              title={showFilters ? 'Hide filters' : 'Show filters'}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
              </svg>
            </button>
          </div>

          {/* Search Input - Shown when showSearch is true */}
          {showSearch && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  type="text"
                  placeholder="Search by ID, name, license plate, type, status, or GPS device ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 40px 10px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                    backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
                    color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = isDark ? '#334155' : '#e2e8f0';
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                    }}
                    title="Clear search"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                )}
                {!searchTerm && (
                  <div
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: isDark ? COLORS.THEME.DARK.TEXT_TERTIARY : COLORS.THEME.LIGHT.TEXT_TERTIARY,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                  </div>
                )}
              </div>
              {debouncedSearchTerm && debouncedSearchTerm !== searchTerm && (
                <div style={{ marginTop: '4px', fontSize: '14px', color: isDark ? '#94a3b8' : '#64748b' }}>
                  Searching...
                </div>
              )}
            </div>
          )}

          {/* Count Display */}
          {/* {!initialLoading && (
            <div
              style={{
                fontSize: '14px',
                color: isDark ? '#94a3b8' : '#64748b',
                marginBottom: '12px',
                padding: '8px',
                backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                borderRadius: '6px',
                textAlign: 'center',
                flexShrink: 0,
              }}
            >
              {filteredAmbulancesForList.length === 0 && debouncedSearchTerm.trim() ? (
                <div style={{ color: isDark ? '#f87171' : '#dc2626' }}>
                  No ambulances found matching "{debouncedSearchTerm}"
                </div>
              ) : (
                <>
                  {filteredAmbulancesForList.length} ambulance{filteredAmbulancesForList.length !== 1 ? 's' : ''}
                   {debouncedSearchTerm.trim() && (
                    <span style={{ display: 'block', fontSize: '13px', marginTop: '4px' }}>
                      Search: "{debouncedSearchTerm}"
                    </span>
                  )} 
                    {statusFilters.length > 0 && (
                    <span style={{ display: 'block', fontSize: '13px', marginTop: '4px' }}>
                      Status: {statusFilters.map((s) => {
                        const labels: Record<string, string> = {
                          'total': 'Total',
                          'standby': 'Standby',
                          'on_trip': 'On Trip',
                          'inactive': 'Inactive',
                        };
                        return labels[s] || s.replace('_', ' ');
                      }).join(', ')}
                    </span>
                  )} 
                    {runningStatusFilters.length > 0 && runningStatusFilters.length < 5 && (
                    <span style={{ display: 'block', fontSize: '13px', marginTop: '4px' }}>
                      Running: {runningStatusFilters.map((s) => {
                        const map: Record<string, string> = {
                          [BOOKING_STATUS.ASSIGNED]: BOOKING_STATUS_DISPLAY[BOOKING_STATUS.ASSIGNED],
                          [BOOKING_STATUS.IN_TRANSIT]: BOOKING_STATUS_DISPLAY[BOOKING_STATUS.IN_TRANSIT],
                          [BOOKING_STATUS.PATIENT_PICKED]: BOOKING_STATUS_DISPLAY[BOOKING_STATUS.PATIENT_PICKED],
                          [BOOKING_STATUS.PATIENT_DROPPED]: BOOKING_STATUS_DISPLAY[BOOKING_STATUS.PATIENT_DROPPED],
                          [BOOKING_STATUS.PENDING]: BOOKING_STATUS_DISPLAY[BOOKING_STATUS.PENDING],
                        };
                        return map[s] || s;
                      }).join(', ')}
                    </span>
                  )}
                </>
              )}
            </div>
          )} */}

          {/* Status & Running Status Filters */}
          {showFilters && (
            <div style={{ display: 'flex', gap: '12px', margin: '8px 0 16px 0' }}>
              {/* Status Filter */}
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: isDark ? '#94a3b8' : '#64748b',
                    marginBottom: '8px',
                  }}
                >
                  Filter by Status
                </label>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    padding: '8px',
                    borderRadius: '6px',
                    border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                    backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                  }}
                >
                  {[
                    { value: 'total', label: 'Total', color: '#3b82f6' },
                    { value: 'standby', label: 'Standby', color: '#10b981' },
                    { value: 'on_trip', label: 'On Trip', color: '#ef4444' },
                    { value: 'inactive', label: 'Inactive', color: '#64748b' },
                  ].map((status) => {
                    const isChecked = statusFilters.includes(status.value);
                    return (
                      <label
                        key={status.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                          userSelect: 'none',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              if (status.value === 'total') {
                                // If total is selected, deselect all others
                                setStatusFilters(['total']);
                              } else {
                                // If any other is selected, deselect total and select this one
                                setStatusFilters([status.value]);
                              }
                            } else {
                              // Unchecking - ensure at least one filter is always selected
                              if (status.value === 'total') {
                                // If unchecking total, select standby as default
                                setStatusFilters(['standby']);
                              } else {
                                // If unchecking standby/on_trip/inactive, select total
                                setStatusFilters(['total']);
                              }
                            }
                          }}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: status.color,
                          }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: status.color,
                            }}
                          />
                          <span>{status.label}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Running Status Filter */}
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                    marginBottom: '8px',
                  }}
                >
                  Filter by Running Status
                </label>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    padding: '8px',
                    borderRadius: '6px',
                    border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                    backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                  }}
                >
                  {[
                    { value: BOOKING_STATUS.ASSIGNED, label: BOOKING_STATUS_LABELS[BOOKING_STATUS.ASSIGNED], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.ASSIGNED] },
                    { value: BOOKING_STATUS.IN_TRANSIT, label: BOOKING_STATUS_LABELS[BOOKING_STATUS.IN_TRANSIT], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.IN_TRANSIT] },
                    { value: BOOKING_STATUS.PATIENT_PICKED, label: BOOKING_STATUS_LABELS[BOOKING_STATUS.PATIENT_PICKED], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.PATIENT_PICKED] },
                    { value: BOOKING_STATUS.PATIENT_DROPPED, label: BOOKING_STATUS_LABELS[BOOKING_STATUS.PATIENT_DROPPED], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.PATIENT_DROPPED] },
                    { value: BOOKING_STATUS.PENDING, label: BOOKING_STATUS_LABELS[BOOKING_STATUS.PENDING], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.PENDING] },
                  ].map((status) => {
                    const isChecked = runningStatusFilters.includes(status.value);
                    return (
                      <label
                        key={status.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                          userSelect: 'none',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRunningStatusFilters([...runningStatusFilters, status.value]);
                            } else {
                              setRunningStatusFilters(runningStatusFilters.filter((s) => s !== status.value));
                            }
                          }}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: status.color,
                          }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: status.color,
                            }}
                          />
                          <span>{status.label}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div
            style={{
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
            }}
          >
            {initialLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: isDark ? '#94a3b8' : '#64748b', flex: 1 }}>
                Loading...
              </div>
            ) : filteredAmbulancesForList.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: isDark ? '#94a3b8' : '#64748b', flex: 1 }}>
                {statusFilters.length === 0 || runningStatusFilters.length < 5 || debouncedSearchTerm.trim()
                  ? 'No ambulances match the current filters'
                  : 'No ambulances found'}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                }}
              >
                {filteredAmbulancesForList.map((ambulance) => {
                  const isSelected = selectedAmbulance?._id === ambulance._id;
                  const runningStatus = getAmbulanceRunningStatus(ambulance._id);
                  const progressStates = getAmbulanceProgressStates(ambulance._id);

                  const statusColor =
                    ambulance.spec?.status === 'on_duty'
                      ? '#ef4444'
                      : ambulance.spec?.status === 'available'
                        ? '#10b981'
                        : ambulance.spec?.status === 'off_duty'
                          ? '#f59e0b'
                          : ambulance.spec?.status === 'maintenance'
                            ? '#64748b'
                            : isDark
                              ? '#64748b'
                              : '#64748b';

                  const handleDeselect = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    setSelectedAmbulance(null);
                    setLocationHistoryRoute([]);
                    setRouteBookings([]);
                    setCurrentRoute(null);
                    setRouteViewMode(ROUTE_VIEW_MODE.DAY);
                    setAmbulanceFuelData(null);
                    setAmbulanceTrips([]);
                    setTripDetailBooking(null);
                  };

                  return (
                    <div
                      key={ambulance._id}
                      onClick={() => handleAmbulanceClick(ambulance)}
                      onDoubleClick={handleDeselect}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: `1px solid ${isSelected ? '#3b82f6' : isDark ? '#334155' : '#e2e8f0'}`,
                        backgroundColor: isSelected
                          ? isDark
                            ? '#1e3a8a'
                            : '#dbeafe'
                          : isDark
                            ? '#0f172a'
                            : '#f8fafc',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        position: 'relative',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = isDark ? '#0f172a' : '#f1f5f9';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = isDark ? '#0f172a' : '#f8fafc';
                        }
                      }}
                    >
                      {/* Cross button to deselect - only show when selected */}
                      {isSelected && (
                        <button
                          onClick={handleDeselect}
                          style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            border: 'none',
                            backgroundColor: isDark ? '#1e293b' : '#ffffff',
                            color: isDark ? '#f1f5f9' : '#0f172a',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            lineHeight: 1,
                            zIndex: 10,
                            boxShadow: isDark ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#f1f5f9';
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = isDark ? '#1e293b' : '#ffffff';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                          title="Deselect ambulance"
                        >
                          X
                        </button>
                      )}
                      {/* Top row: Name, plate, type, status in a single row */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                          marginBottom: '6px',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                            minWidth: 0,
                          }}
                        >
                          <div
                            style={{
                              fontWeight: '600',
                              fontSize: '16px',
                              color: isSelected ? '#3b82f6' : isDark ? '#f1f5f9' : '#0f172a',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {ambulance.metadata?.name || 'Unknown'}
                          </div>
                          <div
                            style={{
                              fontSize: '14px',
                              color: isDark ? '#94a3b8' : '#64748b',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {ambulance.spec?.licensePlate && `Plate: ${ambulance.spec.licensePlate}`}
                          </div>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flexShrink: 0,
                            marginTop: '15px',
                          }}
                        >
                          {ambulance.spec?.status && (
                            <span
                              style={{
                                fontSize: '14px',
                                padding: '3px 6px',
                                borderRadius: '4px',
                                backgroundColor: `${statusColor}0D`,
                                color: statusColor,
                                border: `0.5px solid ${statusColor}`,
                                fontWeight: 500,
                                display: 'inline-block',
                              }}
                            >
                              {formatAmbulanceStatus(ambulance.spec.status)}
                            </span>
                          )}
                        </div>
                      </div>

                      {runningStatus && (
                        <div
                          style={{
                            fontSize: '16px',
                            padding: '3px 6px',
                            borderRadius: '4px',
                            backgroundColor: `${runningStatus.color}0D`,
                            color: runningStatus.color,
                            border: `.5px solid ${runningStatus.color}`,
                            marginBottom: '6px',
                            fontWeight: '500',
                            display: 'inline-block',
                          }}
                        >
                          {runningStatus.status}
                        </div>
                      )}
                      {progressStates && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: '8px',
                            padding: '8px',
                            backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                            borderRadius: '4px',
                          }}
                        >
                          {progressStates.states.map((state, index) => (
                            <React.Fragment key={state.status}>
                              <div
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '50%',
                                  backgroundColor: state.active ? state.color : isDark ? '#334155' : '#e2e8f0',
                                  border: `2px solid ${state.active ? state.color : isDark ? '#475569' : '#cbd5e1'}`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  color: state.active ? '#ffffff' : isDark ? '#64748b' : '#94a3b8',
                                  transition: 'all 0.3s',
                                }}
                                title={state.label}
                              >
                                {index + 1}
                              </div>
                              {index < progressStates.states.length - 1 && (
                                <div
                                  style={{
                                    flex: 1,
                                    height: '2px',
                                    backgroundColor: state.active && progressStates.states[index + 1].active
                                      ? state.color
                                      : isDark
                                        ? '#334155'
                                        : '#e2e8f0',
                                    transition: 'all 0.3s',
                                  }}
                                />
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}

                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Ambulance Detail View - Shows between list and map when ambulance is selected */}
        {selectedAmbulance && (
          <div
            className="tracking-ambulance-details"
            style={{
              width: '350px',
              flexShrink: 0,
              flexBasis: '360px',
              backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
              border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
              borderRadius: '8px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              maxHeight: '100%',
              overflowY: 'auto',
            }}
          >
            {/* Type */}
            <div>
              <div style={{ fontSize: '14px', color: isDark ? '#94a3b8' : '#64748b', marginBottom: '4px' }}>
                Type:  <span style={{ fontSize: '16px', fontWeight: '600', color: isDark ? '#f1f5f9' : '#0f172a' }}>
                  {formatAmbulanceType(selectedAmbulance.spec?.type, ambulanceTypes) || 'N/A'}
                </span>
              </div>
            </div>

            {/* Vehicle Stats */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Distance & Engine Hours (in one row) */}
              {!ambulanceFuelLoading &&
                (ambulanceFuelData?.summary?.distance !== undefined ||
                  ambulanceFuelData?.summary?.engineHours) && (
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '32px', alignItems: 'center' }}>
                    {/* Distance */}
                    {ambulanceFuelData && (
                      <div>
                        <div style={{ fontSize: '14px', color: isDark ? '#94a3b8' : '#64748b', marginBottom: '4px' }}>
                          Distance
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: isDark ? '#f1f5f9' : '#0f172a' }}>
                          {(() => {
                            const { totalKm } = getDistanceFromLocationHistory(ambulanceFuelData.locationHistory);
                            const safeKm = totalKm < 0 ? 0 : totalKm;
                            return `${safeKm.toFixed(2)} km`;
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Engine Hours */}
                    {ambulanceFuelData?.summary?.engineHours && (
                      <div>
                        <div style={{ fontSize: '14px', color: isDark ? '#94a3b8' : '#64748b', marginBottom: '4px' }}>
                          Engine Hours
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: isDark ? '#f1f5f9' : '#0f172a' }}>
                          {(() => {
                            const actualSeconds = ambulanceFuelData.summary.engineHours;
                            const displaySeconds = actualSeconds < 0 ? 0 : actualSeconds;
                            const hours = Math.floor(displaySeconds / 3600);
                            const minutes = Math.floor((displaySeconds % 3600) / 60);
                            const displayText = `${hours}h ${minutes}m`;

                            if (actualSeconds < 0) {
                              const actualHours = Math.floor(actualSeconds / 3600);
                              const actualMinutes = Math.floor((actualSeconds % 3600) / 60);
                            }

                            return displayText;
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                )}

              {/* Fuel Level */}
              {ambulanceFuelLoading ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px 0',
                    gap: '8px',
                  }}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '999px',
                      border: `3px solid ${isDark ? '#1f2937' : '#e5e7eb'}`,
                      borderTopColor: isDark ? '#38bdf8' : '#0ea5e9',
                      borderLeftColor: 'transparent',
                      animation: 'fuel-spin 0.8s linear infinite',
                    }}
                  />
                  {/* <div style={{ fontSize: '14px', color: isDark ? '#94a3b8' : '#64748b' }}>
                    Loading fuel and distance data...
                  </div> */}
                </div>
              ) : (ambulanceFuelData?.fuelReadings && ambulanceFuelData.fuelReadings.length > 0) ||
                (ambulanceFuelData?.locationHistory && ambulanceFuelData.locationHistory.length > 0) ? (
                <div style={{ position: 'relative' }}>
                  {/* Fullscreen toggle button */}
                  <button
                    type="button"
                    onClick={() => setIsFuelGraphFullscreen(true)}
                    aria-label="Open fuel and distance graph in fullscreen"
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      padding: '4px 6px',
                      fontSize: '12px',
                      borderRadius: '4px',
                      // border: 'none',
                      cursor: 'pointer',
                      backgroundColor: isDark ? '#1f2937' : '#e5e7eb',
                      color: isDark ? '#e5e7eb' : '#111827',
                      border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000,
                    }}
                  >
                    <span style={{ fontSize: '23px', lineHeight: 1 }}>⤢</span>
                  </button>
                  <div style={{ fontSize: '12px', color: isDark ? '#94a3b8' : '#64748b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '60px' }}>
                    {ambulanceFuelData?.fuelReadings && ambulanceFuelData.fuelReadings.length > 0 && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ width: 12, height: 2, backgroundColor: '#3b82f6' }} />
                        Fuel (L)
                      </span>
                    )}
                    {ambulanceFuelData?.locationHistory && ambulanceFuelData.locationHistory.length > 0 && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ width: 12, height: 2, backgroundColor: '#10b981' }} />
                        Distance (km)
                      </span>
                    )}
                  </div>
                  {(() => {
                    const sortedReadings = (ambulanceFuelData?.fuelReadings || []).length > 0
                      ? [...ambulanceFuelData.fuelReadings].sort((a: any, b: any) =>
                        new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
                      )
                      : [];
                    const locationHistory = (ambulanceFuelData?.locationHistory || []).slice().sort((a: any, b: any) =>
                      new Date(a.date).getTime() - new Date(b.date).getTime()
                    );
                    const hasFuelData = sortedReadings.length > 0;
                    const hasDistanceData = locationHistory.length > 0;
                    const latestReading = sortedReadings[sortedReadings.length - 1];
                    const rawMinFuel = hasFuelData ? Math.min(...sortedReadings.map((r: any) => r.fuelLevel)) : 0;
                    const rawMaxFuel = hasFuelData ? Math.max(...sortedReadings.map((r: any) => r.fuelLevel)) : 1;
                    const fuelRangePadding = rawMaxFuel === rawMinFuel ? 5 : (rawMaxFuel - rawMinFuel) * 0.1;
                    const minFuel = Math.max(0, rawMinFuel - fuelRangePadding);
                    const maxFuel = rawMaxFuel + fuelRangePadding;
                    const fuelRange = maxFuel - minFuel || 1;

                    // Build distance series from GPS coordinates (Haversine) – shared helper with the distance label
                    const { locCoords, cumulativeKm } = getDistanceFromLocationHistory(locationHistory);
                    const distanceSeries = locCoords.map((c: any, i: number) => ({
                      date: c.date,
                      distanceKm: cumulativeKm[i] ?? 0,
                    }));
                    const hasDistance = distanceSeries.length > 0;
                    const maxDistanceVal = hasDistance
                      ? Math.max(...distanceSeries.map((p: any) => p.distanceKm))
                      : 0;
                    const distanceRange = maxDistanceVal > 0 ? maxDistanceVal * 1.1 : 1;
                    const allTimes = [
                      ...sortedReadings.map((r: any) => new Date(r.recordedAt).getTime()),
                      ...locationHistory.map((e: any) => new Date(e.date).getTime()),
                    ];
                    const firstTime = Math.min(...allTimes);
                    const lastTime = Math.max(...allTimes);
                    const timeRange = lastTime - firstTime || 1;

                    const renderFuelDistanceChart = (options?: { height?: number | string; mode?: 'compact' | 'fullscreen' }) => {
                      const containerHeight = options?.height ?? 220;
                      const isFullscreenChart = options?.mode === 'fullscreen';
                      // Use a denser, higher-resolution canvas in fullscreen so
                      // lines and labels look sleeker and more detailed.
                      const svgWidth = isFullscreenChart ? 1440 : 360;
                      const svgHeight = isFullscreenChart ? 360 : 150;
                      const paddingRight = hasDistance
                        ? (isFullscreenChart ? 88 : 44) // extra room for detailed right-axis labels in fullscreen
                        : (isFullscreenChart ? 40 : 12);
                      const padding = isFullscreenChart
                        ? { top: 32, right: paddingRight, bottom: 40, left: 48 }
                        : { top: 10, right: paddingRight, bottom: 22, left: 18 };
                      const chartWidth = svgWidth - padding.left - padding.right;
                      const chartHeight = svgHeight - padding.top - padding.bottom;
                      const points = sortedReadings.map((reading: any) => {
                        const readingTime = new Date(reading.recordedAt).getTime();
                        const pointX = padding.left + ((readingTime - firstTime) / timeRange) * chartWidth;
                        const pointY =
                          padding.top +
                          chartHeight -
                          ((reading.fuelLevel - minFuel) / fuelRange) * chartHeight;
                        return { x: pointX, y: pointY, reading };
                      });
                      const distancePoints = hasDistance
                        ? distanceSeries.map((entry: any) => {
                          const t = new Date(entry.date).getTime();
                          const x = padding.left + ((t - firstTime) / timeRange) * chartWidth;
                          const y =
                            padding.top +
                            chartHeight -
                            (entry.distanceKm / distanceRange) * chartHeight;
                          return { x, y, distanceKm: entry.distanceKm, date: entry.date };
                        })
                        : [];
                      const distancePathData = distancePoints.length <= 1
                        ? (distancePoints.length === 1
                          ? `M ${padding.left} ${distancePoints[0].y} L ${svgWidth - padding.right} ${distancePoints[0].y}`
                          : '')
                        : distancePoints
                          .map((p: any, i: number) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
                          .join(' ');
                      const heightStyle =
                        typeof containerHeight === 'number' ? `${containerHeight}px` : containerHeight;
                      return (
                        <div style={{ width: '100%', height: heightStyle, position: 'relative', marginBottom: "-40px", marginTop: "-30px", zIndex: 1 }}>
                          <svg
                            width="100%"
                            height="100%"
                            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                            preserveAspectRatio="xMidYMid meet"
                            onMouseMove={(e) => {
                              const svg = e.currentTarget;
                              const rect = svg.getBoundingClientRect();
                              const svgPoint = svg.createSVGPoint();
                              svgPoint.x = e.clientX - rect.left;
                              svgPoint.y = e.clientY - rect.top;

                              // Scale to viewBox coordinates
                              const viewBox = svg.viewBox.baseVal;
                              const scaleX = viewBox.width / rect.width;
                              const scaleY = viewBox.height / rect.height;
                              const x = svgPoint.x * scaleX;
                              const y = svgPoint.y * scaleY;

                              if (points.length > 0) {
                                // Find closest fuel point
                                let closestPoint = points[0];
                                let minFuelDistance = Number.POSITIVE_INFINITY;
                                points.forEach((point) => {
                                  const dx = x - point.x;
                                  const dy = y - point.y;
                                  const distanceSq = dx * dx + dy * dy;
                                  if (distanceSq < minFuelDistance) {
                                    minFuelDistance = distanceSq;
                                    closestPoint = point;
                                  }
                                });
                                // Find closest distance point in time (for tooltip)
                                let closestDistancePoint: any | null = null;
                                if (distancePoints.length > 0) {
                                  const hoverTime = new Date(closestPoint.reading.recordedAt).getTime();
                                  closestDistancePoint = distancePoints.reduce((prev: any, curr: any) =>
                                    Math.abs(new Date(curr.date).getTime() - hoverTime) <
                                      Math.abs(new Date(prev.date).getTime() - hoverTime)
                                      ? curr
                                      : prev
                                  );
                                }
                                setFuelGraphHoveredPoint(closestPoint);
                                setFuelGraphHoveredDistancePoint(closestDistancePoint);
                                setFuelGraphTooltipPosition({ x: svgPoint.x, y: svgPoint.y });
                              } else if (distancePoints.length > 0) {
                                // No fuel points: hover by closest distance point by x
                                const closestDistancePoint = distancePoints.reduce((prev: any, curr: any) =>
                                  Math.abs(curr.x - x) < Math.abs(prev.x - x) ? curr : prev
                                );
                                setFuelGraphHoveredPoint(null);
                                setFuelGraphHoveredDistancePoint(closestDistancePoint);
                                setFuelGraphTooltipPosition({ x: svgPoint.x, y: svgPoint.y });
                              } else {
                                setFuelGraphHoveredPoint(null);
                                setFuelGraphHoveredDistancePoint(null);
                                setFuelGraphTooltipPosition(null);
                              }
                            }}
                            onMouseLeave={() => {
                              setFuelGraphHoveredPoint(null);
                              setFuelGraphHoveredDistancePoint(null);
                              setFuelGraphTooltipPosition(null);
                            }}
                          >
                            {/* Background */}
                            <rect width="100%" height="100%" fill={isDark ? '#0f172a' : '#f8fafc'} />

                            {/* Fuel level area gradient */}
                            <defs>
                              <linearGradient id={`fuelGradient-${selectedAmbulance?._id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.1" />
                              </linearGradient>
                            </defs>

                            {/* Distance line (show when has distance, with or without fuel) */}
                            {hasDistance && distancePathData && (
                              <path
                                d={distancePathData}
                                fill="none"
                                stroke="#10b981"
                                strokeWidth="1.5"
                              />
                            )}
                            {/* Fuel line and area (only when we have fuel readings) */}
                            {sortedReadings.length > 0 && (() => {
                              // If only one point, create a horizontal line
                              let pathData: string;
                              let areaPath: string;

                              if (sortedReadings.length === 1) {
                                const point = points[0];
                                pathData = `M ${padding.left} ${point.y} L ${svgWidth - padding.right} ${point.y}`;
                                areaPath = `M ${padding.left} ${point.y} L ${svgWidth - padding.right} ${point.y} L ${svgWidth - padding.right} ${padding.top + chartHeight} L ${padding.left} ${padding.top + chartHeight} Z`;
                              } else {
                                pathData = points.map((point, index) =>
                                  `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
                                ).join(' ');
                                areaPath = `${pathData} L ${points[points.length - 1].x} ${padding.top + chartHeight} L ${points[0].x} ${padding.top + chartHeight} Z`;
                              }

                              return (
                                <>
                                  <path
                                    d={areaPath}
                                    fill={`url(#fuelGradient-${selectedAmbulance?._id})`}
                                  />
                                  <path
                                    d={pathData}
                                    fill="none"
                                    stroke="#3b82f6"
                                    strokeWidth="2"
                                  />
                                  {/* Data points: invisible hit areas; only show dot when hovered */}
                                  {points.map((point, index) => (
                                    <g key={index}>
                                      <circle
                                        cx={point.x}
                                        cy={point.y}
                                        r="8"
                                        fill="transparent"
                                        style={{ cursor: 'pointer' }}
                                      />
                                      {fuelGraphHoveredPoint?.reading === point.reading && (
                                        <circle
                                          cx={point.x}
                                          cy={point.y}
                                          r="4"
                                          fill="#2563eb"
                                          style={{ transition: 'r 0.2s', cursor: 'pointer' }}
                                        />
                                      )}
                                      {/* Highlight line when hovered */}
                                      {fuelGraphHoveredPoint?.reading === point.reading && (
                                        <>
                                          <line
                                            x1={point.x}
                                            y1={padding.top}
                                            x2={point.x}
                                            y2={padding.top + chartHeight}
                                            stroke="#3b82f6"
                                            strokeWidth="1"
                                            strokeDasharray="4,4"
                                            opacity="0.5"
                                          />
                                          <line
                                            x1={padding.left}
                                            y1={point.y}
                                            x2={svgWidth - padding.right}
                                            y2={point.y}
                                            stroke="#3b82f6"
                                            strokeWidth="1"
                                            strokeDasharray="4,4"
                                            opacity="0.5"
                                          />
                                        </>
                                      )}
                                    </g>
                                  ))}
                                </>
                              );
                            })()}

                            {/* Distance hover dot and crosshair (with or without fuel) */}
                            {hasDistance &&
                              fuelGraphHoveredDistancePoint &&
                              distancePoints.map((p: any, index: number) => {
                                const isHovered =
                                  p.date === fuelGraphHoveredDistancePoint.date &&
                                  Math.abs(p.distanceKm - fuelGraphHoveredDistancePoint.distanceKm) < 1e-6;
                                return isHovered ? (
                                  <circle
                                    key={`distance-hover-${index}`}
                                    cx={p.x}
                                    cy={p.y}
                                    r="4"
                                    fill="#10b981"
                                    style={{ transition: 'r 0.2s' }}
                                  />
                                ) : null;
                              })}
                            {hasDistance && fuelGraphHoveredDistancePoint && (() => {
                              const hoveredP = distancePoints.find(
                                (p: any) =>
                                  p.date === fuelGraphHoveredDistancePoint.date &&
                                  Math.abs(p.distanceKm - fuelGraphHoveredDistancePoint.distanceKm) < 1e-6
                              );
                              if (!hoveredP) return null;
                              return (
                                <>
                                  <line
                                    x1={hoveredP.x}
                                    y1={padding.top}
                                    x2={hoveredP.x}
                                    y2={padding.top + chartHeight}
                                    stroke="#10b981"
                                    strokeWidth="1"
                                    strokeDasharray="4,4"
                                    opacity="0.5"
                                  />
                                  <line
                                    x1={padding.left}
                                    y1={hoveredP.y}
                                    x2={svgWidth - padding.right}
                                    y2={hoveredP.y}
                                    stroke="#10b981"
                                    strokeWidth="1"
                                    strokeDasharray="4,4"
                                    opacity="0.5"
                                  />
                                </>
                              );
                            })()}

                            {/* Y-axis line */}
                            <line
                              x1={padding.left}
                              y1={padding.top}
                              x2={padding.left}
                              y2={padding.top + chartHeight}
                              stroke={isDark ? '#64748b' : '#94a3b8'}
                              strokeWidth="1"
                            />

                            {/* Y-axis labels: fuel ticks (only when we have fuel data) */}
                            {hasFuelData && (() => {
                              const labelX = padding.left - 4; // slightly left of the Y-axis line
                              const topY = padding.top + 10;
                              const bottomY = padding.top + chartHeight - 10;

                              // In compact mode keep only min/max; in fullscreen show more partitions.
                              if (!isFullscreenChart) {
                                return (
                                  <>
                                    <text
                                      x={labelX}
                                      y={topY}
                                      fill={
                                        isDark
                                          ? COLORS.THEME.DARK.TEXT_SECONDARY
                                          : COLORS.THEME.LIGHT.TEXT_SECONDARY
                                      }
                                      fontSize="10"
                                      textAnchor="middle"
                                      transform={`rotate(-90 ${labelX} ${topY})`}
                                    >
                                      {maxFuel.toFixed(1)}L
                                    </text>
                                    <text
                                      x={labelX}
                                      y={bottomY}
                                      fill={
                                        isDark
                                          ? COLORS.THEME.DARK.TEXT_SECONDARY
                                          : COLORS.THEME.LIGHT.TEXT_SECONDARY
                                      }
                                      fontSize="10"
                                      textAnchor="middle"
                                      transform={`rotate(-90 ${labelX} ${bottomY})`}
                                    >
                                      {minFuel.toFixed(1)}L
                                    </text>
                                  </>
                                );
                              }

                              const tickCount = 5; // max, 3 mids, min
                              const ticks: number[] = [];
                              for (let i = 0; i < tickCount; i += 1) {
                                const t = minFuel + ((tickCount - 1 - i) / (tickCount - 1)) * fuelRange;
                                ticks.push(t);
                              }

                              return (
                                <>
                                  {ticks.map((value, index) => {
                                    const y =
                                      padding.top +
                                      chartHeight -
                                      ((value - minFuel) / fuelRange) * chartHeight;
                                    return (
                                      <text
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        x={labelX}
                                        y={y}
                                        fill={
                                          isDark
                                            ? COLORS.THEME.DARK.TEXT_SECONDARY
                                            : COLORS.THEME.LIGHT.TEXT_SECONDARY
                                        }
                                        fontSize="9"
                                        textAnchor="middle"
                                        transform={`rotate(-90 ${labelX} ${y})`}
                                      >
                                        {value.toFixed(1)}L
                                      </text>
                                    );
                                  })}
                                </>
                              );
                            })()}

                            {/* Right Y-axis: distance ticks (more partitions in fullscreen), rotated 90° — same idea as fuel axis on left */}
                            {hasDistance && distancePoints.length > 0 && (() => {
                              const distances = distancePoints.map((p: any) => p.distanceKm);
                              const minDistanceVal = Math.min(...distances);
                              const maxDistanceValForLabels = Math.max(...distances);
                              const labelX = svgWidth - padding.right + 4;
                              const topY = padding.top + 15;
                              const bottomY = padding.top + chartHeight - 15;

                              if (!isFullscreenChart) {
                                return (
                                  <>
                                    <text
                                      x={labelX}
                                      y={topY}
                                      fill="#10b981"
                                      fontSize="10"
                                      textAnchor="middle"
                                      transform={`rotate(90 ${labelX} ${topY})`}
                                    >
                                      {maxDistanceValForLabels.toFixed(2)} km
                                    </text>
                                    <text
                                      x={labelX}
                                      y={bottomY}
                                      fill="#10b981"
                                      fontSize="10"
                                      textAnchor="middle"
                                      transform={`rotate(90 ${labelX} ${bottomY})`}
                                    >
                                      {minDistanceVal.toFixed(2)} km
                                    </text>
                                  </>
                                );
                              }

                              const tickCount = 5;
                              const ticks: number[] = [];
                              for (let i = 0; i < tickCount; i += 1) {
                                const t =
                                  minDistanceVal +
                                  ((tickCount - 1 - i) / (tickCount - 1)) *
                                  (maxDistanceValForLabels - minDistanceVal);
                                ticks.push(t);
                              }

                              return (
                                <>
                                  {ticks.map((value, index) => {
                                    const y =
                                      padding.top +
                                      chartHeight -
                                      ((value - minDistanceVal) /
                                        (maxDistanceValForLabels - minDistanceVal || 1)) *
                                      chartHeight;
                                    return (
                                      // eslint-disable-next-line react/no-array-index-key
                                      <text
                                        key={index}
                                        x={labelX}
                                        y={y}
                                        fill="#10b981"
                                        fontSize="9"
                                        textAnchor="middle"
                                        transform={`rotate(90 ${labelX} ${y})`}
                                      >
                                        {value.toFixed(2)} km
                                      </text>
                                    );
                                  })}
                                </>
                              );
                            })()}

                            {/* X-axis line */}
                            <line
                              x1={padding.left}
                              y1={padding.top + chartHeight}
                              x2={svgWidth - padding.right}
                              y2={padding.top + chartHeight}
                              stroke={isDark ? '#64748b' : '#94a3b8'}
                              strokeWidth="1"
                            />

                            {/* X-axis labels: time ticks (when we have fuel points or distance points) */}
                            {(points.length > 0 || distancePoints.length > 0) && (() => {
                              const formatTime = (timeMs: number) =>
                                new Date(timeMs).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: false,
                                });
                              const labelY = padding.top + chartHeight + 16;

                              // If we only have one timestamp, show a single label in the center
                              if (firstTime === lastTime) {
                                const midX = padding.left + chartWidth / 2;
                                return (
                                  <text
                                    x={midX}
                                    y={labelY}
                                    fill={isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY}
                                    fontSize="11"
                                    textAnchor="middle"
                                  >
                                    {formatTime(firstTime)}
                                  </text>
                                );
                              }

                              if (!isFullscreenChart) {
                                return (
                                  <>
                                    <text
                                      x={padding.left}
                                      y={labelY}
                                      fill={
                                        isDark
                                          ? COLORS.THEME.DARK.TEXT_SECONDARY
                                          : COLORS.THEME.LIGHT.TEXT_SECONDARY
                                      }
                                      fontSize="11"
                                      textAnchor="start"
                                    >
                                      {formatTime(firstTime)}
                                    </text>
                                    <text
                                      x={svgWidth - padding.right}
                                      y={labelY}
                                      fill={
                                        isDark
                                          ? COLORS.THEME.DARK.TEXT_SECONDARY
                                          : COLORS.THEME.LIGHT.TEXT_SECONDARY
                                      }
                                      fontSize="11"
                                      textAnchor="end"
                                    >
                                      {formatTime(lastTime)}
                                    </text>
                                  </>
                                );
                              }

                              const tickCount = 5;
                              const ticks: number[] = [];
                              for (let i = 0; i < tickCount; i += 1) {
                                const t = firstTime + (i / (tickCount - 1)) * timeRange;
                                ticks.push(t);
                              }

                              return (
                                <>
                                  {ticks.map((t, index) => {
                                    const x =
                                      padding.left +
                                      ((t - firstTime) / (timeRange || 1)) * chartWidth;
                                    return (
                                      // eslint-disable-next-line react/no-array-index-key
                                      <text
                                        key={index}
                                        x={x}
                                        y={labelY}
                                        fill={
                                          isDark
                                            ? COLORS.THEME.DARK.TEXT_SECONDARY
                                            : COLORS.THEME.LIGHT.TEXT_SECONDARY
                                        }
                                        fontSize="11"
                                        textAnchor={
                                          index === 0 ? 'start' : index === tickCount - 1 ? 'end' : 'middle'
                                        }
                                      >
                                        {formatTime(t)}
                                      </text>
                                    );
                                  })}
                                </>
                              );
                            })()}
                          </svg>

                          {/* Tooltip (fuel point hover) */}
                          {fuelGraphHoveredPoint && fuelGraphTooltipPosition && (
                            <div
                              style={{
                                position: 'absolute',
                                left: `${fuelGraphTooltipPosition.x}px`,
                                top: 8,
                                transform: 'translate(-50%, 0)',
                                backgroundColor: isDark ? '#1f2937' : '#ffffff',
                                border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                                borderRadius: '6px',
                                padding: '8px 12px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)',
                                pointerEvents: 'none',
                                zIndex: 1000,
                                minWidth: '140px',
                              }}
                            >
                              <div style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                                marginBottom: '4px'
                              }}>
                                Fuel Level: {fuelGraphHoveredPoint.reading.fuelLevel.toFixed(2)}L
                              </div>
                              {hasDistance && fuelGraphHoveredDistancePoint && (
                                <div
                                  style={{
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#10b981',
                                    marginBottom: '4px',
                                  }}
                                >
                                  Distance: {`${fuelGraphHoveredDistancePoint.distanceKm.toFixed(2)} km`}
                                </div>
                              )}
                              <div style={{
                                fontSize: '13px',
                                color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY
                              }}>
                                {new Date(fuelGraphHoveredPoint.reading.recordedAt).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })}
                              </div>
                            </div>
                          )}
                          {/* Tooltip (distance-only when no fuel points) */}
                          {!fuelGraphHoveredPoint && fuelGraphHoveredDistancePoint && fuelGraphTooltipPosition && (
                            <div
                              style={{
                                position: 'absolute',
                                left: `${fuelGraphTooltipPosition.x}px`,
                                top: 8,
                                transform: 'translate(-50%, 0)',
                                backgroundColor: isDark ? '#1f2937' : '#ffffff',
                                border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                                borderRadius: '6px',
                                padding: '8px 12px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)',
                                pointerEvents: 'none',
                                zIndex: 1000,
                                minWidth: '140px',
                              }}
                            >
                              <div style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#10b981',
                                marginBottom: '4px',
                              }}>
                                Distance: {fuelGraphHoveredDistancePoint.distanceKm.toFixed(2)} km
                              </div>
                              <div style={{
                                fontSize: '13px',
                                color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY
                              }}>
                                {new Date(fuelGraphHoveredDistancePoint.date).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    };

                    return (
                      <div>
                        {/* Fuel chart */}
                        {renderFuelDistanceChart({ mode: 'compact' })}

                        {/* Fullscreen overlay */}
                        {isFuelGraphFullscreen && (
                          <div
                            style={{
                              position: 'fixed',
                              inset: 0,
                              backgroundColor: isDark ? 'rgba(15,23,42,0.9)' : 'rgba(15,23,42,0.85)',
                              zIndex: 9999,
                              display: 'flex',
                              flexDirection: 'column',
                              padding: '24px',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '12px',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: isDark ? '#e5e7eb' : '#f9fafb',
                                }}
                              >
                                Fuel & Distance – {selectedAmbulance.metadata?.name}
                              </div>
                              <button
                                type="button"
                                onClick={() => setIsFuelGraphFullscreen(false)}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '12px',
                                  borderRadius: '999px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  backgroundColor: '#ef4444',
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                <span>✕</span>
                                <span>Close</span>
                              </button>
                            </div>
                            <div
                              style={{
                                flex: 1,
                                backgroundColor: isDark ? '#020617' : '#ffffff',
                                borderRadius: '12px',
                                padding: '12px',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.45)',
                              }}
                            >
                              {renderFuelDistanceChart({ height: '100%', mode: 'fullscreen' })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div style={{ fontSize: '16px', color: isDark ? '#94a3b8' : '#64748b' }}>
                  No fuel data available
                </div>
              )}
            </div>

            {/* Last 24 Hours Trips */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                borderBottom: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                paddingBottom: '8px',
              }}>
                Last 24 Hours Trips
              </div>
              {ambulanceTripsLoading ? (
                <div style={{ fontSize: '13px', color: isDark ? '#94a3b8' : '#64748b', padding: '12px 0', textAlign: 'center' }}>
                  Loading trips...
                </div>
              ) : ambulanceTrips.length === 0 ? (
                <div style={{ fontSize: '13px', color: isDark ? '#94a3b8' : '#64748b', padding: '12px 0', textAlign: 'center' }}>
                  No trips in the last 24 hours
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '300px', overflowY: 'auto' }}>
                  {ambulanceTrips.map((trip) => {
                    const statusColor = (() => {
                      switch (trip.spec.status) {
                        case 'pending': return '#ff9800';
                        case 'assigned': return '#2196f3';
                        case 'in_transit': return '#9c27b0';
                        case 'patient_picked': return '#00bcd4';
                        case 'patient_dropped': return '#795548';
                        case 'completed': return '#4caf50';
                        case 'cancelled': return '#fa003f';
                        default: return '#999';
                      }
                    })();
                    const tripTime = trip.spec.timings?.bookingTime || trip.metadata.createdAt;
                    const canRevertTripFromDashboard =
                      (isTenantAdmin || isSystemAdmin) &&
                      trip.spec.status === BOOKING_STATUS.IN_TRANSIT;
                    const tripStatusPillBase: React.CSSProperties = {
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#fff',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.25,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box',
                      // minWidth: '108px',
                      // minHeight: '26px',
                    };
                    return (
                      <div
                        key={trip._id}
                        onClick={() => setTripDetailBooking(trip)}
                        style={{
                          padding: '10px 12px',
                          backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                          border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '8px' }}>
                          <span style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            flex: 1,
                          }}>
                            {trip.spec.patientInfo?.name || trip.metadata.name || 'Unknown'}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                            <span
                              style={{
                                ...tripStatusPillBase,
                                backgroundColor: statusColor,
                              }}
                            >
                              {trip.spec.status.replace('_', ' ')}
                            </span>
                            {canRevertTripFromDashboard && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRevertTripModalBooking(trip);
                                }}
                                style={{
                                  ...tripStatusPillBase,
                                  backgroundColor: '#dc2626',
                                  border: 'none',
                                  cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#b91c1c';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#dc2626';
                                }}
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', color: isDark ? '#94a3b8' : '#64748b' }}>
                            {trip.spec.bookingType === 'to_hospital' ? 'To Hospital' : trip.spec.bookingType === 'from_hospital' ? 'From Hospital' : trip.spec.bookingType || '—'}
                          </span>
                          <span style={{ fontSize: '11px', color: isDark ? '#94a3b8' : '#64748b' }}>
                            {tripTime ? new Date(tripTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
                          </span>
                        </div>
                        {(trip.spec.pickupLocation?.address || trip.spec.dropoffLocation?.address) && (
                          <div style={{ marginTop: '4px', fontSize: '11px', color: isDark ? '#64748b' : '#94a3b8' }}>
                            {trip.spec.pickupLocation?.address && (
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <span style={{ fontWeight: 500 }}>From:</span> {trip.spec.pickupLocation.address}
                              </div>
                            )}
                            {trip.spec.dropoffLocation?.address && (
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <span style={{ fontWeight: 500 }}>To:</span> {trip.spec.dropoffLocation.address}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Vehicle Image */}
            <div
              style={{
                width: '100%',
                height: '200px',
                borderRadius: '8px',
                // overflow: 'hidden',
                border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
              }}
            >
              <img
                src={selectedAmbulance.spec?.image || '/images/default_ambulance.png'}
                alt={selectedAmbulance.metadata?.name || 'Ambulance'}
                style={{
                  width: '100%',
                  height: '200px',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  // Fallback to default image if image fails to load
                  const target = e.target as HTMLImageElement;
                  if (target.src !== '/images/default_ambulance.png') {
                    target.src = '/images/default_ambulance.png';
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* Main Map Area */}
        <div className="tracking-main" style={{ minWidth: 0, minHeight: 0 }}>
          <div className="tracking-map-section" ref={mapSectionRef}>
            <RouteViewTabs
              selectedAmbulance={selectedAmbulance}
              routeViewMode={routeViewMode}
              activeBookings={activeBookings}
              isDark={isDark}
              onCurrentRouteClick={async () => {
                setRouteViewMode(ROUTE_VIEW_MODE.CURRENT);
                if (!selectedAmbulance) return;

                const bookingId = await getBookingId(selectedAmbulance, activeBookings);

                // If booking was found via API, add it to activeBookings
                if (bookingId && !activeBookings.find(b => b._id === bookingId)) {
                  const activeBooking = await queryActiveBookingFromAPI(selectedAmbulance._id);
                  if (activeBooking) {
                    setActiveBookings((prev) => {
                      if (!prev.find(b => b._id === bookingId)) {
                        return [...prev, activeBooking];
                      }
                      return prev;
                    });
                  }
                }

                if (bookingId && bookingId !== selectedAmbulance._id) {
                  try {
                    await fetchBookingDetails(bookingId);
                  } catch (err) {
                    console.error('[Tracking] Error in fetchBookingDetails from Current Route tab:', err);
                  }
                }

                // Route + GPS refresh handled by Traccar 30s poll when in-trip
                if (
                  selectedAmbulance &&
                  !isInTripOnCurrentRoute(selectedAmbulance._id, ROUTE_VIEW_MODE.CURRENT, activeBookings)
                ) {
                  setTimeout(() => calculateCurrentRoute(selectedAmbulance, liveLocations), 200);
                }
              }}
              onDayRouteClick={() => setRouteViewMode(ROUTE_VIEW_MODE.DAY)}
              endSlot={
                routeViewMode === ROUTE_VIEW_MODE.DAY ? (
                  <>
                    {routeLoading && (
                      <div
                        role="status"
                        aria-label="Loading route"
                        style={{
                          width: 18,
                          height: 18,
                          boxSizing: 'border-box',
                          flexShrink: 0,
                          border: `2px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                          borderTopColor: '#3b82f6',
                          borderRadius: '50%',
                          animation: 'spin 0.75s linear infinite',
                        }}
                      />
                    )}
                    <input
                      id="day-route-history-date"
                      type="date"
                      aria-label="Route date"
                      value={dayRouteHistoryDate}
                      max={formatLocalDateYMD(new Date())}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) return;
                        resetDayRoutePlayback();
                        setDayRouteHistoryDate(v);
                      }}
                      disabled={routeLoading}
                      style={{
                        padding: '6px 10px',
                        marginRight: '3px',
                        borderRadius: '6px',
                        border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                        background: isDark ? '#0f172a' : '#ffffff',
                        color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                        fontSize: '14px',
                        cursor: routeLoading ? 'not-allowed' : 'pointer',
                      }}
                    />
                  </>
                ) : null
              }
            />
            {/* Day route player controls: play, pause, forward, backward */}
            {routeViewMode === ROUTE_VIEW_MODE.DAY && locationHistoryRoute.length > 0 && (
              <div
                ref={speedControlsRef}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '8px',
                  marginBottom: '12px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  background: isDark ? 'rgba(30, 41, 59, 0.8)' : 'rgba(248, 250, 252, 0.9)',
                  border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                  width: 'fit-content',
                }}
              >
                <button
                  type="button"
                  onClick={dayRoutePlaybackStepBack}
                  disabled={dayRoutePlaybackIndex <= 0}
                  title="Previous point"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 36,
                    border: 'none',
                    borderRadius: '6px',
                    background: dayRoutePlaybackIndex <= 0 ? (isDark ? '#334155' : '#e2e8f0') : (isDark ? '#475569' : '#cbd5e1'),
                    color: dayRoutePlaybackIndex <= 0 ? (isDark ? '#64748b' : '#94a3b8') : (isDark ? '#f1f5f9' : '#0f172a'),
                    cursor: dayRoutePlaybackIndex <= 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" /></svg>
                </button>
                {dayRouteIsPlaying ? (
                  <button
                    type="button"
                    onClick={dayRoutePlaybackPause}
                    title="Pause"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 36,
                      border: 'none',
                      borderRadius: '6px',
                      background: isDark ? '#475569' : '#94a3b8',
                      color: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      // When starting playback, refit bounds once for the day route
                      shouldFitBoundsRef.current = true;
                      dayRoutePlaybackPlay();
                    }}
                    title="Play"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 36,
                      border: 'none',
                      borderRadius: '6px',
                      background: isDark ? '#3b82f6' : '#2563eb',
                      color: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                  </button>
                )}
                <button
                  type="button"
                  onClick={dayRoutePlaybackStepForward}
                  disabled={dayRoutePlaybackIndex >= locationHistoryRoute.length - 1}
                  title="Next point"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 36,
                    border: 'none',
                    borderRadius: '6px',
                    // marginRight: '15px',
                    background: dayRoutePlaybackIndex >= locationHistoryRoute.length - 1 ? (isDark ? '#334155' : '#e2e8f0') : (isDark ? '#475569' : '#cbd5e1'),
                    color: dayRoutePlaybackIndex >= locationHistoryRoute.length - 1 ? (isDark ? '#64748b' : '#94a3b8') : (isDark ? '#f1f5f9' : '#0f172a'),
                    cursor: dayRoutePlaybackIndex >= locationHistoryRoute.length - 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    {/* Mirror of the \"previous\" icon for perfect symmetry */}
                    <g transform="scale(-1,1) translate(-24,0)">
                      <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
                    </g>
                  </svg>
                </button>
                {/* <span style={{ marginLeft: '4px', fontSize: '13px', color: isDark ? '#94a3b8' : '#64748b' }}>
                  {dayRoutePlaybackIndex < 0 ? '0' : dayRoutePlaybackIndex + 1} / {locationHistoryRoute.length}
                </span> */}
                {/* Compact "burger" speed menu when space is tight */}
                {isSpeedCompact ? (
                  <div style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setIsSpeedMenuOpen((open) => !open)}
                      title="Playback speed"
                      style={{
                        padding: '4px 8px',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        // display: 'flex',
                        // alignItems: 'center',
                        // justifyContent: 'center',
                        background: isDark ? '#334155' : '#e2e8f0',
                        color: isDark ? '#e5e7eb' : '#0f172a',
                        cursor: 'pointer',
                      }}
                    >
                      {/* Simple burger icon */}
                      <span style={{ fontSize: '16px', lineHeight: 1, marginRight: '4px' }}>☰</span>
                      <span>{dayRoutePlaybackSpeed}x</span>
                    </button>
                    {isSpeedMenuOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '110%',
                          right: 0,
                          minWidth: '80px',
                          padding: '6px',
                          borderRadius: '8px',
                          background: isDark ? '#0f172a' : '#ffffff',
                          boxShadow: '0 6px 18px rgba(15,23,42,0.35)',
                          zIndex: 110,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                        }}
                      >
                        {([1, 2, 5, 10, 20] as const).map((speed) => (
                          <button
                            key={speed}
                            type="button"
                            onClick={() => {
                              setDayRoutePlaybackSpeed(speed);
                              setIsSpeedMenuOpen(false);
                            }}
                            title={`${speed}x`}
                            style={{
                              padding: '4px 8px',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              textAlign: 'left',
                              background: dayRoutePlaybackSpeed === speed
                                ? (isDark ? '#1d4ed8' : '#2563eb')
                                : 'transparent',
                              color: dayRoutePlaybackSpeed === speed
                                ? '#ffffff'
                                : (isDark ? '#e5e7eb' : '#0f172a'),
                              cursor: 'pointer',
                            }}
                          >
                            {speed}x
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  // Full inline speed buttons when there is enough space
                  ([1, 2, 5, 10, 20] as const).map((speed) => (
                    <button
                      key={speed}
                      type="button"
                      onClick={() => setDayRoutePlaybackSpeed(speed)}
                      title={`${speed}x`}
                      style={{
                        padding: '4px 10px',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: dayRoutePlaybackSpeed === speed ? 600 : 400,
                        background: dayRoutePlaybackSpeed === speed ? (isDark ? '#3b82f6' : '#2563eb') : (isDark ? '#334155' : '#e2e8f0'),
                        color: dayRoutePlaybackSpeed === speed ? '#fff' : (isDark ? '#94a3b8' : '#64748b'),
                        cursor: 'pointer',
                      }}
                    >
                      {speed}x
                    </button>
                  ))
                )}
              </div>
            )}

            <div className="tracking-map-container" style={{ position: 'relative', flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
              {/* Status Pie Chart - Hide when ambulance is selected, show when unselected */}
              {React.useMemo(() => {
                // Don't render pie chart if ambulance is selected
                if (selectedAmbulance) return null;

                // Always show all 4 statuses with their original counts from vehicleStats
                // This data never changes regardless of filtering - use useMemo to ensure stability
                const statusData = [
                  { status: 'on_duty', count: vehicleStats.onDuty, color: '#ef4444', label: 'On Duty' },
                  { status: 'available', count: vehicleStats.available, color: '#10b981', label: 'Available' },
                  { status: 'off_duty', count: vehicleStats.offDuty, color: '#f59e0b', label: 'Off Duty' },
                  { status: 'maintenance', count: vehicleStats.maintenance, color: '#64748b', label: 'Maintenance' },
                ];

                // Always use the total from vehicleStats to keep pie chart proportions consistent
                // This ensures the pie chart always shows all 4 segments with correct proportions
                const displayTotal = vehicleStats.total > 0 ? vehicleStats.total : 1; // Use 1 to avoid division by zero

                let currentAngle = -90; // Start from top
                const radius = 50;
                const centerX = 60;
                const centerY = 60;
                const strokeWidth = 2;

                const handleStatusClick = (status: string) => {
                  setStatusFilters((prev) => {
                    // Map actual status to filter category
                    const statusToCategory: Record<string, string> = {
                      'available': 'standby',
                      'on_duty': 'on_trip',
                      'off_duty': 'inactive',
                      'maintenance': 'inactive',
                    };
                    const category = statusToCategory[status] || 'total';

                    // If this category is already selected, switch to total
                    if (prev.length === 1 && prev[0] === category) {
                      return ['total'];
                    }
                    // Otherwise, select only this category
                    return [category];
                  });
                };

                return (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '10px',
                      left: '10px',
                      zIndex: 50,
                      // Hide the status pie chart when a single ambulance is selected
                      display: selectedAmbulance ? 'none' : 'block',
                    }}
                  >
                    <svg width="120" height="120" style={{ cursor: 'pointer' }}>
                      {statusData.map((item, index) => {
                        // Always calculate percentage based on the total from vehicleStats
                        // This ensures the pie chart proportions never change, only filtering happens
                        // CRITICAL: Always show all segments, even if count is 0
                        // If count is 0, still show a tiny segment (0.5% of circle) so it's visible
                        const percentage = item.count > 0
                          ? (item.count / displayTotal) * 100
                          : 0.5; // Minimal visible segment for zero count
                        const angle = Math.max((percentage / 100) * 360, 0.1); // Minimum 0.1 degrees to ensure visibility
                        const startAngle = currentAngle;
                        const endAngle = currentAngle + angle;
                        currentAngle = endAngle;

                        const startAngleRad = (startAngle * Math.PI) / 180;
                        const endAngleRad = (endAngle * Math.PI) / 180;

                        const x1 = centerX + radius * Math.cos(startAngleRad);
                        const y1 = centerY + radius * Math.sin(startAngleRad);
                        const x2 = centerX + radius * Math.cos(endAngleRad);
                        const y2 = centerY + radius * Math.sin(endAngleRad);

                        const largeArcFlag = angle > 180 ? 1 : 0;

                        const pathData = [
                          `M ${centerX} ${centerY}`,
                          `L ${x1} ${y1}`,
                          `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                          'Z',
                        ].join(' ');

                        // Check if this status segment is selected based on filter categories
                        const statusToCategory: Record<string, string> = {
                          'available': 'standby',
                          'on_duty': 'on_trip',
                          'off_duty': 'inactive',
                          'maintenance': 'inactive',
                        };
                        const category = statusToCategory[item.status] || 'total';
                        const isSelected = statusFilters.includes(category);
                        // Always use full color, no opacity change - pie chart appearance never changes
                        const segmentColor = item.color;
                        // Use thicker border and blue color to highlight selected segment
                        const segmentStroke = isSelected ? '#3b82f6' : (isDark ? '#1e293b' : '#ffffff');
                        const segmentStrokeWidth = isSelected ? 4 : strokeWidth;

                        return (
                          <g key={item.status}>
                            <path
                              d={pathData}
                              fill={segmentColor}
                              stroke={segmentStroke}
                              strokeWidth={segmentStrokeWidth}
                              style={{ cursor: 'pointer', transition: 'stroke-width 0.2s, stroke 0.2s' }}
                              onClick={() => handleStatusClick(item.status)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '0.9';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '1';
                              }}
                            />
                            {/* Label in center of segment - only show if count > 0 and percentage > 5 */}
                            {item.count > 0 && percentage > 5 && (
                              <text
                                x={centerX + (radius * 0.6) * Math.cos(((startAngle + endAngle) / 2) * Math.PI / 180)}
                                y={centerY + (radius * 0.6) * Math.sin(((startAngle + endAngle) / 2) * Math.PI / 180)}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="12"
                                fontWeight="bold"
                                fill={isDark ? '#f1f5f9' : '#1e293b'}
                                style={{ pointerEvents: 'none', userSelect: 'none' }}
                              >
                                {item.count}
                              </text>
                            )}
                          </g>
                        );
                      })}
                      {/* Center circle */}
                      <circle
                        cx={centerX}
                        cy={centerY}
                        r={radius * 0.4}
                        fill={isDark ? '#1e293b' : '#ffffff'}
                        stroke={isDark ? '#334155' : '#e2e8f0'}
                        strokeWidth={strokeWidth}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          // Click center to reset to default (total)
                          setStatusFilters(['total']);
                        }}
                      />
                      <text
                        x={centerX}
                        y={centerY}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="14"
                        fontWeight="bold"
                        fill={isDark ? '#f1f5f9' : '#1e293b'}
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                      >
                        {displayTotal}
                      </text>
                    </svg>
                  </div>
                );
              }, [vehicleStats, statusFilters, isDark, selectedAmbulance])}
              <LiveMap
                ambulances={filteredAmbulances}
                allAmbulances={allAmbulances}
                liveLocations={filteredLiveLocations}
                selectedAmbulance={selectedAmbulance}
                routeBookings={routeBookings}
                locationHistoryRoute={routeViewMode === ROUTE_VIEW_MODE.DAY ? locationHistoryRoute : []}
                currentRoute={routeViewMode === ROUTE_VIEW_MODE.CURRENT ? currentRoute : null}
                activeBookings={activeBookings}
                hospitalLocation={hospitalLocation}
                hospitalDefaultLocation={hospitalDefaultLocation}
                convertKnotsToKmph={convertKnotsToKmph}
                ambulanceTypes={ambulanceTypes}
                liveLocationsApiFinished={liveLocationsApiFinished}
                shouldFitBoundsRef={shouldFitBoundsRef}
                currentRouteTabHasFittedRef={currentRouteTabHasFittedRef}
                userHasPannedOrZoomedRef={userHasPannedOrZoomedRef}
                dayRoutePlaybackIndex={routeViewMode === ROUTE_VIEW_MODE.DAY ? dayRoutePlaybackIndex : -1}
                dayRoutePlaybackProgress={dayRoutePlaybackProgress}
                dayRouteIsPlaying={dayRouteIsPlaying}
                dayRoutePlaybackSpeed={dayRoutePlaybackSpeed}
                dayRoutePlaybackIndexRef={dayRoutePlaybackIndexRef}
                dayRoutePlaybackProgressRef={dayRoutePlaybackProgressRef}
                dayRouteIsPlayingRef={dayRouteIsPlayingRef}
                dayRouteHistoryDate={dayRouteHistoryDate}
                onInfoWindowStateChange={(isOpen) => {
                  isInfoWindowOpenRef.current = isOpen;
                }}
                onRouteEta={(eta) => setRouteEta(eta)}
                routeViewMode={routeViewMode}
              />
              {/* ETA overlay: above map panes but below app-level drawers (e.g. notifications z-index ~1000) */}
              {routeViewMode === ROUTE_VIEW_MODE.CURRENT && selectedAmbulance && (
                <div style={{ position: 'absolute', top: '10px', right: '65px', zIndex: 100, display: 'flex', alignItems: 'center', gap: '8px', pointerEvents: 'auto' }}>
                  {routeEta != null && ((currentRoute?.bookingStatus === BOOKING_STATUS.PATIENT_PICKED || currentRoute?.bookingStatus === BOOKING_STATUS.PATIENT_DROPPED) ? (routeEta.dropoffEtaMinutes ?? 0) > 0 : ((routeEta.pickupEtaMinutes ?? 0) + (routeEta.dropoffEtaMinutes ?? 0)) > 0) && (
                    <div
                      onMouseEnter={() => setShowEtaTooltip(true)}
                      onMouseLeave={() => setShowEtaTooltip(false)}
                      style={{
                        position: 'relative',
                        padding: '8px 12px',
                        background: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                        color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.15)',
                        cursor: 'default',
                      }}
                    >
                      ETA: {(currentRoute?.bookingStatus === BOOKING_STATUS.PATIENT_PICKED || currentRoute?.bookingStatus === BOOKING_STATUS.PATIENT_DROPPED)
                        ? (routeEta.dropoffEtaMinutes ?? 0)
                        : (routeEta.pickupEtaMinutes ?? 0) + (routeEta.dropoffEtaMinutes ?? 0)} min
                      {showEtaTooltip && (
                        <div
                          style={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            bottom: '100%',
                            marginBottom: '6px',
                            padding: '8px 10px',
                            background: isDark ? '#1e293b' : '#0f172a',
                            color: '#f1f5f9',
                            fontSize: '12px',
                            fontWeight: '500',
                            borderRadius: '6px',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                            zIndex: 2,
                          }}
                        >
                          {(currentRoute?.bookingStatus === BOOKING_STATUS.PATIENT_PICKED || currentRoute?.bookingStatus === BOOKING_STATUS.PATIENT_DROPPED)
                            ? `Dropoff ETA: ${routeEta.dropoffEtaMinutes ?? 0} min`
                            : routeEta.pickupEtaMinutes != null && routeEta.dropoffEtaMinutes != null
                              ? `Pickup ETA: ${routeEta.pickupEtaMinutes} min, Dropoff ETA: ${routeEta.dropoffEtaMinutes} min`
                              : `Dropoff ETA: ${routeEta.dropoffEtaMinutes ?? 0} min`}
                        </div>
                      )}
                    </div>
                  )}
                  {/* <button
                    onClick={async () => {
                      if (!selectedAmbulance) return;
                      await calculateCurrentRoute(selectedAmbulance, liveLocations);
                    }}
                    disabled={routeLoading}
                    style={{
                      padding: '8px 12px',
                      border: 'none',
                      background: routeLoading
                        ? (isDark ? '#475569' : '#cbd5e1')
                        : (isDark ? '#1e293b' : '#ffffff'),
                      color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                      borderRadius: '6px',
                      cursor: routeLoading ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      opacity: routeLoading ? 0.6 : 1,
                      boxShadow: isDark
                        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                        : '0 2px 8px rgba(0, 0, 0, 0.15)',
                    }}
                    title="Refresh route"
                  >
                    {routeLoading ? (
                      <>
                        <span>⟳</span>
                        <span>Refreshing...</span>
                      </>
                    ) : (
                      <>
                        <span>⟳</span>
                        <span>Refresh</span>
                      </>
                    )}
                  </button> */}
                </div>
              )}

              {/* Day Route: manual refresh button to reload path and start/end markers */}
              {routeViewMode === ROUTE_VIEW_MODE.DAY && selectedAmbulance && (
                <div
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '60px',
                    zIndex: 100,
                    display: 'flex',
                    alignItems: 'center',
                    pointerEvents: 'auto',
                  }}
                >
                  <button
                    type="button"
                    onClick={async () => {
                      if (!selectedAmbulance || routeLoading) return;
                      // On manual day-route refresh, refit bounds once after route redraw
                      // shouldFitBoundsRef.current = true;
                      resetDayRoutePlayback();
                      await fetchDayRouteHistory(selectedAmbulance, dayRouteHistoryDate);
                    }}
                    disabled={routeLoading}
                    title="Refresh day route"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: '999px',
                      border: 'none',
                      background: routeLoading
                        ? (isDark ? '#334155' : '#e2e8f0')
                        : (isDark ? '#1e293b' : '#ffffff'),
                      color: isDark ? '#e5e7eb' : '#0f172a',
                      boxShadow: isDark
                        ? '0 2px 8px rgba(0, 0, 0, 0.45)'
                        : '0 2px 8px rgba(15, 23, 42, 0.25)',
                      cursor: routeLoading ? 'not-allowed' : 'pointer',
                      opacity: routeLoading ? 0.7 : 1,
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 4v6h6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20 20v-6h-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5.64 18.36A9 9 0 0 0 19 13M5 11a9 9 0 0 1 13.36-5.36"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Trip/Booking Detail Drawer */}
      {tripDetailBooking && (
        <div style={{ zIndex: 9999 }} className="drawer-overlay" onClick={() => { setTripDetailBooking(null); setShowTripDetailsDownloadMenu(false); }}>
          <div className="drawer" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', minWidth: '690px' }}>
            <div
              className="drawer-header"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}
            >
              <h3 className="drawer-title">Booking Details</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ position: 'relative' }} ref={tripDetailsDownloadMenuRef}>
                  <button
                    onClick={() => !tripDetailsDownloading && setShowTripDetailsDownloadMenu(!showTripDetailsDownloadMenu)}
                    disabled={tripDetailsDownloading}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: tripDetailsDownloading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      opacity: tripDetailsDownloading ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tripDetailsDownloading ? 'Generating...' : 'Download'}
                  </button>
                  {showTripDetailsDownloadMenu && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '5px',
                        backgroundColor: isDark ? '#1e293b' : '#ffffff',
                        border: isDark ? '1px solid #334155' : '1px solid #ddd',
                        borderRadius: '6px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        minWidth: '150px',
                      }}
                    >
                      <button
                        onClick={() => handleDownloadTripDetails('pdf')}
                        disabled={tripDetailsDownloading}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: 'transparent',
                          color: isDark ? '#ffffff' : '#333',
                          border: 'none',
                          borderRadius: '6px 6px 0 0',
                          cursor: tripDetailsDownloading ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          textAlign: 'left',
                          opacity: tripDetailsDownloading ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!tripDetailsDownloading) {
                            e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        Download as PDF
                      </button>
                      <button
                        onClick={() => handleDownloadTripDetails('excel')}
                        disabled={tripDetailsDownloading}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: 'transparent',
                          color: isDark ? '#ffffff' : '#333',
                          border: 'none',
                          borderTop: isDark ? '1px solid #334155' : '1px solid #ddd',
                          borderRadius: '0 0 6px 6px',
                          cursor: tripDetailsDownloading ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          textAlign: 'left',
                          opacity: tripDetailsDownloading ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!tripDetailsDownloading) {
                            e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        Download as Excel
                      </button>
                    </div>
                  )}
                </div>
                <button className="drawer-close" onClick={() => { setTripDetailBooking(null); setShowTripDetailsDownloadMenu(false); }}>
                  ×
                </button>
              </div>
            </div>

            <div className="drawer-body" style={{ padding: '20px' }}>
              {tripDetailsDownloadError && (
                <div style={{ marginBottom: '10px', color: '#f44336', fontSize: '14px' }}>
                  {tripDetailsDownloadError}
                </div>
              )}
              {(() => {
                const booking = tripDetailBooking;

                // Theme-aware colors
                const cardBg = isDark ? '#1e293b' : '#f9f9f9';
                const cardBorder = isDark ? '#334155' : '#e0e0e0';
                const labelColor = isDark ? '#94a3b8' : '#666';
                const textColor = isDark ? '#f1f5f9' : '#0f172a';

                const getStatusColor = (status: string) => {
                  switch (status) {
                    case 'pending':
                      return '#ff9800';
                    case 'assigned':
                      return '#2196f3';
                    case 'in_transit':
                      return '#9c27b0';
                    case 'patient_picked':
                      return '#00bcd4';
                    case 'completed':
                      return '#4caf50';
                    case 'cancelled':
                      return '#fa003f';
                    default:
                      return '#999';
                  }
                };

                const getPriorityColor = (priority: string) => {
                  switch (priority) {
                    case 'critical':
                      return '#f44336';
                    case 'high':
                      return '#ff9800';
                    case 'medium':
                      return '#2196f3';
                    case 'low':
                      return '#4caf50';
                    default:
                      return '#999';
                  }
                };

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Basic Information */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Basic Information</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div style={{ fontSize: '16px', color: textColor }}>
                          <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Reference: </span>
                          <span style={{ marginLeft: '16px', fontWeight: '500' }}>{(booking.metadata?.name ?? '').toUpperCase()}</span>
                        </div>
                        <div style={{ fontSize: '16px', color: textColor, display: 'flex', flexWrap: 'nowrap', gap: '16px', alignItems: 'center' }}>
                          <div style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Status: </span>
                            <span style={{ marginLeft: '8px' }}>
                              <span
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  color: 'white',
                                  background: getStatusColor(booking.spec.status),
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {booking.spec.status.replace('_', ' ').toUpperCase()}
                              </span>
                            </span>
                          </div>
                          <div style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Priority: </span>
                            <span style={{ marginLeft: '8px' }}>
                              <span
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  color: 'white',
                                  background: getPriorityColor(booking.spec.priority),
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {booking.spec.priority.toUpperCase()}
                              </span>
                            </span>
                          </div>
                          <div style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Booking Amount: </span>
                            <span style={{ marginLeft: '8px', fontWeight: '600', fontSize: '16px' }}>
                              {booking.spec.bookingAmount ? `₹${Number(booking.spec.bookingAmount).toLocaleString()}` : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Patient Information */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Patient Information</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Patient Name:</label>
                          <div style={{ fontSize: '16px', fontWeight: '500', color: textColor }}>
                            {booking.spec.patientInfo?.name || booking.spec.patientId || '—'}
                          </div>
                        </div>
                        {(booking.spec.patientInfo?.number || booking.spec.patientInfo?.age !== undefined || booking.spec.patientInfo?.dateOfBirth || booking.spec.patientInfo?.gender) && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                            {booking.spec.patientInfo?.number && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Phone:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>{booking.spec.patientInfo.number}</div>
                              </div>
                            )}
                            {(booking.spec.patientInfo?.age !== undefined || booking.spec.patientInfo?.dateOfBirth) && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Age:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>
                                  {(() => {
                                    const age = booking.spec.patientInfo?.age;
                                    const dob = booking.spec.patientInfo?.dateOfBirth;

                                    // If age is 0 or undefined and DOB exists, calculate age in days
                                    if ((age === 0 || age === undefined) && dob) {
                                      const birthDate = new Date(dob);
                                      const today = new Date();
                                      const diffTime = today.getTime() - birthDate.getTime();
                                      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                      return `${diffDays} days`;
                                    }

                                    // If age is defined and >= 1, show the age value with "years" postfix
                                    if (age !== undefined && age >= 1) {
                                      return `${age} years`;
                                    }

                                    // Fallback
                                    return '—';
                                  })()}
                                </div>
                              </div>
                            )}
                            {booking.spec.patientInfo?.dateOfBirth && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Date of Birth:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>{formatDateDDMMYYYY(booking.spec.patientInfo.dateOfBirth)}</div>
                              </div>
                            )}
                            {booking.spec.patientInfo?.gender && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Gender:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>
                                  {(() => {
                                    const gender = booking.spec.patientInfo.gender?.toLowerCase();
                                    if (gender === 'male') return 'Male';
                                    if (gender === 'female') return 'Female';
                                    if (gender === 'other' || gender === 'others') return 'Others';
                                    // Fallback: capitalize first letter
                                    return booking.spec.patientInfo.gender.charAt(0).toUpperCase() + booking.spec.patientInfo.gender.slice(1).toLowerCase();
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {(booking.spec.patientInfo?.contactPersonName || booking.spec.patientInfo?.contactPersonNumber) && (
                          <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            {booking.spec.patientInfo?.contactPersonName && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Contact Person Name:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>{booking.spec.patientInfo.contactPersonName}</div>
                              </div>
                            )}
                            {booking.spec.patientInfo?.contactPersonNumber && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Contact Person Number:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>{booking.spec.patientInfo.contactPersonNumber}</div>
                              </div>
                            )}
                          </div>
                        )}
                        {booking.spec.medicalCondition && (
                          <div style={{ marginTop: '12px' }}>
                            <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Medical Condition:</label>
                            <div style={{ fontSize: '16px', color: textColor }}>{booking.spec.medicalCondition}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Assignment Information */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Assignment Information</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Booked By:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>{booking.spec.bookedByDisplay || booking.spec.bookedBy || '—'}</div>
                        </div>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Ambulance:</label>
                          <div style={{ fontSize: '16px', color: textColor, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                            {booking.spec.ambulanceInfo
                              ? (() => {
                                const name = booking.spec.ambulanceInfo.name;
                                const licensePlate = booking.spec.ambulanceInfo.licensePlate;
                                const type = booking.spec.ambulanceInfo.type;

                                if (!name && !licensePlate && !type) return '—';

                                const nameDisplay = name
                                  ? (licensePlate ? `${name} (${licensePlate})` : name)
                                  : (licensePlate ? `(${licensePlate})` : '—');

                                return (
                                  <>
                                    <div style={{ flex: 1 }}>{nameDisplay}</div>
                                    {type && (
                                      <span
                                        style={{
                                          fontSize: '14px',
                                          padding: '3px 6px',
                                          borderRadius: '4px',
                                          backgroundColor: 'transparent',
                                          color: labelColor,
                                          border: `1.5px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                                          fontWeight: 500,
                                          display: 'inline-block',
                                          flexShrink: 0,
                                        }}
                                      >
                                        {type}
                                      </span>
                                    )}
                                  </>
                                );
                              })()
                              : '—'}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Pilot/Driver:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>
                            {booking.spec.pilotInfo?.name
                              ? (booking.spec.pilotInfo?.number
                                ? `${booking.spec.pilotInfo.name} (${booking.spec.pilotInfo.number})`
                                : booking.spec.pilotInfo.name)
                              : '—'}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Assigned Staff:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>
                            {booking.spec.assignedStaffInfo && booking.spec.assignedStaffInfo.length > 0 ? (
                              booking.spec.assignedStaffInfo.map((s, idx) => (
                                <div key={idx} style={{ marginBottom: '4px' }}>
                                  {s.name
                                    ? (s.number
                                      ? `${s.name} (${s.number})`
                                      : s.name)
                                    : '—'}
                                </div>
                              ))
                            ) : (
                              '—'
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Location Information */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Location Information</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Pickup Location:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>
                            {booking.spec.pickupLocation?.address || (
                              booking.spec.pickupLocation
                                ? `${booking.spec.pickupLocation.latitude.toFixed(6)}, ${booking.spec.pickupLocation.longitude.toFixed(6)}`
                                : '—'
                            )}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Dropoff Location:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>
                            {booking.spec.dropoffLocation?.address || (
                              booking.spec.dropoffLocation
                                ? `${booking.spec.dropoffLocation.latitude.toFixed(6)}, ${booking.spec.dropoffLocation.longitude.toFixed(6)}`
                                : '—'
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Timings */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Timings</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div style={{ fontSize: '16px', color: textColor }}>
                          <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Booking Time: </span>
                          <span style={{ marginLeft: '16px' }}>
                            {booking.spec.timings?.bookingTime
                              ? formatDateTimeDDMMYYYY(booking.spec.timings.bookingTime)
                              : booking.metadata.createdAt
                                ? formatDateTimeDDMMYYYY(booking.metadata.createdAt)
                                : '—'}
                          </span>
                        </div>
                        <div style={{ fontSize: '16px', color: textColor }}>
                          <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Last Update: </span>
                          <span style={{ marginLeft: '16px' }}>
                            {booking.metadata.updatedAt ? formatDateTimeDDMMYYYY(booking.metadata.updatedAt) : '—'}
                          </span>
                        </div>
                        {booking.spec.timings?.assignedTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Assigned Time: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.assignedTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.inTransitTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Trip Start: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.inTransitTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.scheduledPickupTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Scheduled Pickup: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.scheduledPickupTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.estimatedArrivalTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Estimated Arrival: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.estimatedArrivalTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.actualArrivalTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Actual Arrival: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.actualArrivalTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.completionTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Completion Time: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.completionTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.cancelledTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Cancelled Time: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.cancelledTime)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Admin cancellation (when booking cancelled by tenant admin) */}
                    {booking.spec.status === 'cancelled' && booking.spec.cancellationReason && (
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Cancellation Reason (Admin)</h4>
                        <div style={{ padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}`, fontSize: '16px', color: textColor }}>
                          {booking.spec.cancellationReason}
                        </div>
                      </div>
                    )}

                    {/* Pilot Assignment History */}
                    {booking.spec.pilotHistory && booking.spec.pilotHistory.length > 0 && (
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Pilot Assignment History</h4>
                        <div style={{ padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}`, maxHeight: '300px', overflowY: 'auto' }}>
                          {booking.spec.pilotHistory.map((history, index) => {
                            const getHistoryStatusColor = (status: string) => {
                              switch (status) {
                                case 'assigned': return '#2196f3';
                                case 'in_transit': return '#9c27b0';
                                case 'patient_picked': return '#ff9800';
                                case 'patient_dropped': return '#795548';
                                case 'completed': return '#4caf50';
                                case 'cancelled': return '#fa003f';
                                default: return '#666';
                              }
                            };

                            const formatTime = (timestamp?: number) => {
                              if (!timestamp) return '—';
                              return formatDateTimeDDMMYYYY(timestamp);
                            };

                            return (
                              <div
                                key={index}
                                style={{
                                  padding: '12px',
                                  marginBottom: index < booking.spec.pilotHistory!.length - 1 ? '12px' : '0',
                                  borderBottom: index < booking.spec.pilotHistory!.length - 1 ? `1px solid ${cardBorder}` : 'none',
                                  backgroundColor: index === booking.spec.pilotHistory!.length - 1 ? (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)') : 'transparent',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                  <div style={{ fontWeight: '600', fontSize: '17px', color: textColor }}>{history.name}</div>
                                  <span
                                    style={{
                                      padding: '4px 10px',
                                      borderRadius: '4px',
                                      fontSize: '13px',
                                      fontWeight: 'bold',
                                      color: 'white',
                                      background: getHistoryStatusColor(history.status),
                                    }}
                                  >
                                    {history.status.replace('_', ' ').toUpperCase()}
                                  </span>
                                </div>
                                {history.phone && (
                                  <div style={{ fontSize: '15px', color: labelColor, marginBottom: '6px' }}>
                                    Phone: {history.phone}
                                  </div>
                                )}
                                <div style={{ fontSize: '14px', color: labelColor, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                  {history.assignedTime && <div>Assigned: {formatTime(history.assignedTime)}</div>}
                                  {history.inTransitTime && <div>In Transit: {formatTime(history.inTransitTime)}</div>}
                                  {(history.patientPickedTime ?? history.arrivedTime) && <div>Patient Picked: {formatTime(history.patientPickedTime ?? history.arrivedTime)}</div>}
                                  {(history as { patientDroppedTime?: number }).patientDroppedTime && <div>Patient Dropped: {formatTime((history as { patientDroppedTime?: number }).patientDroppedTime)}</div>}
                                  {history.completionTime && <div>Completed: {formatTime(history.completionTime)}</div>}
                                  {history.cancelledTime && <div>Cancelled: {formatTime(history.cancelledTime)}</div>}
                                  {history.status === 'cancelled' && history.cancellationReason && (
                                    <div style={{ marginTop: '6px', fontStyle: 'italic' }}>Reason: {history.cancellationReason}</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {booking.spec.notes && (
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Notes</h4>
                        <div style={{ fontSize: '16px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}`, whiteSpace: 'pre-wrap', color: textColor }}>
                          {booking.spec.notes}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={revertTripModalBooking !== null}
        title="Cancel Booking Assignment & Reset to Pending?"
        message="The booking ambulance assignment will be cancelled and the booking will be reset to pending, the ambulance will be marked available. This action cannot be undone."
        confirmText={revertTripSubmitting ? 'Working…' : 'Cancel & Reset'}
        cancelText="Back"
        type="warning"
        onConfirm={() => {
          void handleRevertTripConfirm();
        }}
        onCancel={() => {
          if (!revertTripSubmitting) setRevertTripModalBooking(null);
        }}
      />
    </div>
  );
};

const LiveMap: React.FC<{
  ambulances: OnTripAmbulance[];
  allAmbulances: Ambulance[];
  liveLocations?: any[];
  selectedAmbulance?: Ambulance | null;
  routeBookings?: Booking[];
  locationHistoryRoute?: any[];
  currentRoute?: {
    origin: { lat: number; lng: number };
    destination: { lat: number; lng: number };
    destinationType: 'pickup' | 'dropoff';
    stops?: RouteStop[];
    bookingType?: BookingType;
    bookingStatus?: string;
  } | null;
  onRouteEta?: (eta: { pickupEtaMinutes?: number; dropoffEtaMinutes?: number }) => void;
  routeViewMode?: RouteViewMode;
  activeBookings?: Booking[];
  hospitalLocation?: { latitude: number; longitude: number } | null;
  hospitalDefaultLocation?: { latitude: number; longitude: number } | null;
  convertKnotsToKmph?: boolean;
  ambulanceTypes?: AmbulanceType[];
  liveLocationsApiFinished?: boolean;
  shouldFitBoundsRef?: React.MutableRefObject<boolean>;
  currentRouteTabHasFittedRef?: React.MutableRefObject<boolean>;
  userHasPannedOrZoomedRef?: React.MutableRefObject<boolean>;
  dayRoutePlaybackIndex?: number;
  dayRoutePlaybackProgress?: number;
  dayRouteIsPlaying?: boolean;
  dayRoutePlaybackSpeed?: number;
  dayRoutePlaybackIndexRef?: React.MutableRefObject<number>;
  dayRoutePlaybackProgressRef?: React.MutableRefObject<number>;
  dayRouteIsPlayingRef?: React.MutableRefObject<boolean>;
  /** When Day Route history date is not "today", idle live overlay is hidden */
  dayRouteHistoryDate: string;
  onInfoWindowStateChange?: (isOpen: boolean) => void;
}> = ({ ambulances, allAmbulances, liveLocations = [], selectedAmbulance, routeBookings = [], locationHistoryRoute = [], currentRoute = [], routeViewMode = ROUTE_VIEW_MODE.CURRENT, activeBookings = [], hospitalLocation = null, hospitalDefaultLocation = null, convertKnotsToKmph = false, ambulanceTypes = [], liveLocationsApiFinished = false, shouldFitBoundsRef, currentRouteTabHasFittedRef, userHasPannedOrZoomedRef, dayRoutePlaybackIndex = -1, dayRoutePlaybackProgress = 0, dayRouteIsPlaying = false, dayRoutePlaybackSpeed = 1, dayRoutePlaybackIndexRef, dayRoutePlaybackProgressRef, dayRouteIsPlayingRef, dayRouteHistoryDate, onInfoWindowStateChange, onRouteEta }) => {
  const [mapMode, setMapMode] = useState<'standard' | 'satellite'>('standard');
  // Process live locations from external API
  // Expected format: array of objects with { id, deviceId, isOffline, lastUpdate, liveLocation: [longitude, latitude] }
  const processedLiveLocations = React.useMemo(() => {
    return liveLocations.map((loc: any) => {
      const deviceId = loc.deviceId;
      // liveLocation is [longitude, latitude] array
      const liveLoc = loc.liveLocation;
      const longitude = Array.isArray(liveLoc) && liveLoc.length === 2 ? liveLoc[0] : null;
      const latitude = Array.isArray(liveLoc) && liveLoc.length === 2 ? liveLoc[1] : null;

      return {
        deviceId,
        vehicleId: deviceId, // For compatibility
        latitude,
        longitude,
        isOffline: loc.isOffline,
        lastUpdate: loc.lastUpdate,
        ...loc, // Keep all original data
      };
    }).filter((loc: any) => loc.latitude && loc.longitude && !isNaN(loc.latitude) && !isNaN(loc.longitude));
  }, [liveLocations]);

  // Combine on-trip ambulances with all ambulances to get full location data
  // Also merge with live locations from external API
  // When selectedAmbulance is set, only show that vehicle on the map
  const ambulancesWithLocation = React.useMemo(() => {
    const onTripWithLocation = ambulances
      .map((onTripAmb) => {
        const fullAmb = allAmbulances.find((a) => a._id === onTripAmb.ambulanceId || a.metadata.name === onTripAmb.id);

        // If selectedAmbulance is set, only include matching vehicles
        if (selectedAmbulance) {
          const matchesSelected = fullAmb?._id === selectedAmbulance._id ||
            fullAmb?.metadata.name === selectedAmbulance.metadata.name ||
            onTripAmb.ambulanceId === selectedAmbulance._id ||
            onTripAmb.id === selectedAmbulance.metadata.name;
          if (!matchesSelected) {
            return null;
          }
        }

        // Check if there's a live location for this ambulance
        // Match by GPS device ID (deviceId) from the live location API
        const gpsDeviceId = fullAmb?.spec?.gpsDevice?.deviceId;
        const liveLoc = processedLiveLocations.find((loc: any) => {
          // Match by deviceId (GPS device ID)
          return loc.deviceId === gpsDeviceId || loc.deviceId === fullAmb?.spec?.gpsDevice?.gpsDeviceId;
        });

        // Prefer live location over stored location
        const currentLocation = liveLoc
          ? { latitude: liveLoc.latitude, longitude: liveLoc.longitude, address: liveLoc.address }
          : onTripAmb.currentLocation || fullAmb?.spec?.currentLocation || null;

        return {
          ...onTripAmb,
          currentLocation,
          liveLocation: liveLoc ? true : false,
          // Device may flag isOffline during lag; on_duty ambulances with live coords follow operational status
          isOffline: !!(liveLoc?.isOffline && fullAmb?.spec?.status !== 'on_duty'),
          lastUpdate: liveLoc?.lastUpdate,
          // Device last location & summary from external API (if present)
          lastLocationsObject: (liveLoc as any)?.lastLocationsObject,
          summary: (liveLoc as any)?.summary,
          // Instantaneous speed from device (if present)
          speed: (liveLoc as any)?.lastLocationsObject?.speed ?? (liveLoc as any)?.speed ?? (onTripAmb as any)?.speed ?? null,
        };
      })
      .filter((amb) => amb !== null && amb.currentLocation && amb.currentLocation.latitude && amb.currentLocation.longitude) as any[];

    // Add vehicles from live locations that aren't in on-trip list
    // Only if no ambulance is selected, or if the vehicle matches the selected ambulance
    const liveOnlyVehicles = processedLiveLocations
      .filter((loc: any) => {
        // If selectedAmbulance is set, only include matching vehicles
        if (selectedAmbulance) {
          const fullAmb = allAmbulances.find((a) => {
            return a.spec?.gpsDevice?.deviceId === loc.deviceId ||
              a.spec?.gpsDevice?.gpsDeviceId === loc.deviceId;
          });
          const matchesSelected = fullAmb?._id === selectedAmbulance._id ||
            fullAmb?.metadata.name === selectedAmbulance.metadata.name;
          if (!matchesSelected) {
            return false;
          }
        }

        // Check if this vehicle is already in onTripWithLocation
        return !onTripWithLocation.some((amb) => {
          const fullAmb = allAmbulances.find((a) => a._id === amb.ambulanceId || a.metadata.name === amb.id);
          const gpsDeviceId = fullAmb?.spec?.gpsDevice?.deviceId;
          return loc.deviceId === gpsDeviceId || loc.deviceId === fullAmb?.spec?.gpsDevice?.gpsDeviceId;
        });
      })
      .map((loc: any) => {
        // Try to find matching ambulance by GPS device ID
        const fullAmb = allAmbulances.find((a) => {
          return a.spec?.gpsDevice?.deviceId === loc.deviceId ||
            a.spec?.gpsDevice?.gpsDeviceId === loc.deviceId;
        });

        return {
          id: fullAmb?.metadata.name || loc.deviceId || 'Unknown',
          ambulanceId: fullAmb?._id || loc.deviceId,
          type: fullAmb?.spec?.type || 'Ambulance',
          status: fullAmb?.spec?.status || (loc.isOffline ? 'offline' : 'available'),
          currentLocation: {
            latitude: loc.latitude,
            longitude: loc.longitude,
            address: undefined, // API doesn't provide address
          },
          liveLocation: true,
          isOffline: !!(loc.isOffline && fullAmb?.spec?.status !== 'on_duty'),
          lastUpdate: loc.lastUpdate,
          lastLocationsObject: loc.lastLocationsObject,
          summary: loc.summary,
          speed: loc.lastLocationsObject?.speed ?? loc.speed ?? null,
        };
      });

    const allVehicles = [...onTripWithLocation, ...liveOnlyVehicles];

    // If selectedAmbulance is set but not found in the above lists, try to add it from allAmbulances
    if (selectedAmbulance && allVehicles.length === 0) {
      const selectedFullAmb = allAmbulances.find(
        (a) => a._id === selectedAmbulance._id || a.metadata.name === selectedAmbulance.metadata.name
      );

      if (selectedFullAmb && selectedFullAmb.spec?.currentLocation) {
        const gpsDeviceId = selectedFullAmb.spec?.gpsDevice?.deviceId;
        const liveLoc = processedLiveLocations.find((loc: any) => {
          return loc.deviceId === gpsDeviceId || loc.deviceId === selectedFullAmb.spec?.gpsDevice?.gpsDeviceId;
        });

        const currentLocation = liveLoc
          ? { latitude: liveLoc.latitude, longitude: liveLoc.longitude, address: liveLoc.address }
          : selectedFullAmb.spec.currentLocation;

        if (currentLocation && currentLocation.latitude && currentLocation.longitude) {
          allVehicles.push({
            id: selectedFullAmb.metadata.name,
            ambulanceId: selectedFullAmb._id,
            type: selectedFullAmb.spec?.type || 'Ambulance',
            status: selectedFullAmb.spec?.status || 'available',
            currentLocation,
            liveLocation: liveLoc ? true : false,
            isOffline: !!(liveLoc?.isOffline && selectedFullAmb.spec?.status !== 'on_duty'),
            lastUpdate: liveLoc?.lastUpdate,
          });
        }
      }
    }

    // When in day route playback, hide the selected ambulance's live marker so only the moving playback marker is visible
    if (selectedAmbulance && dayRoutePlaybackIndex >= 0) {
      return allVehicles.filter(
        (v) => v.ambulanceId !== selectedAmbulance._id && v.id !== selectedAmbulance.metadata?.name
      );
    }
    return allVehicles;
  }, [ambulances, allAmbulances, processedLiveLocations, selectedAmbulance, dayRoutePlaybackIndex]);

  // Calculate center from all ambulance locations, hospital location, or use default
  const calculateCenter = React.useMemo((): [number, number] => {
    // If an ambulance is selected, center on it
    if (selectedAmbulance && selectedAmbulance.spec?.currentLocation) {
      const loc = selectedAmbulance.spec.currentLocation;
      return [loc.latitude, loc.longitude];
    }

    // If live locations API hasn't finished yet, use hospital default location or India center
    if (!liveLocationsApiFinished) {
      if (hospitalDefaultLocation &&
        typeof hospitalDefaultLocation.latitude === 'number' &&
        typeof hospitalDefaultLocation.longitude === 'number' &&
        !isNaN(hospitalDefaultLocation.latitude) &&
        !isNaN(hospitalDefaultLocation.longitude)) {
        return [hospitalDefaultLocation.latitude, hospitalDefaultLocation.longitude];
      }
      // Default to center of India for full map view
      return [20.5937, 78.9629]; // Center of India
    }

    // If live locations API finished but returned empty, use hospital default location or India center
    if (liveLocations.length === 0) {
      if (hospitalDefaultLocation &&
        typeof hospitalDefaultLocation.latitude === 'number' &&
        typeof hospitalDefaultLocation.longitude === 'number' &&
        !isNaN(hospitalDefaultLocation.latitude) &&
        !isNaN(hospitalDefaultLocation.longitude)) {
        return [hospitalDefaultLocation.latitude, hospitalDefaultLocation.longitude];
      }
      // Default to center of India for full map view
      return [20.5937, 78.9629]; // Center of India
    }

    // If there are ambulances with locations, center on their average
    if (ambulancesWithLocation.length > 0) {
      const avgLat = ambulancesWithLocation.reduce((sum, amb) => sum + amb.currentLocation!.latitude, 0) / ambulancesWithLocation.length;
      const avgLng = ambulancesWithLocation.reduce((sum, amb) => sum + amb.currentLocation!.longitude, 0) / ambulancesWithLocation.length;
      return [avgLat, avgLng];
    }

    // If no ambulances, use hospital default location if available and valid
    if (hospitalDefaultLocation &&
      typeof hospitalDefaultLocation.latitude === 'number' &&
      typeof hospitalDefaultLocation.longitude === 'number' &&
      !isNaN(hospitalDefaultLocation.latitude) &&
      !isNaN(hospitalDefaultLocation.longitude)) {
      return [hospitalDefaultLocation.latitude, hospitalDefaultLocation.longitude];
    }

    // Default fallback to center of India for full map view
    return [20.5937, 78.9629]; // Center of India
  }, [ambulancesWithLocation, selectedAmbulance, hospitalDefaultLocation, liveLocationsApiFinished, liveLocations.length]);

  const defaultCenter = calculateCenter;
  const defaultZoom = React.useMemo(() => {
    // If live locations API hasn't finished or is empty, use appropriate zoom
    if (!liveLocationsApiFinished || liveLocations.length === 0) {
      // If hospital default location is available, zoom to it, otherwise show full India
      if (hospitalDefaultLocation) {
        return 13;
      }
      return 5; // Full India map view
    }
    // After API finishes and has data, use existing logic
    return ambulancesWithLocation.length === 1 ? 15 : ambulancesWithLocation.length > 1 ? 12 : 10;
  }, [ambulancesWithLocation.length, liveLocationsApiFinished, liveLocations.length, hospitalDefaultLocation]);

  // Google Maps implementation
  const mapRef = React.useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const [infoWindows, setInfoWindows] = useState<google.maps.InfoWindow[]>([]);
  const [polylines, setPolylines] = useState<google.maps.Polyline[]>([]);
  const [routeMarkers, setRouteMarkers] = useState<google.maps.Marker[]>([]);
  const routeMarkersRef = React.useRef<google.maps.Marker[]>([]);
  const markerClustererRef = React.useRef<any>(null);
  const directionsServiceRef = React.useRef<any>(null);
  const directionsRendererRef = React.useRef<any>(null);
  const selectedAmbulanceRef = React.useRef(selectedAmbulance);
  selectedAmbulanceRef.current = selectedAmbulance;
  const routeViewModeRef = React.useRef<RouteViewMode>(routeViewMode);
  routeViewModeRef.current = routeViewMode;
  // Track latest ambulancesWithLocation so marker handlers always see fresh data
  const ambulancesWithLocationRef = React.useRef(ambulancesWithLocation);
  ambulancesWithLocationRef.current = ambulancesWithLocation;
  const liveLocationsRef = React.useRef(liveLocations);
  liveLocationsRef.current = liveLocations;
  const allAmbulancesRef = React.useRef(allAmbulances);
  allAmbulancesRef.current = allAmbulances;
  const openInfoWindowRef = React.useRef<google.maps.InfoWindow | null>(null); // Track if an info window is open
  // Store markers by ambulance ID to prevent recreation
  const markersMapRef = React.useRef<Map<string, { marker: google.maps.Marker; infoWindow: google.maps.InfoWindow }>>(new Map());
  // Track which icon URL is currently set per marker so we don't reset to default on every live update
  const markerIconUrlRef = React.useRef<Map<string, string>>(new Map());
  // Track patient/hospital route markers explicitly so we can clear them
  const pickupHospitalMarkersRef = React.useRef<google.maps.Marker[]>([]);
  const playbackMarkerRef = React.useRef<google.maps.Marker | null>(null);
  const playbackOverlayRef = React.useRef<PlaybackInfoOverlayInstance | null>(null);
  const currentRouteInfoOverlayRef = React.useRef<PlaybackInfoOverlayInstance | null>(null);
  /** Day Route (not playing): compact date/speed bubble above selected vehicle; hidden while full InfoWindow is open */
  const dayRouteIdleCompactOverlayRef = React.useRef<PlaybackInfoOverlayInstance | null>(null);
  const showSelectedAmbulanceOverlayRef = React.useRef<(() => void) | null>(null);
  const startDayRouteOverlayRef = React.useRef<PlaybackInfoOverlayInstance | null>(null);
  const endDayRouteOverlayRef = React.useRef<PlaybackInfoOverlayInstance | null>(null);
  const dayRouteStartEndMarkersRef = React.useRef<google.maps.Marker[]>([]);
  const startOverlayHideTimeoutRef = React.useRef<number | null>(null);
  const endOverlayHideTimeoutRef = React.useRef<number | null>(null);
  const dayVehicleInfoWindowHideTimeoutRef = React.useRef<Map<string, number>>(new Map());
  const dayVehicleInfoWindowHoveredRef = React.useRef<Map<string, boolean>>(new Map());
  const dayVehicleInfoWindowCleanupRef = React.useRef<Map<string, () => void>>(new Map());
  const dayRoutePathRef = React.useRef<google.maps.LatLng[]>([]);
  const playbackRafRef = React.useRef<number | null>(null);
  const prevRouteEffectStateRef = React.useRef<{
    selectedAmbulance: Ambulance | null | undefined;
    routeBookings: Booking[];
    locationHistoryRoute: any[];
    currentRoute: any;
    routeViewMode: RouteViewMode;
    dayRoutePlaybackIndex: number;
    liveLocations: any[];
    dayRouteIsPlaying: boolean;
    dayRouteHistoryDate: string;
  } | null>(null);
  const lastCurrentRouteStopsKeyRef = React.useRef<string | null>(null);
  const lastDirectionsRequestKeyRef = React.useRef<string | null>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const AMBULANCE_ICON_URL = '/images/ambulance_icon.png';
  const PATIENT_ICON_URL = '/images/patient_icon.png';
  const HOSPITAL_ICON_URL = '/images/hospital_icon.png';
  const START_ROUTE_ICON_URL = '/images/start_marker.png';
  const END_ROUTE_ICON_URL = '/images/end_marker.png';
  const DAY_ROUTE_VEHICLE_INFO_HIDE_DELAY_MS = 10000;

  // When no ambulance is selected, ensure that route overlays (especially
  // Patient/Hospital pickup/drop icons) are cleared from the map so they are
  // not visible.
  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return;
    if (selectedAmbulance) return;

    // Clear route polylines
    polylines.forEach((polyline: google.maps.Polyline) => polyline.setMap(null));
    setPolylines([]);

    // Clear all route markers (they are only created for selected ambulance routes)
    routeMarkersRef.current.forEach((marker: google.maps.Marker) => {
      marker.setMap(null);
    });
    routeMarkersRef.current = [];
    setRouteMarkers([]);

    // Specifically clear any patient/hospital pickup/drop markers we track
    pickupHospitalMarkersRef.current.forEach((marker: google.maps.Marker) => {
      marker.setMap(null);
    });
    pickupHospitalMarkersRef.current = [];

    // Clear any playback marker/overlay from previous day-route playback
    if (playbackMarkerRef.current) {
      playbackMarkerRef.current.setMap(null);
      playbackMarkerRef.current = null;
    }
    if (playbackOverlayRef.current) {
      playbackOverlayRef.current.setMap(null);
      playbackOverlayRef.current = null;
    }

    // Clear day route start/end markers and info overlays
    dayRouteStartEndMarkersRef.current.forEach((m) => m.setMap(null));
    dayRouteStartEndMarkersRef.current = [];
    const existingStartOverlay = startDayRouteOverlayRef.current as PlaybackInfoOverlayInstance | null;
    if (existingStartOverlay) {
      existingStartOverlay.setMap(null);
      startDayRouteOverlayRef.current = null;
    }
    const existingEndOverlay = endDayRouteOverlayRef.current as PlaybackInfoOverlayInstance | null;
    if (existingEndOverlay) {
      existingEndOverlay.setMap(null);
      endDayRouteOverlayRef.current = null;
    }

    // Also clear any directions polyline
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selectedAmbulance]);

  // Memoize default ambulance icon (use resolved URL so it loads when deployed under base path)
  const defaultAmbulanceIcon = React.useMemo<google.maps.Icon | google.maps.Symbol | any>(() => {
    const resolvedUrl = resolveIconUrl(AMBULANCE_ICON_URL);
    const gm = (window as any).google?.maps;
    if (!gm) {
      return { url: resolvedUrl };
    }
    return {
      url: resolvedUrl,
      scaledSize: new gm.Size(65, 65),
      anchor: new gm.Point(22, 22),
    };
  }, []);

  const createImageIcon = React.useCallback((url: string, size = 42): google.maps.Icon | any => {
    const gm = (window as any).google?.maps;
    if (!gm) {
      return { url };
    }
    return {
      url,
      scaledSize: new gm.Size(size, size),
      anchor: new gm.Point(size / 2, size / 2),
    };
  }, []);

  // Helper function to close info window
  const closeInfoWindow = React.useCallback((infoWindow: google.maps.InfoWindow) => {
    if (infoWindow && 'close' in infoWindow) {
      (infoWindow as any).close();
    }
  }, []);

  // When tab (routeViewMode) or selected ambulance changes, clear all info windows and overlays
  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return;

    // Close all standard Google InfoWindows
    infoWindows.forEach((iw) => closeInfoWindow(iw));
    openInfoWindowRef.current = null;

    // Clear any custom overlays (current route, day route start/end, playback)
    const overlays: (PlaybackInfoOverlayInstance | null)[] = [
      currentRouteInfoOverlayRef.current,
      playbackOverlayRef.current,
      startDayRouteOverlayRef.current,
      endDayRouteOverlayRef.current,
      dayRouteIdleCompactOverlayRef.current,
    ];
    overlays.forEach((ov, idx) => {
      if (ov) {
        ov.setMap(null);
        if (idx === 0) currentRouteInfoOverlayRef.current = null;
        if (idx === 1) playbackOverlayRef.current = null;
        if (idx === 2) startDayRouteOverlayRef.current = null;
        if (idx === 3) endDayRouteOverlayRef.current = null;
        if (idx === 4) dayRouteIdleCompactOverlayRef.current = null;
      }
    });

    onInfoWindowStateChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, routeViewMode, selectedAmbulance]);

  // Map ambulance type name -> resolved icon URL from ambulanceTypes (prefer type icon when available)
  const ambulanceTypeIconMap = React.useMemo(() => {
    const map = new Map<string, string>();
    ambulanceTypes.forEach((t) => {
      if (t.metadata?.name && t.spec?.iconUrl) {
        const resolved = resolveIconUrl(t.spec.iconUrl);
        map.set(t.metadata.name, resolved);
        map.set(t.metadata.name.toLowerCase(), resolved);
      }
    });
    return map;
  }, [ambulanceTypes]);

  // Resolve full ambulance from allAmbulances (by _id, metadata.name, or gps deviceId)
  const findFullAmbulance = React.useCallback(
    (ambulance: { ambulanceId?: string; id?: string }) => {
      const aid = ambulance.ambulanceId || ambulance.id;
      if (!aid) return undefined;
      return (
        allAmbulances.find(
          (a) =>
            a._id === aid ||
            a.metadata?.name === aid ||
            a.metadata?.name === ambulance.id ||
            a.spec?.gpsDevice?.deviceId === aid ||
            a.spec?.gpsDevice?.gpsDeviceId === aid
        )
      );
    },
    [allAmbulances]
  );

  // Get icon URL for ambulance: prefer type icon when available, else default (always resolved for production)
  const getAmbulanceIconUrl = React.useCallback(
    (ambulance: { ambulanceId?: string; id?: string; type?: string }, fullAmb?: Ambulance | null): string => {
      const typeName = fullAmb?.spec?.type || ambulance.type;
      if (!typeName) return resolveIconUrl(AMBULANCE_ICON_URL);
      const exact = ambulanceTypeIconMap.get(typeName);
      if (exact) return exact;
      const byLower = ambulanceTypeIconMap.get(typeName.toLowerCase());
      if (byLower) return byLower;
      const byType = ambulanceTypes.find(
        (t) => t.metadata?.name === typeName || t.metadata?.name?.toLowerCase() === typeName?.toLowerCase()
      );
      if (byType?.spec?.iconUrl) return resolveIconUrl(byType.spec.iconUrl);
      return resolveIconUrl(AMBULANCE_ICON_URL);
    },
    [ambulanceTypeIconMap, ambulanceTypes]
  );

  // Initialize Google Map - only once, don't recreate on data updates
  const initializeMap = React.useCallback(() => {
    if (!mapRef.current || !window.google || !window.google.maps || map) return; // Don't reinitialize if map already exists

    const center = new google.maps.LatLng(defaultCenter[0], defaultCenter[1]);

    const googleMap = new google.maps.Map(mapRef.current, {
      center,
      zoom: defaultZoom,
      mapTypeId: mapMode === 'satellite' ? google.maps.MapTypeId.SATELLITE : google.maps.MapTypeId.ROADMAP,
      fullscreenControl: true,
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
    });

    setMap(googleMap);
  }, [mapMode, map]); // Only depend on mapMode and map state, not on defaultCenter/defaultZoom to prevent re-initialization

  // Wait for Google Maps to load - only initialize once
  useEffect(() => {
    if (!mapRef.current || map) return; // Don't reinitialize if map already exists

    if (!window.google || !window.google.maps) {
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.maps && mapRef.current && !map) {
          clearInterval(checkGoogle);
          initializeMap();
        }
      }, 100);
      return () => clearInterval(checkGoogle);
    }

    initializeMap();
  }, [initializeMap, map]); // Include map in dependencies to prevent re-initialization

  // Update map type when mode changes
  useEffect(() => {
    if (map) {
      map.setMapTypeId(mapMode === 'satellite' ? google.maps.MapTypeId.SATELLITE : google.maps.MapTypeId.ROADMAP);
    }
  }, [map, mapMode]);

  // Track programmatic fitBounds so we don't mark "user zoomed" when we caused the zoom
  const programmaticFitInProgressRef = React.useRef<boolean>(false);
  // On Current Route: mark when user zooms or pans so we never fitBounds again until they leave the tab
  useEffect(() => {
    if (!map || !userHasPannedOrZoomedRef || !window.google?.maps) return;
    const gm = window.google.maps;
    const onUserInteraction = () => {
      if (programmaticFitInProgressRef.current) return;
      if (routeViewModeRef.current === ROUTE_VIEW_MODE.CURRENT) {
        userHasPannedOrZoomedRef.current = true;
      }
    };
    const zoomListener = gm.event.addListener(map, 'zoom_changed', onUserInteraction);
    const dragListener = gm.event.addListener(map, 'dragend', onUserInteraction);
    return () => {
      gm.event.removeListener(zoomListener);
      gm.event.removeListener(dragListener);
    };
  }, [map, userHasPannedOrZoomedRef]);

  // Current Route tab: single one-time fitBounds when we have route data with valid coords (not 0,0).
  // Skip fitting if origin/destination are placeholder (0,0) so we fit once when pickup or dropoff becomes available.
  useEffect(() => {
    if (routeViewMode !== ROUTE_VIEW_MODE.CURRENT || !map || !selectedAmbulance || !currentRoute || typeof currentRoute !== 'object' || !('origin' in currentRoute) || !currentRoute.origin || !currentRoute.destination) return;
    if (currentRouteTabHasFittedRef?.current || !shouldFitBoundsRef?.current) return;
    const cr = currentRoute as { origin: { lat: number; lng: number }; destination: { lat: number; lng: number }; stops?: { lat: number; lng: number }[] };
    const isInvalidCoord = (lat: number, lng: number) => (lat === 0 && lng === 0) || isNaN(lat) || isNaN(lng);
    if (isInvalidCoord(cr.origin.lat, cr.origin.lng) || isInvalidCoord(cr.destination.lat, cr.destination.lng)) return; // wait for valid route; do not mark as fitted
    const b = new google.maps.LatLngBounds();
    b.extend(new google.maps.LatLng(cr.origin.lat, cr.origin.lng));
    b.extend(new google.maps.LatLng(cr.destination.lat, cr.destination.lng));
    if (cr.stops?.length) cr.stops.forEach((s) => { if (!isInvalidCoord(s.lat, s.lng)) b.extend(new google.maps.LatLng(s.lat, s.lng)); });
    programmaticFitInProgressRef.current = true;
    map.fitBounds(b, 50);
    if (currentRouteTabHasFittedRef) currentRouteTabHasFittedRef.current = true;
    if (shouldFitBoundsRef) shouldFitBoundsRef.current = false;
    setTimeout(() => { programmaticFitInProgressRef.current = false; }, 200);
  }, [routeViewMode, currentRoute, map, selectedAmbulance]);

  // Update map center when hospitalDefaultLocation becomes available and liveLocations is empty (never when a route is shown)
  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return;
    // Do not reset zoom/center when user has selected an ambulance and is viewing current route
    if (selectedAmbulance) return;

    // Update center if hospital default location is available and:
    // Case 1: Live locations API hasn't finished yet (initial load)
    // Case 2: Live locations API finished but returned empty AND no ambulances with locations
    const shouldUseHospitalLocation =
      hospitalDefaultLocation &&
      typeof hospitalDefaultLocation.latitude === 'number' &&
      typeof hospitalDefaultLocation.longitude === 'number' &&
      !isNaN(hospitalDefaultLocation.latitude) &&
      !isNaN(hospitalDefaultLocation.longitude) &&
      (
        !liveLocationsApiFinished || // Before API finishes
        (liveLocationsApiFinished && liveLocations.length === 0 && ambulancesWithLocation.length === 0) // After API finishes with empty data
      );

    if (shouldUseHospitalLocation) {
      const center = new google.maps.LatLng(
        hospitalDefaultLocation.latitude,
        hospitalDefaultLocation.longitude
      );
      map.setCenter(center);
      map.setZoom(13);
    }
  }, [map, selectedAmbulance, liveLocationsApiFinished, liveLocations.length, ambulancesWithLocation.length, hospitalDefaultLocation]);

  // Memoize cluster renderer to prevent recreation
  const clusterRenderer = React.useMemo(() => ({
    render: ({ count, position }: { count: number; position: google.maps.LatLng }) => {
      // Calculate size based on count
      const size = count < 10 ? 50 : count < 50 ? 60 : count < 100 ? 70 : 80;
      const fontSize = count < 10 ? 16 : count < 50 ? 18 : count < 100 ? 20 : 22;
      const radius = size / 2 - 4;
      const center = size / 2;

      // Create cluster icon - white circle with red border and black number
      const svgContent = `
        <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
          <circle cx="${center}" cy="${center}" r="${radius}" fill="#FFFFFF" stroke="#DC2626" stroke-width="4" opacity="1"/>
          <text x="${center}" y="${center + fontSize / 2.5}" font-size="${fontSize}" font-weight="bold" text-anchor="middle" fill="#000000" font-family="Arial, sans-serif">${count}</text>
        </svg>
      `;

      const icon = {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgContent),
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(center, center),
      };

      return new google.maps.Marker({
        position,
        icon,
      });
    },
  }), []);

  // Helper to create info window content (general)
  const createInfoWindowContent = React.useCallback((ambulance: any, typeDisplayName: string, formattedStatus: string) => {
    const deviceOffline = !!(ambulance as any).isOffline;
    const statusLower = (ambulance.status || '').toString().toLowerCase();
    const showOfflineDeviceBadge = deviceOffline && statusLower !== 'on_duty';
    return `
      <div style="
        color: ${isDark ? '#f1f5f9' : '#0f172a'}; 
        background-color: ${isDark ? '#1e293b' : '#ffffff'}; 
        font-size: 16px; 
        min-width: 200px; 
        padding: 12px; 
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      ">
        <strong style="font-size: 18px; color: ${isDark ? '#f1f5f9' : '#0f172a'};">
          ${ambulance.id}
        </strong>
        ${ambulance.liveLocation ? `
          <span style="
            margin-left: 8px; 
            padding: 4px 8px; 
            background-color: ${showOfflineDeviceBadge ? '#EF4444' : '#10B981'}; 
            color: #FFFFFF; 
            border-radius: 4px; 
            font-size: 13px;
            font-weight: bold;
            display: inline-block;
          ">
            ${showOfflineDeviceBadge ? 'OFFLINE' : 'LIVE'}
          </span>
        ` : ''}
        <br /><br />
        <div style="color: ${isDark ? '#cbd5e1' : '#475569'}; margin-top: 8px;">
          <div style="margin-bottom: 6px;">
            <strong style="color: ${isDark ? '#f1f5f9' : '#0f172a'};">Type:</strong> ${typeDisplayName}
          </div>
          <div style="margin-bottom: 6px;">
            <strong style="color: ${isDark ? '#f1f5f9' : '#0f172a'};">Status:</strong> ${formattedStatus}
          </div>
          ${(ambulance as any).lastUpdate ? `
            <div style="margin-bottom: 6px;">
              <strong style="color: ${isDark ? '#f1f5f9' : '#0f172a'};">Last Update:</strong> ${formatDateTimeDDMMYYYY((ambulance as any).lastUpdate)}
            </div>
          ` : ''}
          ${ambulance.currentLocation?.address ? `
            <div style="margin-bottom: 6px;">
              <strong style="color: ${isDark ? '#f1f5f9' : '#0f172a'};">Location:</strong> ${ambulance.currentLocation.address}
            </div>
          ` : ''}
          <div>
            <strong style="color: ${isDark ? '#f1f5f9' : '#0f172a'};">Coordinates:</strong>
            <a
              href="https://www.google.com/maps?q=${encodeURIComponent(
                `${ambulance.currentLocation!.latitude},${ambulance.currentLocation!.longitude}`
              )}"
              target="_blank"
              rel="noopener noreferrer"
              style="color: ${isDark ? '#38bdf8' : '#2563eb'}; text-decoration: underline; margin-left: 4px; cursor: pointer;"
            >${ambulance.currentLocation!.latitude.toFixed(6)}, ${ambulance.currentLocation!.longitude.toFixed(6)}</a>
          </div>
        </div>
      </div>
    `;
  }, [isDark]);

  // Helper to create info window content for Current Route (clicking ambulance marker)
  const createCurrentRouteInfoWindowContent = React.useCallback(
    (ambulance: any) => {
      const textColor = isDark ? '#f1f5f9' : '#f9fafb';
      const bgColor = '#0f172a';

      const lastLoc = (ambulance as any).lastLocationsObject || {};
      const summary = (ambulance as any).summary || {};

      // Time: newest of last fix time vs envelope lastUpdate (API can lag one field behind the other)
      let timeStr = formatOverlayDateTime(new Date());
      const ts = preferredLiveFixTimestampMs(lastLoc, ambulance);
      if (ts != null) {
        try {
          const d = new Date(ts);
          timeStr = !isNaN(d.getTime())
            ? d.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true,
            })
            : formatOverlayDateTime(new Date());
        } catch {
          timeStr = formatOverlayDateTime(new Date());
        }
      }

      // Fuel from lastLocationsObject.fuelInLiters (if > 0), else —
      let fuelStr = '—';
      const fuelInLiters = (lastLoc as any).fuelInLiters;
      if (typeof fuelInLiters === 'number' && fuelInLiters > 0) {
        fuelStr = `${Number(fuelInLiters).toFixed(2)} L`;
      }

      const rawSpeed = pickLiveSpeedForDisplay(lastLoc, ambulance, summary);
      const speedStr = formatSpeedDisplay(rawSpeed, convertKnotsToKmph);

      // Distance from summary.distance in km (if present)
      let distanceStr = '—';
      const distance = (summary as any).distance;
      if (typeof distance === 'number' && !isNaN(distance)) {
        distanceStr = `${Number(distance).toFixed(2)} km`;
      }

      return (
        '<div style="display:flex;flex-direction:column;align-items:center;">' +
        '<div style="color: ' +
        textColor +
        '; background-color: ' +
        bgColor +
        '; font-size: 14px; width: max-content; max-width: 260px; padding: 12px 14px; border-radius: 8px; box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);">' +
        '<div style="margin-bottom: 4px;"><strong style="color: ' +
        textColor +
        ';">' +
        timeStr +
        '</strong></div>' +
        '<div style="margin-bottom: 4px;"><em style="color: ' +
        textColor +
        ';">Speed: </em><strong style="color: ' +
        textColor +
        ';">' +
        speedStr +
        '</strong></div>' +
        '</div></div>'
      );
    },
    [convertKnotsToKmph, isDark]
  );

  // Update markers when ambulances change - optimized to update positions instead of recreating
  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return;

    const resolvedDefaultUrl = resolveIconUrl(AMBULANCE_ICON_URL);
    const iconUrlRef = markerIconUrlRef.current;
    // Preload type icon and set marker only when image loads; on error keep default (fixes deployed flakiness)
    const setTypeIconWhenLoaded = (marker: google.maps.Marker, url: string, aid: string) => {
      const img = new Image();
      img.onload = () => {
        marker.setIcon(createImageIcon(url, 65));
        iconUrlRef.set(aid, url);
      };
      img.onerror = () => { /* keep default icon */ };
      img.src = url;
    };

    const currentMarkersMap = markersMapRef.current;
    const newAmbulanceIds = new Set<string>();
    const newMarkers: google.maps.Marker[] = [];
    const newInfoWindows: google.maps.InfoWindow[] = [];
    let markersChanged = false;

    const hideDayRouteIdleCompactOverlay = () => {
      const o = dayRouteIdleCompactOverlayRef.current;
      if (o) {
        o.setMap(null);
        dayRouteIdleCompactOverlayRef.current = null;
      }
    };

    const showDayRouteIdleCompactOverlayForSelected = () => {
      if (routeViewModeRef.current !== ROUTE_VIEW_MODE.DAY) return;
      if (dayRouteIsPlayingRef?.current) return;
      if (dayRouteHistoryDate.trim() !== formatLocalDateYMD(new Date())) return;
      if (!map || !selectedAmbulanceRef.current) return;
      const sel = selectedAmbulanceRef.current;
      const selId = (sel._id || (sel as any).id || sel.metadata?.name) as string | undefined;
      if (!selId) return;
      const markerData = markersMapRef.current.get(selId);
      if (!markerData) return;
      const pos = markerData.marker.getPosition();
      if (!pos) return;
      hideDayRouteIdleCompactOverlay();
      const latestAmb = pickAmbulanceForLiveInfoOverlay(
        sel,
        ambulancesWithLocationRef.current.find((a: any) => (a.ambulanceId || a.id) === selId),
        allAmbulancesRef.current,
        liveLocationsRef.current
      );
      const overlay = createDayRoutePlaybackInfoOverlay(
        pos,
        createCurrentRouteInfoWindowContent(latestAmb)
      );
      dayRouteIdleCompactOverlayRef.current = overlay;
      overlay.setMap(map);
      onInfoWindowStateChange?.(true);
    };

    // Process each ambulance
    ambulancesWithLocation.forEach((ambulance) => {
      const ambulanceId = ambulance.ambulanceId || ambulance.id;
      newAmbulanceIds.add(ambulanceId);

      const position = new google.maps.LatLng(
        ambulance.currentLocation!.latitude,
        ambulance.currentLocation!.longitude
      );

      // Check if marker already exists
      const existingMarkerData = currentMarkersMap.get(ambulanceId);

      const fullAmb = findFullAmbulance(ambulance);
      const typeIconUrl = getAmbulanceIconUrl(ambulance, fullAmb);
      const isDefaultIcon = typeIconUrl === resolvedDefaultUrl;
      const currentIconUrl = iconUrlRef.get(ambulanceId);
      // Don't reset icon when we already show the correct type icon (avoids flash to default on every live update)
      const iconAlreadyCorrect = currentIconUrl === typeIconUrl;
      const markerIcon: google.maps.Icon = defaultAmbulanceIcon;

      if (existingMarkerData) {
        // Update position instead of recreating
        const currentPos = existingMarkerData.marker.getPosition();
        if (currentPos && (currentPos.lat() !== position.lat() || currentPos.lng() !== position.lng())) {
          existingMarkerData.marker.setPosition(position);
        }
        if (!iconAlreadyCorrect) {
          existingMarkerData.marker.setIcon(markerIcon);
          iconUrlRef.set(ambulanceId, resolvedDefaultUrl);
          if (!isDefaultIcon) setTypeIconWhenLoaded(existingMarkerData.marker, typeIconUrl, ambulanceId);
        }

        // Update info window content if needed
        const ambulanceTypeName = fullAmb?.spec?.type || ambulance.type;
        const ambulanceType = ambulanceTypes.find((t) => t.metadata?.name === ambulanceTypeName);
        const typeDisplayName = ambulanceType?.metadata?.displayName || ambulanceTypeName || ambulance.type;
        const formattedStatus = formatAmbulanceStatus(ambulance.status);
        const newContent =
          routeViewMode === ROUTE_VIEW_MODE.CURRENT && selectedAmbulance
            ? createCurrentRouteInfoWindowContent(ambulance)
            : createInfoWindowContent(ambulance, typeDisplayName, formattedStatus);
        (existingMarkerData.infoWindow as any).setContent?.(newContent);

        newMarkers.push(existingMarkerData.marker);
        newInfoWindows.push(existingMarkerData.infoWindow);
      } else {
        // Create new marker for new ambulance
        markersChanged = true;
        const marker = new google.maps.Marker({
          position,
          map: null, // Don't add to map directly - let clusterer handle it
          icon: markerIcon,
          title: ambulance.id,
        });
        iconUrlRef.set(ambulanceId, resolvedDefaultUrl);
        if (!isDefaultIcon) setTypeIconWhenLoaded(marker, typeIconUrl, ambulanceId);

        const ambulanceTypeName = fullAmb?.spec?.type || ambulance.type;
        const ambulanceType = ambulanceTypes.find((t) => t.metadata?.name === ambulanceTypeName);
        const typeDisplayName = ambulanceType?.metadata?.displayName || ambulanceTypeName || ambulance.type;
        const formattedStatus = formatAmbulanceStatus(ambulance.status);
        const infoContent =
          routeViewMode === ROUTE_VIEW_MODE.CURRENT && selectedAmbulance
            ? createCurrentRouteInfoWindowContent(ambulance)
            : createInfoWindowContent(ambulance, typeDisplayName, formattedStatus);
        const infoWindow = new google.maps.InfoWindow({
          content: infoContent,
        });

        const showCurrentRouteOverlay = () => {
          const positionForOverlay = marker.getPosition();
          if (!positionForOverlay) return;
          if (routeViewModeRef.current !== ROUTE_VIEW_MODE.CURRENT || !selectedAmbulanceRef.current) return;
          hideDayRouteIdleCompactOverlay();

          // Always resolve latest ambulance snapshot from latest ambulancesWithLocation
          const latestAmb =
            (ambulanceId &&
              ambulancesWithLocationRef.current.find(
                (a: any) => (a.ambulanceId || a.id) === ambulanceId
              )) ||
            ambulance;
          const overlayLatest =
            selectedAmbulanceRef.current != null
              ? pickAmbulanceForLiveInfoOverlay(
                  selectedAmbulanceRef.current,
                  latestAmb,
                  allAmbulancesRef.current,
                  liveLocationsRef.current
                )
              : latestAmb;

          const existingOverlay = currentRouteInfoOverlayRef.current as any;
          if (existingOverlay) {
            existingOverlay.setMap(null);
            currentRouteInfoOverlayRef.current = null;
          }
          const overlay = createDayRoutePlaybackInfoOverlay(
            positionForOverlay,
            createCurrentRouteInfoWindowContent(overlayLatest)
          );
          currentRouteInfoOverlayRef.current = overlay;
          overlay.setMap(map);
          onInfoWindowStateChange?.(true);
        };

        const hideCurrentRouteOverlay = () => {
          const existingOverlay = currentRouteInfoOverlayRef.current as any;
          if (existingOverlay) {
            existingOverlay.setMap(null);
            currentRouteInfoOverlayRef.current = null;
            onInfoWindowStateChange?.(false);
          }
        };
        let markerHovered = false;
        const clearDayVehicleHideTimeout = () => {
          const timeoutId = dayVehicleInfoWindowHideTimeoutRef.current.get(ambulanceId);
          if (timeoutId != null) {
            window.clearTimeout(timeoutId);
            dayVehicleInfoWindowHideTimeoutRef.current.delete(ambulanceId);
          }
        };
        const hideDayVehicleInfoWindow = () => {
          closeInfoWindow(infoWindow);
          onInfoWindowStateChange?.(false);
          if (
            routeViewModeRef.current === ROUTE_VIEW_MODE.DAY &&
            !dayRouteIsPlayingRef?.current &&
            selectedAmbulanceRef.current
          ) {
            const sel = selectedAmbulanceRef.current;
            const selId = sel._id || (sel as any).id || sel.metadata?.name;
            if (String(selId) === String(ambulanceId)) {
              window.requestAnimationFrame(() => showDayRouteIdleCompactOverlayForSelected());
            }
          }
        };
        const scheduleDayVehicleHide = () => {
          clearDayVehicleHideTimeout();
          const timeoutId = window.setTimeout(() => {
            const isHoveringInfo = dayVehicleInfoWindowHoveredRef.current.get(ambulanceId) === true;
            if (!markerHovered && !isHoveringInfo) {
              hideDayVehicleInfoWindow();
            }
            dayVehicleInfoWindowHideTimeoutRef.current.delete(ambulanceId);
          }, DAY_ROUTE_VEHICLE_INFO_HIDE_DELAY_MS);
          dayVehicleInfoWindowHideTimeoutRef.current.set(ambulanceId, timeoutId);
        };

        marker.addListener('click', () => {
          // Close all other info windows
          newInfoWindows.forEach(closeInfoWindow);

          if (routeViewModeRef.current === ROUTE_VIEW_MODE.CURRENT && selectedAmbulanceRef.current) {
            // Use custom overlay for Current Route ambulance info
            showCurrentRouteOverlay();
          } else {
            if (
              routeViewModeRef.current === ROUTE_VIEW_MODE.DAY &&
              !dayRouteIsPlayingRef?.current &&
              selectedAmbulanceRef.current
            ) {
              const sel = selectedAmbulanceRef.current;
              const selId = sel._id || (sel as any).id || sel.metadata?.name;
              if (String(selId) === String(ambulanceId)) {
                hideDayRouteIdleCompactOverlay();
              }
            }
            // Default: use Google Maps InfoWindow
            infoWindow.open(map, marker);
            onInfoWindowStateChange?.(true);
          }
        });

        // Show info for all markers on hover, hide on mouse out
        marker.addListener('mouseover', () => {
          markerHovered = true;
          clearDayVehicleHideTimeout();
          // Close other info windows so only one is visible
          newInfoWindows.forEach(closeInfoWindow);

          if (routeViewModeRef.current === ROUTE_VIEW_MODE.CURRENT && selectedAmbulanceRef.current) {
            showCurrentRouteOverlay();
          } else {
            if (
              routeViewModeRef.current === ROUTE_VIEW_MODE.DAY &&
              !dayRouteIsPlayingRef?.current &&
              selectedAmbulanceRef.current
            ) {
              const sel = selectedAmbulanceRef.current;
              const selId = sel._id || (sel as any).id || sel.metadata?.name;
              if (String(selId) === String(ambulanceId)) {
                hideDayRouteIdleCompactOverlay();
              }
            }
            infoWindow.open(map, marker);
            onInfoWindowStateChange?.(true);
          }
        });

        marker.addListener('mouseout', () => {
          markerHovered = false;
          // In Current Route, keep selected ambulance's info overlay always visible (don't hide on mouseout)
          if (routeViewModeRef.current === ROUTE_VIEW_MODE.CURRENT && selectedAmbulanceRef.current && ambulanceId === (selectedAmbulanceRef.current._id || (selectedAmbulanceRef.current as any).id || selectedAmbulanceRef.current.metadata?.name)) {
            return;
          }
          if (routeViewModeRef.current === ROUTE_VIEW_MODE.CURRENT && selectedAmbulanceRef.current) {
            hideCurrentRouteOverlay();
            // Restore selected ambulance's overlay so it stays always visible
            showSelectedAmbulanceOverlayRef.current?.();
          } else if (routeViewModeRef.current === ROUTE_VIEW_MODE.DAY) {
            scheduleDayVehicleHide();
          } else {
            closeInfoWindow(infoWindow);
            onInfoWindowStateChange?.(false);
          }
        });

        (infoWindow as any).addListener('domready', () => {
          if (routeViewModeRef.current !== ROUTE_VIEW_MODE.DAY) return;
          const existingCleanup = dayVehicleInfoWindowCleanupRef.current.get(ambulanceId);
          if (existingCleanup) {
            existingCleanup();
            dayVehicleInfoWindowCleanupRef.current.delete(ambulanceId);
          }
          // Google Maps renders InfoWindow DOM outside React; bind hover handlers to the latest rendered content.
          const infoWindowNodes = Array.from(document.querySelectorAll('.gm-style-iw, .gm-style-iw-d')) as HTMLElement[];
          const targetNode = infoWindowNodes[infoWindowNodes.length - 1];
          if (!targetNode) return;
          // Hide Google Maps default close button for day-route vehicle info window.
          const infoWindowContainer = targetNode.closest('.gm-style-iw-c') as HTMLElement | null;
          const closeButton = infoWindowContainer?.querySelector('button.gm-ui-hover-effect') as HTMLElement | null;
          if (closeButton) {
            closeButton.style.display = 'none';
          }
          const onMouseEnter = () => {
            dayVehicleInfoWindowHoveredRef.current.set(ambulanceId, true);
            clearDayVehicleHideTimeout();
          };
          const onMouseLeave = () => {
            dayVehicleInfoWindowHoveredRef.current.set(ambulanceId, false);
            if (!markerHovered) {
              hideDayVehicleInfoWindow();
            }
          };
          targetNode.addEventListener('mouseenter', onMouseEnter);
          targetNode.addEventListener('mouseleave', onMouseLeave);
          dayVehicleInfoWindowCleanupRef.current.set(ambulanceId, () => {
            targetNode.removeEventListener('mouseenter', onMouseEnter);
            targetNode.removeEventListener('mouseleave', onMouseLeave);
          });
        });

        (infoWindow as any).addListener('closeclick', () => {
          clearDayVehicleHideTimeout();
          dayVehicleInfoWindowHoveredRef.current.delete(ambulanceId);
          const cleanup = dayVehicleInfoWindowCleanupRef.current.get(ambulanceId);
          if (cleanup) {
            cleanup();
            dayVehicleInfoWindowCleanupRef.current.delete(ambulanceId);
          }
          onInfoWindowStateChange?.(false);
          if (
            routeViewModeRef.current === ROUTE_VIEW_MODE.DAY &&
            !dayRouteIsPlayingRef?.current &&
            selectedAmbulanceRef.current
          ) {
            const sel = selectedAmbulanceRef.current;
            const selId = sel._id || (sel as any).id || sel.metadata?.name;
            if (String(selId) === String(ambulanceId)) {
              window.requestAnimationFrame(() => showDayRouteIdleCompactOverlayForSelected());
            }
          }
        });

        currentMarkersMap.set(ambulanceId, { marker, infoWindow });
        newMarkers.push(marker);
        newInfoWindows.push(infoWindow);
      }
    });

    // Remove markers for ambulances that no longer exist
    const markersToRemove: string[] = [];
    currentMarkersMap.forEach((data, ambulanceId) => {
      if (!newAmbulanceIds.has(ambulanceId)) {
        markersChanged = true;
        const sel = selectedAmbulanceRef.current;
        const selId = sel?._id || (sel as any)?.id || sel?.metadata?.name;
        if (selId != null && String(selId) === String(ambulanceId)) {
          hideDayRouteIdleCompactOverlay();
        }
        data.marker.setMap(null);
        closeInfoWindow(data.infoWindow);
        const timeoutId = dayVehicleInfoWindowHideTimeoutRef.current.get(ambulanceId);
        if (timeoutId != null) {
          window.clearTimeout(timeoutId);
        }
        dayVehicleInfoWindowHideTimeoutRef.current.delete(ambulanceId);
        dayVehicleInfoWindowHoveredRef.current.delete(ambulanceId);
        const cleanup = dayVehicleInfoWindowCleanupRef.current.get(ambulanceId);
        if (cleanup) cleanup();
        dayVehicleInfoWindowCleanupRef.current.delete(ambulanceId);
        markersToRemove.push(ambulanceId);
        markerIconUrlRef.current.delete(ambulanceId);
      }
    });
    markersToRemove.forEach((id) => currentMarkersMap.delete(id));

    setMarkers(newMarkers);
    setInfoWindows(newInfoWindows);

    // In Current Route tab with selected ambulance: show info overlay always (like day-route playback)
    if (routeViewMode === ROUTE_VIEW_MODE.CURRENT && selectedAmbulance && map) {
      hideDayRouteIdleCompactOverlay();
      const selId = selectedAmbulance._id || (selectedAmbulance as any).id || selectedAmbulance.metadata?.name;
      const markerData = selId ? currentMarkersMap.get(selId) : undefined;
      if (markerData) {
        const pos = markerData.marker.getPosition();
        if (pos) {
          const latestAmb = pickAmbulanceForLiveInfoOverlay(
            selectedAmbulance,
            ambulancesWithLocationRef.current.find((a: any) => (a.ambulanceId || a.id) === selId),
            allAmbulancesRef.current,
            liveLocationsRef.current
          );
          const existingOverlay = currentRouteInfoOverlayRef.current as PlaybackInfoOverlayInstance | null;
          if (existingOverlay) {
            existingOverlay.setMap(null);
            currentRouteInfoOverlayRef.current = null;
          }
          const overlay = createDayRoutePlaybackInfoOverlay(
            pos,
            createCurrentRouteInfoWindowContent(latestAmb)
          );
          currentRouteInfoOverlayRef.current = overlay;
          overlay.setMap(map);
          onInfoWindowStateChange?.(true);
          // Store restore function so mouseout from another marker can re-show selected ambulance's overlay
          showSelectedAmbulanceOverlayRef.current = () => {
            if (!map || routeViewModeRef.current !== ROUTE_VIEW_MODE.CURRENT || !selectedAmbulanceRef.current) return;
            const data = markersMapRef.current.get(selId!);
            if (!data) return;
            const p = data.marker.getPosition();
            if (!p) return;
            const amb = pickAmbulanceForLiveInfoOverlay(
              selectedAmbulanceRef.current,
              ambulancesWithLocationRef.current.find((a: any) => (a.ambulanceId || a.id) === selId),
              allAmbulancesRef.current,
              liveLocationsRef.current
            );
            const existing = currentRouteInfoOverlayRef.current;
            if (existing) {
              existing.setMap(null);
              currentRouteInfoOverlayRef.current = null;
            }
            const ov = createDayRoutePlaybackInfoOverlay(p, createCurrentRouteInfoWindowContent(amb));
            currentRouteInfoOverlayRef.current = ov;
            ov.setMap(map);
            onInfoWindowStateChange?.(true);
          };
        }
      }
    } else if (
      routeViewMode === ROUTE_VIEW_MODE.DAY &&
      selectedAmbulance &&
      map &&
      !dayRouteIsPlaying &&
      dayRouteHistoryDate.trim() === formatLocalDateYMD(new Date())
    ) {
      showSelectedAmbulanceOverlayRef.current = null;
      const selId = selectedAmbulance._id || (selectedAmbulance as any).id || selectedAmbulance.metadata?.name;
      const markerData = selId ? currentMarkersMap.get(selId) : undefined;
      if (markerData) {
        const pos = markerData.marker.getPosition();
        if (pos) {
          const latestAmb = pickAmbulanceForLiveInfoOverlay(
            selectedAmbulance,
            ambulancesWithLocationRef.current.find((a: any) => (a.ambulanceId || a.id) === selId),
            allAmbulancesRef.current,
            liveLocationsRef.current
          );
          hideDayRouteIdleCompactOverlay();
          const overlay = createDayRoutePlaybackInfoOverlay(
            pos,
            createCurrentRouteInfoWindowContent(latestAmb)
          );
          dayRouteIdleCompactOverlayRef.current = overlay;
          overlay.setMap(map);
          onInfoWindowStateChange?.(true);
        } else {
          hideDayRouteIdleCompactOverlay();
        }
      } else {
        hideDayRouteIdleCompactOverlay();
      }
    } else {
      hideDayRouteIdleCompactOverlay();
      showSelectedAmbulanceOverlayRef.current = null;
    }

    // Only recreate clusterer if markers were added or removed
    if (markersChanged) {
      // Clear existing clusterer
      if (markerClustererRef.current) {
        markerClustererRef.current.clearMarkers();
        markerClustererRef.current = null;
      }

      // Create new clusterer only if we have markers
      const MarkerClustererClass = (window as any).markerClusterer?.MarkerClusterer;
      if (MarkerClustererClass && newMarkers.length > 0) {
        const clusterer = new MarkerClustererClass({
          map,
          markers: newMarkers,
          renderer: clusterRenderer,
        });
        markerClustererRef.current = clusterer;
      }
    }
    // If markers only moved positions, the clusterer will automatically handle it

    // On Current Route tab never fit from markers (only one-time fit from route effect runs)
    const isCurrentRoute = routeViewMode === ROUTE_VIEW_MODE.CURRENT;
    if (newMarkers.length > 0 && shouldFitBoundsRef?.current && !isCurrentRoute) {
      const bounds = new google.maps.LatLngBounds();
      newMarkers.forEach((marker) => {
        const position = marker.getPosition();
        if (position) bounds.extend(position);
      });
      map.fitBounds(bounds, 50);
      if (shouldFitBoundsRef) shouldFitBoundsRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    ambulancesWithLocation,
    isDark,
    closeInfoWindow,
    ambulanceTypes,
    allAmbulances,
    defaultAmbulanceIcon,
    clusterRenderer,
    createInfoWindowContent,
    createCurrentRouteInfoWindowContent,
    findFullAmbulance,
    getAmbulanceIconUrl,
    createImageIcon,
    routeViewMode,
    selectedAmbulance,
    dayRouteHistoryDate,
    dayRouteIsPlaying,
    currentRouteInfoOverlayRef,
    dayRouteIsPlayingRef,
  ]);

  // Draw route polylines and markers when routeBookings, locationHistoryRoute, or currentRoute change
  useEffect(() => {
    if (!map || !window.google || !window.google.maps || !selectedAmbulance) {
      // Clear existing polylines, route markers (patient/hospital icons), day-route start/end, and directions when no ambulance selected
      polylines.forEach((polyline) => polyline.setMap(null));
      setPolylines([]);
      routeMarkers.forEach((marker) => marker.setMap(null));
      routeMarkersRef.current.forEach((m) => m.setMap(null));
      routeMarkersRef.current = [];
      pickupHospitalMarkersRef.current.forEach((m) => m.setMap(null));
      pickupHospitalMarkersRef.current = [];
      dayRouteStartEndMarkersRef.current.forEach((m) => m.setMap(null));
      dayRouteStartEndMarkersRef.current = [];
      setRouteMarkers([]);
      const existingStartOverlayNoAmbulance = startDayRouteOverlayRef.current as PlaybackInfoOverlayInstance | null;
      if (existingStartOverlayNoAmbulance) {
        existingStartOverlayNoAmbulance.setMap(null);
        startDayRouteOverlayRef.current = null;
      }
      const existingEndOverlayNoAmbulance = endDayRouteOverlayRef.current as PlaybackInfoOverlayInstance | null;
      if (existingEndOverlayNoAmbulance) {
        existingEndOverlayNoAmbulance.setMap(null);
        endDayRouteOverlayRef.current = null;
      }
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
      }
      // Clear any current-route info overlay when no ambulance is selected
      if (currentRouteInfoOverlayRef.current) {
        currentRouteInfoOverlayRef.current.setMap(null);
        currentRouteInfoOverlayRef.current = null;
      }
      if (dayRouteIdleCompactOverlayRef.current) {
        dayRouteIdleCompactOverlayRef.current.setMap(null);
        dayRouteIdleCompactOverlayRef.current = null;
      }
      return;
    }

    // Detect whether this effect ran only because the dayRoutePlaybackIndex changed
    const prevState = prevRouteEffectStateRef.current;
    const routeDataChanged =
      !prevState ||
      prevState.selectedAmbulance !== selectedAmbulance ||
      prevState.routeBookings !== routeBookings ||
      prevState.locationHistoryRoute !== locationHistoryRoute ||
      prevState.currentRoute !== currentRoute ||
      prevState.routeViewMode !== routeViewMode ||
      prevState.liveLocations !== liveLocations ||
      prevState.dayRouteIsPlaying !== dayRouteIsPlaying ||
      prevState.dayRouteHistoryDate !== dayRouteHistoryDate;
    const playbackIndexChanged =
      !!prevState && prevState.dayRoutePlaybackIndex !== dayRoutePlaybackIndex;

    prevRouteEffectStateRef.current = {
      selectedAmbulance,
      routeBookings,
      locationHistoryRoute,
      currentRoute,
      routeViewMode,
      dayRoutePlaybackIndex,
      liveLocations,
      dayRouteIsPlaying,
      dayRouteHistoryDate,
    };

    // If only playback index changed (no route data change) and the playback marker already exists,
    // skip re-drawing the static route and start/end overlays to avoid overlay flicker on hover.
    if (!routeDataChanged && playbackIndexChanged && playbackMarkerRef.current) {
      // If playback was reset, clear the playback marker/overlay but keep static overlays intact.
      if (dayRoutePlaybackIndex < 0) {
        const existingPlaybackMarker = playbackMarkerRef.current as google.maps.Marker | null;
        if (existingPlaybackMarker) {
          existingPlaybackMarker.setMap(null);
          playbackMarkerRef.current = null;
        }
        const existingPlaybackOverlay =
          playbackOverlayRef.current as PlaybackInfoOverlayInstance | null;
        if (existingPlaybackOverlay) {
          existingPlaybackOverlay.setMap(null);
          playbackOverlayRef.current = null;
        }
      }
      // For normal playback index increments, per-frame effects will move the marker/overlay.
      return;
    }

    const isCurrentRouteTab = routeViewMode === ROUTE_VIEW_MODE.CURRENT;
    const isDayRouteTab = routeViewMode === ROUTE_VIEW_MODE.DAY;

    // Compute current-route stops key (for same-stops optimization when on Current tab)
    let currentRouteStopsKey: string | null = null;
    const cr = currentRoute as { origin?: { lat: number; lng: number }; destination?: { lat: number; lng: number }; stops?: { lat: number; lng: number }[] } | null | undefined;
    if (cr && typeof cr === 'object' && !Array.isArray(cr) && cr.origin && cr.destination) {
      const stops = cr.stops?.length ? cr.stops : [{ lat: cr.destination.lat, lng: cr.destination.lng }];
      currentRouteStopsKey = JSON.stringify(stops.map((s) => [s.lat, s.lng]));
    }
    const sameCurrentRouteStops = isCurrentRouteTab && currentRouteStopsKey !== null && currentRouteStopsKey === lastCurrentRouteStopsKeyRef.current;
    if (!currentRoute) lastCurrentRouteStopsKeyRef.current = null;

    // Clear polylines and non-reused route markers (avoid clearing pickup/hospital so they don't flicker).
    polylines.forEach((polyline) => polyline.setMap(null));
    const keepPickupHospital = (isCurrentRouteTab && sameCurrentRouteStops) ? new Set(pickupHospitalMarkersRef.current) : new Set<google.maps.Marker>();
    routeMarkersRef.current.forEach((m) => {
      if (!keepPickupHospital.has(m)) m.setMap(null);
    });
    routeMarkersRef.current = [];
    if (!isCurrentRouteTab || !sameCurrentRouteStops) {
      pickupHospitalMarkersRef.current.forEach((m) => m.setMap(null));
      pickupHospitalMarkersRef.current = [];
    } else {
      routeMarkersRef.current = pickupHospitalMarkersRef.current.slice();
      setRouteMarkers(routeMarkersRef.current);
    }
    // Clear any playback marker/overlay when switching ambulance/route
    if (playbackMarkerRef.current) {
      playbackMarkerRef.current.setMap(null);
      playbackMarkerRef.current = null;
    }
    if (playbackOverlayRef.current) {
      playbackOverlayRef.current.setMap(null);
      playbackOverlayRef.current = null;
    }
    const existingStartOverlay = startDayRouteOverlayRef.current as PlaybackInfoOverlayInstance | null;
    if (existingStartOverlay) {
      existingStartOverlay.setMap(null);
      startDayRouteOverlayRef.current = null;
    }
    const existingEndOverlay = endDayRouteOverlayRef.current as PlaybackInfoOverlayInstance | null;
    if (existingEndOverlay) {
      existingEndOverlay.setMap(null);
      endDayRouteOverlayRef.current = null;
    }
    dayRouteStartEndMarkersRef.current.forEach((m) => m.setMap(null));
    dayRouteStartEndMarkersRef.current = [];

    const newPolylines: google.maps.Polyline[] = [];
    const newRouteMarkers: google.maps.Marker[] = [];
    const bounds = new google.maps.LatLngBounds();

    // Current Route tab only: patient_icon + hospital_icon (pickup/dropoff). No start/end markers.
    // Skip drawing when origin/destination are placeholder (0,0) to avoid showing marker at Gulf of Guinea.
    const routeOrigin = currentRoute && typeof currentRoute === 'object' && 'origin' in currentRoute ? (currentRoute as any).origin : null;
    const routeDest = currentRoute && typeof currentRoute === 'object' && 'destination' in currentRoute ? (currentRoute as any).destination : null;
    const isValidRouteCoord = (lat: number, lng: number) => (lat !== 0 || lng !== 0) && !isNaN(lat) && !isNaN(lng);
    const hasValidCurrentRouteCoords = routeOrigin && routeDest && isValidRouteCoord(routeOrigin.lat, routeOrigin.lng) && isValidRouteCoord(routeDest.lat, routeDest.lng);

    if (isCurrentRouteTab && currentRoute && typeof currentRoute === 'object' && 'origin' in currentRoute && 'destination' in currentRoute && currentRoute.origin && currentRoute.destination && hasValidCurrentRouteCoords) {

      const routeData = currentRoute as {
        origin: { lat: number; lng: number };
        destination: { lat: number; lng: number };
        destinationType: 'pickup' | 'dropoff';
        stops?: RouteStop[];
        bookingType?: BookingType;
      };

      const orderedStops: RouteStop[] =
        routeData.stops && routeData.stops.length > 0
          ? routeData.stops
          : [
            {
              lat: routeData.destination.lat,
              lng: routeData.destination.lng,
              type: routeData.destinationType === 'dropoff' ? 'hospital' : 'patient',
              label: routeData.destinationType === 'dropoff' ? 'Dropoff' : 'Pickup',
            },
          ];

      // Reuse existing pickup/hospital markers when stops unchanged to avoid flicker and keep info windows open
      const shouldReuseMarkers = sameCurrentRouteStops && pickupHospitalMarkersRef.current.length === orderedStops.length;
      if (shouldReuseMarkers) {
        pickupHospitalMarkersRef.current.forEach((m) => {
          m.setMap(map); // re-attach to map (they were removed by routeMarkers.forEach(setMap(null)) at start of effect)
          newRouteMarkers.push(m);
          const pos = m.getPosition();
          if (pos) bounds.extend(pos);
        });
        routeMarkersRef.current = newRouteMarkers;
        setRouteMarkers(newRouteMarkers);
        if (currentRouteStopsKey !== null) lastCurrentRouteStopsKeyRef.current = currentRouteStopsKey;
      } else {
        pickupHospitalMarkersRef.current.forEach((m) => m.setMap(null));
        pickupHospitalMarkersRef.current = [];
        const currentRouteBookingStatus = (routeData as any).bookingStatus || 'pending';
        const currentRouteIsPickupDone = ['in_transit', 'patient_picked', 'patient_dropped', 'completed'].includes(currentRouteBookingStatus);
        const currentRouteBookingType = routeData.bookingType || 'to_hospital';
        orderedStops.forEach((stop) => {
          const stopLatLng = new window.google.maps.LatLng(stop.lat, stop.lng);
          let stopIconUrl: string;
          let stopColor: string;
          if (stop.type === 'hospital') {
            stopIconUrl = HOSPITAL_ICON_URL;
            stopColor = '#EF4444';
          } else if (stop.type === 'patient') {
            stopIconUrl = PATIENT_ICON_URL;
            stopColor = '#10B981';
          } else {
            if (currentRouteBookingType === 'to_hospital') {
              stopIconUrl = (stop.label === 'Pickup' || stop.label?.includes('Pickup') || !currentRouteIsPickupDone) ? PATIENT_ICON_URL : HOSPITAL_ICON_URL;
              stopColor = stopIconUrl === PATIENT_ICON_URL ? '#10B981' : '#EF4444';
            } else {
              stopIconUrl = (stop.label === 'Pickup' || stop.label?.includes('Pickup') || !currentRouteIsPickupDone) ? HOSPITAL_ICON_URL : PATIENT_ICON_URL;
              stopColor = stopIconUrl === HOSPITAL_ICON_URL ? '#EF4444' : '#10B981';
            }
          }
          const stopIcon = createImageIcon(stopIconUrl, 36);
          const stopMarker = new google.maps.Marker({
            position: stopLatLng,
            map,
            icon: stopIcon,
            title: stop.label,
          });
          const infoWindow = new google.maps.InfoWindow({
            content: `<div style="padding: 8px; color: ${isDark ? '#f1f5f9' : '#0f172a'};">
              <strong style="color: ${stopColor};">${stop.label}</strong><br/>
              Latitude: ${stop.lat.toFixed(6)}<br/>
              Longitude: ${stop.lng.toFixed(6)}
            </div>`,
          });

          // Show stop info on hover and hide on mouse out
          stopMarker.addListener('mouseover', () => {
            infoWindow.open(map, stopMarker);
            onInfoWindowStateChange?.(true);
          });
          stopMarker.addListener('mouseout', () => {
            closeInfoWindow(infoWindow);
            onInfoWindowStateChange?.(false);
          });
          newRouteMarkers.push(stopMarker);
          pickupHospitalMarkersRef.current.push(stopMarker);
          bounds.extend(stopLatLng);
        });
        routeMarkersRef.current = newRouteMarkers;
        setRouteMarkers(newRouteMarkers);
        if (currentRouteStopsKey !== null) lastCurrentRouteStopsKeyRef.current = currentRouteStopsKey;
      }

      // Directions Service (route line + ETA)
      if (!directionsServiceRef.current && window.google && window.google.maps) {
        directionsServiceRef.current = new (window.google.maps as any).DirectionsService();
      }
      const bookingStatus = (currentRoute as any).bookingStatus || 'pending';
      const isPickupDone = bookingStatus === 'patient_picked' || bookingStatus === 'patient_dropped';
      const routeColor = isPickupDone ? '#10B981' : '#3b82f6';
      if (!directionsRendererRef.current && window.google && window.google.maps) {
        directionsRendererRef.current = new (window.google.maps as any).DirectionsRenderer({
          map,
          suppressMarkers: true,
          preserveViewport: true, // do not zoom/pan when setDirections is called (e.g. on 5s ETA refresh)
          polylineOptions: { strokeColor: routeColor, strokeOpacity: 0.9, strokeWeight: 5 },
        });
      } else if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(map);
        if (directionsRendererRef.current.setOptions) {
          directionsRendererRef.current.setOptions({
            polylineOptions: { strokeColor: routeColor, strokeOpacity: 0.9, strokeWeight: 5 },
          });
        }
      }

      const destinationStop = orderedStops[orderedStops.length - 1];
      const waypointStops = orderedStops.slice(0, -1);
      // When patient picked up (arrived), route is ambulance → dropoff only; do not include pickup as waypoint
      const isArrived = ['patient_picked', 'patient_dropped'].includes((currentRoute as any)?.bookingStatus);
      const requestOrigin = new window.google.maps.LatLng(routeData.origin.lat, routeData.origin.lng);
      const requestDestination = new window.google.maps.LatLng(destinationStop.lat, destinationStop.lng);
      const requestWaypoints = isArrived ? [] : waypointStops.map((stop) => ({
        location: new window.google.maps.LatLng(stop.lat, stop.lng),
        stopover: true,
      }));
      // Only call Directions API when origin/destination/waypoints change to avoid infinite script injection
      const directionsRequestKey = JSON.stringify({
        origin: [routeData.origin.lat, routeData.origin.lng],
        destination: [destinationStop.lat, destinationStop.lng],
        waypoints: isArrived ? [] : waypointStops.map((s) => [s.lat, s.lng]),
      });
      if (lastDirectionsRequestKeyRef.current !== directionsRequestKey && directionsServiceRef.current && window.google && window.google.maps) {
        lastDirectionsRequestKeyRef.current = directionsRequestKey;
        directionsServiceRef.current.route(
          {
            origin: requestOrigin,
            destination: requestDestination,
            waypoints: requestWaypoints,
            travelMode: (window.google.maps as any).TravelMode?.DRIVING || 'DRIVING',
          },
          (result: any, status: any) => {
            if (status === ((window.google.maps as any).DirectionsStatus?.OK || 'OK') && result) {
              // If user unselected ambulance while request was in flight, do not add route or markers
              if (!selectedAmbulanceRef.current) {
                if (directionsRendererRef.current) directionsRendererRef.current.setMap(null);
                return;
              }
              // Extract ETA from legs (duration.value is seconds) and notify parent
              const legs = result?.routes?.[0]?.legs;
              if (onRouteEta && Array.isArray(legs) && legs.length > 0) {
                if (legs.length === 1) {
                  const minutes = Math.round(legs[0].duration.value / 60);
                  if (routeData.destinationType === 'pickup') {
                    onRouteEta({ pickupEtaMinutes: minutes });
                  } else {
                    onRouteEta({ dropoffEtaMinutes: minutes });
                  }
                } else {
                  onRouteEta({
                    pickupEtaMinutes: Math.round(legs[0].duration.value / 60),
                    dropoffEtaMinutes: Math.round(legs[1].duration.value / 60),
                  });
                }
              }

              // Render the route (patient/hospital markers were already added synchronously above)
              directionsRendererRef.current?.setDirections(result);

              // Extend bounds to include the route
              if (result.routes[0]?.bounds) {
                const routeBounds = result.routes[0].bounds;
                bounds.extend(routeBounds.getNorthEast());
                bounds.extend(routeBounds.getSouthWest());
              }

              routeMarkersRef.current = newRouteMarkers;
              setRouteMarkers(newRouteMarkers);
            } else {
              console.error('[Tracking] LiveMap: Directions Service failed', { status, result });
              // Fallback: clear directions renderer
              if (directionsRendererRef.current) {
                directionsRendererRef.current.setMap(null);
              }
            }
          }
        );
      }
    } else {
      // Clear directions renderer if no route
      lastDirectionsRequestKeyRef.current = null;
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
      }
    }

    // Day Route tab only: start marker always; end marker only while playing. No patient/hospital icons.
    if (isDayRouteTab && locationHistoryRoute && locationHistoryRoute.length > 0) {
      const historyPath: google.maps.LatLng[] = [];
      let firstPointDate: string | null = null;

      locationHistoryRoute.forEach((point: any, index: number) => {
        let lat: number | null = null;
        let lng: number | null = null;

        // Handle different possible formats
        if (Array.isArray(point)) {
          // Legacy format: [latitude, longitude] (already converted from API's [lng, lat] format)
          if (point.length >= 2) {
            lat = point[0];
            lng = point[1];
          }
        } else if (typeof point === 'object') {
          // New format: { lat, lng, date } or { latitude, longitude }
          lat = point.latitude || point.lat;
          lng = point.longitude || point.lng;

          // Capture date from first point for info window (API provides 'date' field)
          if (index === 0 && point.date) {
            firstPointDate = point.date;
          }
        }

        if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
          // Validate coordinates are within valid ranges
          if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            historyPath.push(new google.maps.LatLng(lat, lng));
          }
        }
      });

      const displayPath = historyPath.slice();
      const dayRouteIsToday =
        dayRouteHistoryDate.trim() === formatLocalDateYMD(new Date());
      if (
        !dayRouteIsPlaying &&
        dayRouteIsToday &&
        selectedAmbulance &&
        liveLocations?.length
      ) {
        const gpsId = selectedAmbulance.spec?.gpsDevice?.deviceId;
        const liveRow = liveLocations.find((loc: any) => loc.deviceId === gpsId);
        const liveLoc = liveRow?.liveLocation;
        if (Array.isArray(liveLoc) && liveLoc.length === 2) {
          const [lng, lat] = liveLoc;
          if (lng !== 0 && lat !== 0 && !isNaN(lng) && !isNaN(lat)) {
            const last = displayPath[displayPath.length - 1];
            if (!last || Math.abs(last.lat() - lat) > 1e-6 || Math.abs(last.lng() - lng) > 1e-6) {
              displayPath.push(new google.maps.LatLng(lat, lng));
            }
          }
        }
      }

      if (displayPath.length > 0) {
        dayRoutePathRef.current = displayPath;
        const polyline = new google.maps.Polyline({
          path: displayPath,
          geodesic: true,
          strokeColor: '#3b82f6',
          strokeOpacity: 0.8,
          strokeWeight: 4,
          map,
        });

        newPolylines.push(polyline);

        displayPath.forEach((point) => bounds.extend(point));

        const startPoint = displayPath[0];
        const startIcon: google.maps.Icon = {
          url: START_ROUTE_ICON_URL,
          scaledSize: new google.maps.Size(48, 48),
          anchor: new google.maps.Point(27, 45),
        };
        const startMarker = new google.maps.Marker({
          position: startPoint,
          map,
          icon: startIcon,
          title: 'Start of Day Route',
        });

        // Format timing for display
        let timingDisplay = 'Time not available';
        if (firstPointDate) {
          try {
            const date = new Date(firstPointDate);
            if (!isNaN(date.getTime())) {
              timingDisplay = date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true,
              });
            }
          } catch (e) {
            // If date parsing fails, use the raw value
            timingDisplay = firstPointDate;
          }
        }

        // Build info content with same style as ambulance playback overlay (dark box, compact, speech-bubble tail)
        const textColor = isDark ? '#f1f5f9' : '#0f172a';
        const bgColor = isDark ? '#1e293b' : '#ffffff';

        const startInfoContent =
          '<div style="display:flex;flex-direction:column;align-items:center;">' +
          '<div style="color: ' + textColor + '; background-color: ' + bgColor + '; font-size: 14px; width: max-content; max-width: 240px; padding: 12px 14px; border-radius: 8px; box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);">' +
          '<div style="margin-bottom: 4px;"><strong style="color: ' + textColor + ';">Start of Day Route</strong></div>' +
          '<div><strong style="color: ' + textColor + ';">' + timingDisplay + '</strong></div>' +
          '</div>' +
          '<div style="width:0;height:0;border-left:10px solid transparent;border-right:10px solid transparent;border-top:10px solid ' + bgColor + ';margin-top:-1px;"></div>' +
          '</div>';

        const existingStartOverlayForPath = startDayRouteOverlayRef.current as PlaybackInfoOverlayInstance | null;
        if (existingStartOverlayForPath) {
          existingStartOverlayForPath.setPosition(startPoint);
          existingStartOverlayForPath.setContent(startInfoContent);
          // Keep hidden until user hovers the marker
          existingStartOverlayForPath.setMap(null);
        } else {
          const startOverlay = createDayRoutePlaybackInfoOverlay(startPoint, startInfoContent);
          // Keep hidden until user hovers the marker
          startOverlay.setMap(null);
          startDayRouteOverlayRef.current = startOverlay;
        }

        // Show start-of-day overlay on hover and hide 800ms after mouse leaves
        startMarker.addListener('mouseover', () => {
          const overlay = startDayRouteOverlayRef.current as PlaybackInfoOverlayInstance | null;
          if (!overlay) return;
          if (startOverlayHideTimeoutRef.current != null) {
            window.clearTimeout(startOverlayHideTimeoutRef.current);
            startOverlayHideTimeoutRef.current = null;
          }
          overlay.setMap(map);
        });

        startMarker.addListener('mouseout', () => {
          const overlay = startDayRouteOverlayRef.current as PlaybackInfoOverlayInstance | null;
          if (!overlay) return;
          if (startOverlayHideTimeoutRef.current != null) {
            window.clearTimeout(startOverlayHideTimeoutRef.current);
          }
          startOverlayHideTimeoutRef.current = window.setTimeout(() => {
            overlay.setMap(null);
            startOverlayHideTimeoutRef.current = null;
          }, 800);
        });

        newRouteMarkers.push(startMarker);
        dayRouteStartEndMarkersRef.current = [startMarker];
        bounds.extend(startPoint);

        if (dayRouteIsPlaying && historyPath.length > 0) {
          const endPoint = historyPath[historyPath.length - 1];
          const lastPointData: any = locationHistoryRoute[locationHistoryRoute.length - 1];
          const endIcon: google.maps.Icon = {
            url: END_ROUTE_ICON_URL,
            scaledSize: new google.maps.Size(48, 48),
            anchor: new google.maps.Point(27, 45),
          };
          const endMarker = new google.maps.Marker({
            position: endPoint,
            map,
            icon: endIcon,
            title: 'End of Day Route',
          });

          let endTimingDisplay = 'Time not available';
          if (lastPointData?.date) {
            try {
              const endDate = new Date(lastPointData.date);
              if (!isNaN(endDate.getTime())) {
                endTimingDisplay = endDate.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true,
                });
              }
            } catch {
              endTimingDisplay = lastPointData.date;
            }
          }

          const endInfoContent =
            '<div style="display:flex;flex-direction:column;align-items:center;">' +
            '<div style="color: ' + textColor + '; background-color: ' + bgColor + '; font-size: 14px; width: max-content; max-width: 240px; padding: 12px 14px; border-radius: 8px; box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);">' +
            '<div style="margin-bottom: 4px;"><strong style="color: ' + textColor + ';">End of Day Route</strong></div>' +
            '<div><strong style="color: ' + textColor + ';">' + endTimingDisplay + '</strong></div>' +
            '</div>' +
            '<div style="width:0;height:0;border-left:10px solid transparent;border-right:10px solid transparent;border-top:10px solid ' + bgColor + ';margin-top:-1px;"></div>' +
            '</div>';

          const existingEndOverlayForPath = endDayRouteOverlayRef.current as PlaybackInfoOverlayInstance | null;
          if (existingEndOverlayForPath) {
            existingEndOverlayForPath.setPosition(endPoint);
            existingEndOverlayForPath.setContent(endInfoContent);
            existingEndOverlayForPath.setMap(null);
          } else {
            const endOverlay = createDayRoutePlaybackInfoOverlay(endPoint, endInfoContent);
            endOverlay.setMap(null);
            endDayRouteOverlayRef.current = endOverlay;
          }

          endMarker.addListener('mouseover', () => {
            const overlay = endDayRouteOverlayRef.current as PlaybackInfoOverlayInstance | null;
            if (!overlay) return;
            if (endOverlayHideTimeoutRef.current != null) {
              window.clearTimeout(endOverlayHideTimeoutRef.current);
              endOverlayHideTimeoutRef.current = null;
            }
            overlay.setMap(map);
          });

          endMarker.addListener('mouseout', () => {
            const overlay = endDayRouteOverlayRef.current as PlaybackInfoOverlayInstance | null;
            if (!overlay) return;
            if (endOverlayHideTimeoutRef.current != null) {
              window.clearTimeout(endOverlayHideTimeoutRef.current);
            }
            endOverlayHideTimeoutRef.current = window.setTimeout(() => {
              overlay.setMap(null);
              endOverlayHideTimeoutRef.current = null;
            }, 800);
          });

          newRouteMarkers.push(endMarker);
          dayRouteStartEndMarkersRef.current = [dayRouteStartEndMarkersRef.current[0], endMarker];
          bounds.extend(endPoint);
        } else {
          const existingEndOverlayIdle = endDayRouteOverlayRef.current as PlaybackInfoOverlayInstance | null;
          if (existingEndOverlayIdle) {
            existingEndOverlayIdle.setMap(null);
            endDayRouteOverlayRef.current = null;
          }
        }

        if (dayRoutePlaybackIndex < 0) {
          const existingPlaybackMarker = playbackMarkerRef.current as google.maps.Marker | null;
          if (existingPlaybackMarker) {
            existingPlaybackMarker.setMap(null);
            playbackMarkerRef.current = null;
          }
          const existingPlaybackOverlay = playbackOverlayRef.current as PlaybackInfoOverlayInstance | null;
          if (existingPlaybackOverlay) {
            existingPlaybackOverlay.setMap(null);
            playbackOverlayRef.current = null;
          }
        } else if (dayRoutePlaybackIndex < historyPath.length && dayRoutePlaybackIndex < locationHistoryRoute.length) {
          const idx = dayRoutePlaybackIndex;
          const playbackPoint: any = locationHistoryRoute[idx];
          const _playbackCum = cumulativeDistancesKm(locationHistoryRoute.slice(0, idx + 1));
          const distanceKm = _playbackCum.length > 0
            ? `${_playbackCum[_playbackCum.length - 1].toFixed(2)} km`
            : '—';
          let timeStr = formatOverlayDateTime(new Date());
          if (playbackPoint?.date) {
            try {
              const d = new Date(playbackPoint.date);
              timeStr = !isNaN(d.getTime())
                ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })
                : formatOverlayDateTime(new Date());
            } catch {
              timeStr = formatOverlayDateTime(new Date());
            }
          }
          const fuelStr = playbackPoint?.fuel != null ? `${Number(playbackPoint.fuel).toFixed(2)} L` : '—';
          const speedStr = formatSpeedDisplay(playbackPoint?.speed, convertKnotsToKmph);

          const playbackInfoContent =
            '<div style="display:flex;flex-direction:column;align-items:center;">' +
            '<div style="color: ' + textColor + '; background-color: ' + bgColor + '; font-size: 14px; width: max-content; max-width: 240px; padding: 12px 14px; border-radius: 8px; box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);">' +
            '<div style="margin-bottom: 4px;"><strong style="color: ' + textColor + ';">' + timeStr + '</strong></div>' +
            '<div style="margin-bottom: 4px;"><em style="color: ' + textColor + ';">Fuel: </em><strong style="color: ' + textColor + ';">' + fuelStr + '</strong></div>' +
            '<div style="margin-bottom: 4px;"><em style="color: ' + textColor + ';">Speed: </em><strong style="color: ' + textColor + ';">' + speedStr + '</strong></div>' +
            '<div><em style="color: ' + textColor + ';">Distance: </em><strong style="color: ' + textColor + ';">' + distanceKm + '</strong></div>' +
            '</div></div>' +
            '<div style="width:0;height:0;border-left:10px solid transparent;border-right:10px solid transparent;border-top:10px solid ' + bgColor + ';margin-top:-1px;"></div>' +
            '</div>';

          const playbackPosition = historyPath[idx];
          const playbackTypeIconUrl = getAmbulanceIconUrl(selectedAmbulance as any, selectedAmbulance);
          const playbackResolvedDefault = resolveIconUrl(AMBULANCE_ICON_URL);
          const playbackIcon: google.maps.Icon =
            playbackTypeIconUrl === playbackResolvedDefault
              ? defaultAmbulanceIcon
              : createImageIcon(playbackTypeIconUrl, 65);

          const existingPlaybackMarker = playbackMarkerRef.current as google.maps.Marker | null;
          const existingPlaybackOverlay = playbackOverlayRef.current as PlaybackInfoOverlayInstance | null;

          if (existingPlaybackMarker && existingPlaybackOverlay) {
            existingPlaybackMarker.setMap(map);
            existingPlaybackMarker.setPosition(playbackPosition);
            (existingPlaybackMarker as google.maps.Marker & { setIcon?(icon: google.maps.Icon | null): void }).setIcon?.(playbackIcon);
            existingPlaybackOverlay.setMap(map);
            existingPlaybackOverlay.setPosition(playbackPosition);
            existingPlaybackOverlay.setContent(playbackInfoContent);
          } else {
            const playbackMarker = new google.maps.Marker({
              position: playbackPosition,
              map,
              icon: playbackIcon,
              title: 'Route playback',
            });
            const playbackOverlay = createDayRoutePlaybackInfoOverlay(playbackPosition, playbackInfoContent);
            playbackOverlay.setMap(map);
            playbackMarkerRef.current = playbackMarker;
            playbackOverlayRef.current = playbackOverlay;
          }

          if (playbackMarkerRef.current) {
            newRouteMarkers.push(playbackMarkerRef.current as google.maps.Marker);
            bounds.extend(playbackPosition);
          }
          onInfoWindowStateChange?.(true);
        }
      } else {
        dayRoutePathRef.current = [];
        console.warn('[Tracking] LiveMap: No valid path points for day route', {
          locationHistoryRouteLength: locationHistoryRoute.length,
        });
      }
    }
    // Patient/hospital icons are shown only in Current Route tab (from currentRoute block above).
    // Start/end markers are shown only in Day Route tab (from locationHistoryRoute block above).
    // No routeBookings-based patient/hospital markers on Day Route.

    setPolylines(newPolylines);
    routeMarkersRef.current = newRouteMarkers;
    setRouteMarkers(newRouteMarkers);

    // Never call fitBounds here when on Current Route (handled by one-time effect below)
    const isCurrentRoute = routeViewMode === ROUTE_VIEW_MODE.CURRENT;
    const mayFit = (newPolylines.length > 0 || newRouteMarkers.length > 0) && shouldFitBoundsRef?.current;
    if (mayFit && !isCurrentRoute) {
      map.fitBounds(bounds, 50);
      if (shouldFitBoundsRef) shouldFitBoundsRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selectedAmbulance, routeBookings, locationHistoryRoute, currentRoute, routeViewMode, dayRoutePlaybackIndex, dayRouteIsPlaying, dayRouteHistoryDate, liveLocations, convertKnotsToKmph]);

  // Update playback marker and custom overlay position/content when progress changes (smooth movement)
  useEffect(() => {
    if (!map || !locationHistoryRoute?.length || dayRoutePlaybackIndex < 0 || !playbackMarkerRef.current || !playbackOverlayRef.current) return;
    const path: google.maps.LatLng[] = [];
    locationHistoryRoute.forEach((point: any, index: number) => {
      let lat: number | null = null;
      let lng: number | null = null;
      if (Array.isArray(point) && point.length >= 2) {
        lat = point[0];
        lng = point[1];
      } else if (typeof point === 'object') {
        lat = point.latitude || point.lat;
        lng = point.longitude || point.lng;
      }
      if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        path.push(new google.maps.LatLng(lat, lng));
      }
    });
    if (path.length === 0) return;
    const idx = dayRoutePlaybackIndex;
    const nextIdx = Math.min(idx + 1, path.length - 1);
    const progress = Math.max(0, Math.min(1, dayRoutePlaybackProgress));
    const p0 = path[idx];
    const p1 = path[nextIdx];
    const lat = p0.lat() + progress * (p1.lat() - p0.lat());
    const lng = p0.lng() + progress * (p1.lng() - p0.lng());
    const interpolatedPosition = new google.maps.LatLng(lat, lng);
    const playbackPoint: any = locationHistoryRoute[idx];
    const _pbCum2 = cumulativeDistancesKm(locationHistoryRoute.slice(0, idx + 1));
    const distanceKm = _pbCum2.length > 0
      ? `${_pbCum2[_pbCum2.length - 1].toFixed(2)} km`
      : '—';
    let timeStr = formatOverlayDateTime(new Date());
    if (playbackPoint?.date) {
      try {
        const d = new Date(playbackPoint.date);
        timeStr = !isNaN(d.getTime())
          ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })
          : formatOverlayDateTime(new Date());
      } catch {
        timeStr = formatOverlayDateTime(new Date());
      }
    }
    const fuelStr = playbackPoint?.fuel != null ? `${Number(playbackPoint.fuel).toFixed(2)} L` : '—';
    const speedStr = formatSpeedDisplay(playbackPoint?.speed, convertKnotsToKmph);
    const textColor = isDark ? '#f1f5f9' : '#0f172a';
    const bgColor = isDark ? '#1e293b' : '#ffffff';
    const content =
      '<div style="display:flex;flex-direction:column;align-items:center;">' +
      '<div style="color: ' + textColor + '; background-color: ' + bgColor + '; font-size: 14px; width: max-content; max-width: 240px; padding: 12px 14px; border-radius: 8px; box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);">' +
      '<div style="margin-bottom: 4px;"><strong style="color: ' + textColor + ';">' + timeStr + '</strong></div>' +
      '<div style="margin-bottom: 4px;"><em style="color: ' + textColor + ';">Fuel: </em><strong style="color: ' + textColor + ';">' + fuelStr + '</strong></div>' +
      '<div style="margin-bottom: 4px;"><em style="color: ' + textColor + ';">Speed: </em><strong style="color: ' + textColor + ';">' + speedStr + '</strong></div>' +
      '<div><em style="color: ' + textColor + ';">Distance: </em><strong style="color: ' + textColor + ';">' + distanceKm + '</strong></div>' +
      '</div></div>' +
      '<div style="width:0;height:0;border-left:10px solid transparent;border-right:10px solid transparent;border-top:10px solid ' + bgColor + ';margin-top:-1px;"></div>' +
      '</div>';
    playbackMarkerRef.current.setPosition(interpolatedPosition);
    playbackOverlayRef.current.setPosition(interpolatedPosition);
    playbackOverlayRef.current.setContent(content);
  }, [map, locationHistoryRoute, dayRoutePlaybackIndex, dayRoutePlaybackProgress, isDark, convertKnotsToKmph]);

  // Per-frame update: move vehicle marker and info overlay together when playing (so both move smoothly every frame)
  const locationHistoryRouteRef = React.useRef(locationHistoryRoute);
  locationHistoryRouteRef.current = locationHistoryRoute;
  React.useEffect(() => {
    if (
      !map ||
      !dayRouteIsPlayingRef?.current ||
      !playbackMarkerRef.current ||
      !playbackOverlayRef.current ||
      !dayRoutePlaybackIndexRef ||
      !dayRoutePlaybackProgressRef
    )
      return;
    const gm = (window as any).google?.maps;
    if (!gm) return;
    const path = dayRoutePathRef.current;
    const routeData = locationHistoryRouteRef.current;
    if (!path?.length || !routeData?.length) return;

    const tick = () => {
      if (
        !dayRouteIsPlayingRef?.current ||
        !playbackMarkerRef.current ||
        !playbackOverlayRef.current
      )
        return;
      const idx = dayRoutePlaybackIndexRef.current;
      const progress = Math.max(0, Math.min(1, dayRoutePlaybackProgressRef.current));
      if (idx < 0 || idx >= path.length) return;
      const nextIdx = Math.min(idx + 1, path.length - 1);
      const p0 = path[idx];
      const p1 = path[nextIdx];
      const lat = p0.lat() + progress * (p1.lat() - p0.lat());
      const lng = p0.lng() + progress * (p1.lng() - p0.lng());
      const interpolatedPosition = new gm.LatLng(lat, lng);

      const playbackPoint: any = routeData[idx] ?? {};
      const _pbCum3 = cumulativeDistancesKm(routeData.slice(0, idx + 1));
      const distanceKm = _pbCum3.length > 0
        ? `${_pbCum3[_pbCum3.length - 1].toFixed(2)} km`
        : '—';
      let timeStr = formatOverlayDateTime(new Date());
      if (playbackPoint?.date) {
        try {
          const d = new Date(playbackPoint.date);
          timeStr = !isNaN(d.getTime())
            ? d.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true,
            })
            : formatOverlayDateTime(new Date());
        } catch {
          timeStr = formatOverlayDateTime(new Date());
        }
      }
      const fuelStr =
        playbackPoint?.fuel != null ? `${Number(playbackPoint.fuel).toFixed(2)} L` : '—';
      const speedStr = formatSpeedDisplay(playbackPoint?.speed, convertKnotsToKmph);
      const textColor = isDark ? '#f1f5f9' : '#0f172a';
      const bgColor = isDark ? '#1e293b' : '#ffffff';
      const content =
        '<div style="display:flex;flex-direction:column;align-items:center;">' +
        '<div style="color: ' +
        textColor +
        '; background-color: ' +
        bgColor +
        '; font-size: 14px; width: max-content; max-width: 240px; padding: 12px 14px; border-radius: 8px; box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);">' +
        '<div style="margin-bottom: 4px;"><strong style="color: ' +
        textColor +
        ';">' +
        timeStr +
        '</strong></div>' +
        '<div style="margin-bottom: 4px;"><em style="color: ' +
        textColor +
        ';">Fuel: </em><strong style="color: ' +
        textColor +
        ';">' +
        fuelStr +
        '</strong></div>' +
        '<div style="margin-bottom: 4px;"><em style="color: ' +
        textColor +
        ';">Speed: </em><strong style="color: ' +
        textColor +
        ';">' +
        speedStr +
        '</strong></div>' +
        '<div><em style="color: ' +
        textColor +
        ';">Distance: </em><strong style="color: ' +
        textColor +
        ';">' +
        distanceKm +
        '</strong></div>' +
        '</div></div>' +
        '<div style="width:0;height:0;border-left:10px solid transparent;border-right:10px solid transparent;border-top:10px solid ' +
        bgColor +
        ';margin-top:-1px;"></div>' +
        '</div>';

      playbackMarkerRef.current.setPosition(interpolatedPosition);
      playbackOverlayRef.current.setPosition(interpolatedPosition);
      playbackOverlayRef.current.setContent(content);
      if (dayRouteIsPlayingRef?.current) {
        playbackRafRef.current = requestAnimationFrame(tick);
      }
    };
    playbackRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (playbackRafRef.current != null) {
        cancelAnimationFrame(playbackRafRef.current);
        playbackRafRef.current = null;
      }
    };
  }, [map, dayRouteIsPlaying, dayRoutePlaybackIndexRef, dayRoutePlaybackProgressRef, dayRouteIsPlayingRef, isDark, convertKnotsToKmph]);

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <div
        ref={mapRef}
        style={{
          height: '100%',
          width: '100%',
          borderRadius: '6px',
          flex: 1,
          minHeight: 0,
        }}
      />
      {/* Map Controls */}
      <div
        style={{
          position: 'absolute',
          top: '10px',
          left: '10px', // Move map controls (standard/satellite toggle) to left side
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {/* Map Type Toggle */}
        <div
          style={{
            display: 'flex',
            gap: '4px',
            backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
            borderRadius: '6px',
            padding: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <button
            onClick={() => setMapMode('standard')}
            style={{
              padding: '6px 12px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: mapMode === 'standard' ? '#3b82f6' : 'transparent',
              color: mapMode === 'standard' ? '#ffffff' : (isDark ? '#f1f5f9' : '#0f172a'),
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
            title="Standard Mode"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3h18v18H3z"></path>
              <path d="M9 9h6v6H9z"></path>
            </svg>
            {/* Standard */}
          </button>
          <button
            onClick={() => setMapMode('satellite')}
            style={{
              padding: '6px 12px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: mapMode === 'satellite' ? '#3b82f6' : 'transparent',
              color: mapMode === 'satellite' ? '#ffffff' : (isDark ? '#f1f5f9' : '#0f172a'),
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
            title="Satellite Mode"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
            </svg>
            {/* Satellite */}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LiveTrackingTab;
